// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import { auth, db, createAtomicBooking, cancelBookingFn } from './firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendEmailVerification,
  onAuthStateChanged,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { 
  doc, setDoc, getDoc, getDocs, collection, query, where,
  onSnapshot, addDoc, updateDoc, deleteDoc, serverTimestamp, orderBy, arrayRemove,
  runTransaction
} from 'firebase/firestore';
import { TIMEZONES, COUNTRIES, HOURS, formatHour, convertTimezone, getCountryFromTimezone } from './timezones';
import { 
  Calendar, Clock, Users, MessageSquare, User, Settings, LogOut, 
  Play, Video, Phone, Search, Bell, Moon, Sun, Check, X, Plus,
  ChevronLeft, ChevronRight, Star, Zap, Target, TrendingUp,
  Award, BookOpen, HelpCircle, Mail, Send, Download, ExternalLink,
  AlertCircle, CheckCircle, Info, Loader, RefreshCw, Copy,
  Home, BarChart3, Crown, Shield, Lock, Unlock, Eye, EyeOff,
  Mic, MicOff, VideoOff, PhoneOff, Share2, MoreVertical,
  ArrowRight, ArrowLeft, ChevronDown, ChevronUp, Filter,
  Trash2, Edit, Save, XCircle, PlusCircle, MinusCircle,
  Globe, MapPin, Building, Briefcase, GraduationCap,
  Heart, ThumbsUp, ThumbsDown, Meh, Frown, Smile,
  Radio, Wifi, WifiOff, Circle, Square, Triangle, FileText,
  Activity, UserCheck, UserX, Sparkles, CreditCard, Cookie, Menu
} from 'lucide-react';
import './App.css';

// Utils and Components
import debug from './utils/debug';
import { ErrorBoundary } from './components/Common/ErrorBoundary';
import { LoadingButton } from './components/Common/LoadingButton';

// Zoom API server URL (change this when deploying)
const ZOOM_SERVER_URL = 'https://meetsync-zoom.onrender.com';

// Generate random community code
const generateCommunityCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let code = '';
  for (let i = 0; i < 10; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

const AVATAR_OPTIONS = ['👤', '👩', '👨'];
const DEFAULT_AVATAR = '👤';

function App() {
  // Landing page state
  const [showLanding, setShowLanding] = useState(true);
  const [landingView, setLandingView] = useState('home'); // home, pricing, create-community, checkout, contact-sales, payment-success
  const [selectedPlan, setSelectedPlan] = useState(null); // 'starter' or 'pro'
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Bug report modal
  const [showBugReport, setShowBugReport] = useState(false);
  const [bugReportForm, setBugReportForm] = useState({
    type: 'bug',
    description: '',
    email: '',
    page: ''
  });
  const [bugReportSubmitting, setBugReportSubmitting] = useState(false);
  
  // Cookie consent & legal
  const [showCookieConsent, setShowCookieConsent] = useState(() => {
    return !localStorage.getItem('cookieConsent');
  });
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showRefundPolicy, setShowRefundPolicy] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Always on
    analytics: false,
    marketing: false
  });
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  
  // Stripe Product IDs (TEST MODE)
  // TODO: Switch to live product IDs for production
  const STRIPE_PRODUCTS = {
    starter: {
      productId: 'prod_Tmj5ENiyu00MV8', // TEST: Basic
      name: 'Starter',
      members: 10
    },
    pro: {
      productId: 'prod_Tmj5nx61AJGr2X', // TEST: Pro
      name: 'Pro',
      members: 50
    }
  };
  
  // Regional Pricing Configuration (static prices, no ugly conversions)
  const REGIONAL_PRICING = {
    USD: { symbol: '$', code: 'USD', name: 'US Dollar', starter: 149, pro: 499 },
    EUR: { symbol: '€', code: 'EUR', name: 'Euro', starter: 139, pro: 459 },
    GBP: { symbol: '£', code: 'GBP', name: 'British Pound', starter: 119, pro: 399 },
    CAD: { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar', starter: 199, pro: 669 },
    AUD: { symbol: 'A$', code: 'AUD', name: 'Australian Dollar', starter: 229, pro: 769 },
    INR: { symbol: '₹', code: 'INR', name: 'Indian Rupee', starter: 9999, pro: 34999 },
    BRL: { symbol: 'R$', code: 'BRL', name: 'Brazilian Real', starter: 599, pro: 1999 },
    MXN: { symbol: 'MX$', code: 'MXN', name: 'Mexican Peso', starter: 2499, pro: 8499 },
    JPY: { symbol: '¥', code: 'JPY', name: 'Japanese Yen', starter: 19900, pro: 69900 },
    CHF: { symbol: 'CHF', code: 'CHF', name: 'Swiss Franc', starter: 139, pro: 459 },
  };
  
  // Country to Currency mapping
  const COUNTRY_CURRENCY_MAP = {
    // USD
    US: 'USD', 
    // EUR
    DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', 
    PT: 'EUR', IE: 'EUR', FI: 'EUR', GR: 'EUR', LU: 'EUR', SK: 'EUR', SI: 'EUR',
    EE: 'EUR', LV: 'EUR', LT: 'EUR', MT: 'EUR', CY: 'EUR',
    // GBP
    GB: 'GBP', UK: 'GBP',
    // CAD
    CA: 'CAD',
    // AUD
    AU: 'AUD', NZ: 'AUD',
    // INR
    IN: 'INR',
    // BRL
    BR: 'BRL',
    // MXN
    MX: 'MXN',
    // JPY
    JP: 'JPY',
    // CHF
    CH: 'CHF', LI: 'CHF',
  };
  
  // Currency state with localStorage persistence
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem('meetsync_currency');
    return saved && REGIONAL_PRICING[saved] ? saved : 'USD';
  });
  const [currencyLoading, setCurrencyLoading] = useState(true);
  
  // Get current pricing based on currency
  const getCurrentPricing = () => REGIONAL_PRICING[currency] || REGIONAL_PRICING.USD;
  
  // Update currency and persist
  const updateCurrency = (newCurrency) => {
    if (REGIONAL_PRICING[newCurrency]) {
      setCurrency(newCurrency);
      localStorage.setItem('meetsync_currency', newCurrency);
    }
  };
  
  // Auto-detect currency on first load
  useEffect(() => {
    const detectCurrency = async () => {
      // Check if already set by user
      const savedCurrency = localStorage.getItem('meetsync_currency');
      if (savedCurrency && REGIONAL_PRICING[savedCurrency]) {
        setCurrencyLoading(false);
        return;
      }
      
      try {
        // Use free IP geolocation API
        const response = await fetch('https://ipapi.co/json/', { timeout: 3000 });
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code;
          const detectedCurrency = COUNTRY_CURRENCY_MAP[countryCode] || 'USD';
          
          debug.log('Currency detection:', countryCode, '->', detectedCurrency);
          updateCurrency(detectedCurrency);
        }
      } catch (err) {
        debug.log('Currency detection failed, using USD:', err.message);
      }
      setCurrencyLoading(false);
    };
    
    detectCurrency();
  }, []);
  
  // Community creation states
  const [communityName, setCommunityName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminName, setAdminName] = useState('');
  const [createdCommunityCode, setCreatedCommunityCode] = useState('');
  const [hasPaid, setHasPaid] = useState(false);
  
  // Landing page live stats (for social proof)
  const [liveStats, setLiveStats] = useState({
    currentlyRoleplaying: Math.floor(Math.random() * 8) + 4, // 4-12
    matchesThisMonth: Math.floor(Math.random() * 500) + 3500, // 3500-4000
  });
  
  // Testimonial carousel state
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const testimonials = [
    {
      quote: "Our SDRs went from 12% to 23% meeting-to-opportunity rate in 6 weeks. MeetSync is now mandatory for onboarding.",
      author: "Marcus Chen",
      title: "VP Sales, TechCorp",
      photo: "/images/testimonial.jpg"
    },
    {
      quote: "Finally, a way to practice that doesn't feel awkward. My team actually looks forward to roleplay sessions now.",
      author: "Sarah Mitchell",
      title: "Sales Director, CloudScale",
      photo: "/images/testimonial.jpg"
    },
    {
      quote: "We cut ramp time for new hires by 40%. The AI coaching tips after each session are incredibly valuable.",
      author: "James Rodriguez",
      title: "Head of Enablement, FinServe",
      photo: "/images/testimonial.jpg"
    },
    {
      quote: "The timezone matching is a game-changer for our global team. Everyone can find practice partners easily.",
      author: "Emma Thompson",
      title: "L&D Manager, GlobalTech",
      photo: "/images/testimonial.jpg"
    }
  ];
  
  // Contact sales form states
  const [showContactSales, setShowContactSales] = useState(false);
  const [contactForm, setContactForm] = useState({
    workEmail: '',
    firstName: '',
    lastName: '',
    companyName: '',
    phoneNumber: '',
    specificNeeds: ''
  });
  
  // Admin dashboard states
  const [adminViewMode, setAdminViewMode] = useState('overview'); // overview, members, analytics, leaderboard
  const [selectedMember, setSelectedMember] = useState(null);
  const [communityMembers, setCommunityMembers] = useState([]);
  const [communityBookings, setCommunityBookings] = useState([]); // All bookings in community
  const [communityActivity, setCommunityActivity] = useState([]); // Recent activity feed
  const [communityReflections, setCommunityReflections] = useState([]); // All reflections for avg ratings
  
  // AI Coaching states
  const [weeklyCoachInsight, setWeeklyCoachInsight] = useState(null);
  const [isGeneratingTips, setIsGeneratingTips] = useState(false);
  const [showWeeklyCoachModal, setShowWeeklyCoachModal] = useState(false);
  
  // Auth states
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authMode, setAuthMode] = useState('login');
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Access code states (now for community code)
  const [accessCode, setAccessCode] = useState('');
  const [showAccessCodeScreen, setShowAccessCodeScreen] = useState(false);
  const [communityInfo, setCommunityInfo] = useState(null);
  
  // Dropdown menu state
  const [showDropdown, setShowDropdown] = useState(false);
  
  // App states
  const [view, setView] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isMatching, setIsMatching] = useState(false);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [bookings, setBookings] = useState([]);
  
  // Typing indicator state
  const [isTyping, setIsTyping] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  
  // Unread messages count per conversation
  const [unreadCounts, setUnreadCounts] = useState({});
  
  // Dark mode
  const [darkMode, setDarkMode] = useState(false); // Default to light, will be set from user profile
  
  // Ref for scrolling chat to bottom
  const messagesEndRef = useRef(null);
  
  // Calendar states
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [tempAvailability, setTempAvailability] = useState([]); // Temporary state for cancel logic
  
  // Invite modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteUser, setInviteUser] = useState(null);
  const [inviteWeekStart, setInviteWeekStart] = useState(new Date());
  const [selectedInviteSlot, setSelectedInviteSlot] = useState(null);
  
  // Profile states
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileBio, setProfileBio] = useState('');
  const [profileTimezone, setProfileTimezone] = useState('America/New_York');
  const [profileAvatar, setProfileAvatar] = useState('🧑‍💼');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [profileWeeklyGoal, setProfileWeeklyGoal] = useState(''); // Weekly goal setting
  
  // Weekly tracker states
  const [weeklyCompletedCount, setWeeklyCompletedCount] = useState(0);
  
  // Onboarding tutorial states
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  
  // Analytics states
  const [analyticsData, setAnalyticsData] = useState(null);
  
  // Real-time clock for status updates (updates every second)
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Notification states
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Roleplay Now states
  const [roleplayNowRequests, setRoleplayNowRequests] = useState([]);
  const [isRoleplayNowActive, setIsRoleplayNowActive] = useState(false);
  const [showRoleplayNowModal, setShowRoleplayNowModal] = useState(false);
  const [pendingRoleplayRequest, setPendingRoleplayRequest] = useState(null);
  const [activeRoleplayRequestId, setActiveRoleplayRequestId] = useState(null);
  
  // Live Call Tracker state
  const [liveCall, setLiveCall] = useState(null); // Current in-progress call
  const [liveCallTimer, setLiveCallTimer] = useState(0); // Seconds elapsed
  const [roleplayNowBlocked, setRoleplayNowBlocked] = useState(false); // 1-hour buffer
  const [roleplayBlockReason, setRoleplayBlockReason] = useState('');
  
  // Flash-Prep Waiting Room overlay
  const [showPrepOverlay, setShowPrepOverlay] = useState(false);
  const [prepCountdown, setPrepCountdown] = useState(3);
  const [prepPartner, setPrepPartner] = useState(null);
  const [prepZoomLink, setPrepZoomLink] = useState(null);
  
  // Global Booking Overlay (anti-double-click)
  const [showBookingOverlay, setShowBookingOverlay] = useState(false);
  const [bookingOverlayData, setBookingOverlayData] = useState(null);
  const [isBookingInProgress, setIsBookingInProgress] = useState(false);
  
  // Cancel booking states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Reset availability states
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmStep, setResetConfirmStep] = useState(1);
  
  // Leave community states (for students)
  const [showLeaveCommunityModal, setShowLeaveCommunityModal] = useState(false);
  const [leaveConfirmStep, setLeaveConfirmStep] = useState(1);
  
  // Delete community states (for admins)
  const [showDeleteCommunityModal, setShowDeleteCommunityModal] = useState(false);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(1);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Admin broadcast notification states
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);
  
  // Admin view student profile states
  const [showStudentProfileModal, setShowStudentProfileModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Drag selection states for availability calendar
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragMode, setDragMode] = useState(null); // 'select' or 'deselect'

  // Post-call feedback states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackStep, setFeedbackStep] = useState(1); // 1 = smiley, 2 = reflection, 3 = admin question
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackReflection, setFeedbackReflection] = useState('');
  const [feedbackAdminQuestion, setFeedbackAdminQuestion] = useState('');
  const [feedbackBooking, setFeedbackBooking] = useState(null);
  
  // Track which bookings we've already processed for completion to prevent ghost triggers
  const processedCompletions = useRef(new Set());
  
  // Track which bookings we've already sent 10-min reminders for
  const sentReminders = useRef(new Set());
  
  // Admin questions state
  const [adminQuestions, setAdminQuestions] = useState([]);
  
  // Contact requests state (for super admin to view enterprise inquiries)
  const [contactRequests, setContactRequests] = useState([]);
  
  // Reflections book state
  const [reflections, setReflections] = useState([]);
  const [showReflectionsBook, setShowReflectionsBook] = useState(false);
  
  // Toast notification
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('default'); // 'default', 'dnd-on', 'dnd-off', 'success', 'error'
  
  // Meeting reminder popup
  const [showMeetingReminder, setShowMeetingReminder] = useState(false);
  const [upcomingMeeting, setUpcomingMeeting] = useState(null);
  const [remindedMeetings, setRemindedMeetings] = useState(new Set());
  
  // Timezone search
  const [timezoneSearch, setTimezoneSearch] = useState('');
  
  // Sidebar state (for mobile)
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Do Not Disturb mode
  const [dndMode, setDndMode] = useState(false);
  
  // Calendar view mode for Upcoming Calls
  const [calendarViewMode, setCalendarViewMode] = useState('weekly'); // daily, weekly, monthly
  const [calendarDate, setCalendarDate] = useState(new Date());
  
  // Event popup for calendar
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventPopup, setShowEventPopup] = useState(false);
  
  // Pending invites for calendar display
  const [pendingInvites, setPendingInvites] = useState([]);
  
  // Country search for timezone
  const [countrySearch, setCountrySearch] = useState('');

  // Default availability structure
  const defaultAvailability = {};

  // Dark mode effect - only apply when logged in with user profile
  useEffect(() => {
    // Only apply dark mode for logged-in users
    if (userProfile && darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode, userProfile]);

  // Update current time every SECOND for real-time clock
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second
    return () => clearInterval(interval);
  }, []);

  // Update live stats every 2-3 minutes for landing page social proof
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveStats(prev => ({
        currentlyRoleplaying: Math.max(2, Math.min(18, prev.currentlyRoleplaying + Math.floor(Math.random() * 5) - 2)),
        matchesThisMonth: prev.matchesThisMonth + Math.floor(Math.random() * 3) + 1,
      }));
    }, 150000 + Math.random() * 30000); // 2.5-3 minutes
    return () => clearInterval(interval);
  }, []);

  // Auto-advance testimonials every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Click-away to close notifications and dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close notifications if clicking outside
      if (showNotifications && !event.target.closest('.notification-wrapper')) {
        setShowNotifications(false);
      }
      // Close user dropdown if clicking outside
      if (showDropdown && !event.target.closest('.user-menu-wrapper')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications, showDropdown]);

  // Close dropdowns when changing views
  useEffect(() => {
    setShowNotifications(false);
    setShowDropdown(false);
    setMobileMenuOpen(false);
  }, [view]);

  // Update user's online status
  useEffect(() => {
    if (!userProfile) return;
    
    // Set online when component mounts
    const setOnline = async () => {
      await updateDoc(doc(db, 'users', userProfile.id), { 
        isOnline: true,
        lastSeen: serverTimestamp()
      });
    };
    setOnline();

    // Set offline when leaving
    const setOffline = async () => {
      await updateDoc(doc(db, 'users', userProfile.id), { 
        isOnline: false,
        lastSeen: serverTimestamp()
      });
    };

    // Handle tab close/refresh
    window.addEventListener('beforeunload', setOffline);
    
    // Heartbeat to keep online status fresh (every 2 minutes)
    const heartbeat = setInterval(setOnline, 120000);

    return () => {
      window.removeEventListener('beforeunload', setOffline);
      clearInterval(heartbeat);
      setOffline();
    };
  }, [userProfile]);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        setShowLanding(false); // User is logged in, hide landing
        if (!currentUser.emailVerified) {
          setAuthMode('verify');
        } else {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            
            // Clean up past availability dates
            let cleanedAvailability = data.availability || {};
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayKey = today.toISOString().split('T')[0];
            
            const pastDates = Object.keys(cleanedAvailability).filter(dateKey => dateKey < todayKey);
            if (pastDates.length > 0) {
              pastDates.forEach(dateKey => {
                delete cleanedAvailability[dateKey];
              });
              // Update Firebase to remove past dates
              try {
                await updateDoc(doc(db, 'users', currentUser.uid), { availability: cleanedAvailability });
                debug.log(`Cleaned up ${pastDates.length} past availability dates`);
              } catch (err) {
                debug.error('Failed to clean up past availability:', err);
              }
            }
            
            setUserProfile({ id: currentUser.uid, ...data, availability: cleanedAvailability });
            setProfileBio(data.bio || '');
            setProfileTimezone(data.timezone || 'America/New_York');
            setProfileAvatar(data.avatar || '🧑‍💼');
            setProfileWeeklyGoal(data.weeklyGoal || '');
            
            // Load user's dark mode preference
            setDarkMode(data.darkMode === true);
            
            // Load user's DND mode preference
            setDndMode(data.dndMode === true);
            
            // Load community info if user belongs to one
            if (data.communityId) {
              const communityDoc = await getDoc(doc(db, 'communities', data.communityId));
              if (communityDoc.exists()) {
                setCommunityInfo({ id: communityDoc.id, ...communityDoc.data() });
              }
            }
            
            // Check if user has access (joined a community)
            // IMPORTANT: Must explicitly check for true, not just truthy
            if (data.hasAccess !== true || !data.communityId) {
              debug.log('User needs to enter classroom code:', { hasAccess: data.hasAccess, communityId: data.communityId });
              setShowAccessCodeScreen(true);
            } else {
              setShowAccessCodeScreen(false);
              
              // Check if user needs onboarding (new user with no completed sessions)
              if (!data.hasCompletedOnboarding) {
                setShowOnboarding(true);
                setOnboardingStep(1);
              }
            }
          }
        }
      } else {
        setUserProfile(null);
        setShowAccessCodeScreen(false);
        setCommunityInfo(null);
        setDarkMode(false); // Reset to light mode on logout
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Presence/Heartbeat system - update lastOnline periodically
  useEffect(() => {
    if (!userProfile) return;
    
    // Update lastOnline immediately
    const updatePresence = async () => {
      try {
        await updateDoc(doc(db, 'users', userProfile.id), {
          lastOnline: serverTimestamp(),
          isOnline: true
        });
      } catch (err) {
        debug.error('Presence update failed:', err);
      }
    };
    
    // Update immediately on mount
    updatePresence();
    
    // Update every 2 minutes while active
    const heartbeatInterval = setInterval(updatePresence, 2 * 60 * 1000);
    
    // Activity detection - reset timer on user activity
    let inactivityTimeout;
    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimeout);
      inactivityTimeout = setTimeout(async () => {
        // Mark as offline after 5 minutes of inactivity
        try {
          await updateDoc(doc(db, 'users', userProfile.id), {
            isOnline: false
          });
        } catch (err) {
          debug.error('Failed to set offline:', err);
        }
      }, 5 * 60 * 1000);
    };
    
    // Listen for user activity
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, { passive: true });
    });
    resetInactivityTimer();
    
    // Set offline when tab closes
    const handleBeforeUnload = async () => {
      navigator.sendBeacon && navigator.sendBeacon('/api/offline', JSON.stringify({ userId: userProfile.id }));
      // Also try direct update (may not complete)
      try {
        await updateDoc(doc(db, 'users', userProfile.id), {
          isOnline: false,
          lastOnline: serverTimestamp()
        });
      } catch (err) {}
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      clearInterval(heartbeatInterval);
      clearTimeout(inactivityTimeout);
      activityEvents.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer);
      });
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Mark offline on unmount
      updateDoc(doc(db, 'users', userProfile.id), { isOnline: false }).catch(() => {});
    };
  }, [userProfile?.id]);

  // Calculate weekly completed roleplays
  useEffect(() => {
    if (!userProfile || !bookings) return;
    
    const userTz = userProfile.timezone || 'America/New_York';
    
    // Get start of current week (Monday) in user's timezone
    const now = new Date();
    const nowInUserTz = new Date(now.toLocaleString('en-US', { timeZone: userTz }));
    const dayOfWeek = nowInUserTz.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 6 days from Monday
    
    const weekStart = new Date(nowInUserTz);
    weekStart.setDate(weekStart.getDate() - daysFromMonday);
    weekStart.setHours(0, 0, 0, 0);
    
    // Count completed bookings this week (only from current community)
    const completedThisWeek = bookings.filter(b => {
      if (b.status !== 'completed') return false;
      if (b.communityId && b.communityId !== userProfile.communityId) return false;
      const bookingDate = new Date(b.date);
      return bookingDate >= weekStart;
    }).length;
    
    setWeeklyCompletedCount(completedThisWeek);
  }, [userProfile, bookings]);

  // Compute analytics data (scoped to current community only)
  useEffect(() => {
    if (!userProfile || !bookings || userProfile.isAdmin || !userProfile.communityId) return;
    
    // STRICT FILTER: Only count completed bookings from current community
    const completedBookings = bookings.filter(b => 
      b.status === 'completed' && 
      b.communityId === userProfile.communityId
    );
    
    // Sessions over time (last 12 weeks)
    const sessionsOverTime = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - (i * 7) - now.getDay() + 1);
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      
      const count = completedBookings.filter(b => {
        const d = new Date(b.date || b.dateTime);
        return d >= weekStart && d < weekEnd;
      }).length;
      
      sessionsOverTime.push({
        week: weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        sessions: count
      });
    }
    
    // Practice hours by week (assuming 1 hour per session)
    const practiceHours = sessionsOverTime.map(w => ({
      ...w,
      hours: w.sessions // 1 hour per session
    }));
    
    // Partner variety
    const partnerCounts = {};
    completedBookings.forEach(b => {
      const partnerId = b.participants?.find(p => p !== userProfile.id);
      if (partnerId) {
        const partnerName = b.participantNames?.[partnerId] || 'Unknown';
        partnerCounts[partnerName] = (partnerCounts[partnerName] || 0) + 1;
      }
    });
    const partners = Object.entries(partnerCounts)
      .map(([name, count]) => ({ name, sessions: count }))
      .sort((a, b) => b.sessions - a.sessions)
      .slice(0, 5);
    
    // Peak practice times (hour heatmap)
    const hourCounts = Array(24).fill(0);
    completedBookings.forEach(b => {
      const hour = parseInt(b.hour?.split(':')[0] || '0');
      hourCounts[hour]++;
    });
    const peakTimes = hourCounts.map((count, hour) => ({
      hour: hour,
      label: hour === 0 ? '12am' : hour < 12 ? `${hour}am` : hour === 12 ? '12pm' : `${hour - 12}pm`,
      count
    }));
    
    // Day of week distribution
    const dayCounts = { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0 };
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    completedBookings.forEach(b => {
      const d = new Date(b.date || b.dateTime);
      dayCounts[dayNames[d.getDay()]]++;
    });
    const dayDistribution = Object.entries(dayCounts).map(([day, count]) => ({ day, count }));
    
    // Total stats
    const totalSessions = completedBookings.length;
    const totalHours = totalSessions; // 1 hour per session
    const uniquePartners = Object.keys(partnerCounts).length;
    const currentStreak = calculateStreak(completedBookings, userProfile.timezone);
    
    setAnalyticsData({
      sessionsOverTime,
      practiceHours,
      partners,
      peakTimes,
      dayDistribution,
      totalSessions,
      totalHours,
      uniquePartners,
      currentStreak
    });
  }, [userProfile, bookings]);
  
  // Helper function to calculate practice streak
  const calculateStreak = (completedBookings, timezone) => {
    if (completedBookings.length === 0) return 0;
    
    const sortedDates = [...new Set(
      completedBookings.map(b => new Date(b.date || b.dateTime).toDateString())
    )].sort((a, b) => new Date(b) - new Date(a));
    
    let streak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < sortedDates.length; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      
      if (sortedDates.includes(checkDate.toDateString())) {
        streak++;
      } else if (i === 0) {
        // Today doesn't have a session, check if yesterday did
        continue;
      } else {
        break;
      }
    }
    
    return streak;
  };

  // Fetch all users (filtered by community)
  useEffect(() => {
    if (!userProfile || !userProfile.communityId) return;
    
    // Only fetch users from the same community
    const q = query(
      collection(db, 'users'),
      where('communityId', '==', userProfile.communityId)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(u => u.id !== userProfile.id && u.hasAccess);
      setUsers(usersData);
      
      // For admins, also set all community members (including self)
      if (userProfile.isAdmin) {
        const allMembers = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(u => u.hasAccess);
        setCommunityMembers(allMembers);
      }
    });
    return () => unsubscribe();
  }, [userProfile]);

  // Fetch admin questions (for admins only)
  useEffect(() => {
    if (!userProfile || !userProfile.isAdmin || !userProfile.communityId) {
      debug.log('Admin questions: skipping - ', { 
        hasUserProfile: !!userProfile, 
        isAdmin: userProfile?.isAdmin, 
        communityId: userProfile?.communityId 
      });
      return;
    }
    
    debug.log('Admin questions: fetching for community', userProfile.communityId);
    
    const q = query(
      collection(db, 'adminQuestions'),
      where('communityId', '==', userProfile.communityId)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      debug.log('Admin questions: received', snapshot.docs.length, 'questions');
      const questions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Sort on client side instead of Firestore to avoid index requirement
      questions.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime; // Most recent first
      });
      setAdminQuestions(questions);
    }, (error) => {
      debug.error('Error fetching admin questions:', error);
      setAdminQuestions([]);
    });
    return () => unsubscribe();
  }, [userProfile]);

  // Fetch contact requests (enterprise inquiries) - only for simonegalvan.it@gmail.com
  useEffect(() => {
    if (!userProfile || userProfile.email !== 'simonegalvan.it@gmail.com') return;
    
    const q = query(collection(db, 'contactRequests'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      requests.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      });
      setContactRequests(requests);
    });
    return () => unsubscribe();
  }, [userProfile]);

  // Fetch ALL community bookings (for admin stats)
  useEffect(() => {
    if (!userProfile || !userProfile.isAdmin || !userProfile.communityId) return;
    
    const q = query(
      collection(db, 'bookings'),
      where('communityId', '==', userProfile.communityId)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allBookings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      allBookings.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.dateTime);
        const dateB = b.createdAt?.toDate?.() || new Date(b.dateTime);
        return dateB - dateA; // Most recent first
      });
      setCommunityBookings(allBookings);
      
      // Generate activity feed
      const activities = allBookings.slice(0, 15).map(b => ({
        id: b.id,
        type: b.status === 'completed' ? 'completed' : b.status === 'scheduled' ? 'scheduled' : 'other',
        participants: b.participants,
        participantDetails: b.participantDetails,
        timestamp: b.completedAt || b.createdAt || b.dateTime,
        status: b.status
      }));
      setCommunityActivity(activities);
    });
    return () => unsubscribe();
  }, [userProfile]);

  // Fetch ALL community reflections (for average ratings)
  useEffect(() => {
    if (!userProfile || !userProfile.isAdmin || !userProfile.communityId) return;
    
    // We need to fetch reflections for all community members
    // Since reflections don't have communityId, we'll filter by member IDs
    const unsubscribe = onSnapshot(collection(db, 'reflections'), (snapshot) => {
      const allReflections = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Filter to only community members
      const memberIds = communityMembers.map(m => m.id);
      const communityReflects = allReflections.filter(r => memberIds.includes(r.userId));
      setCommunityReflections(communityReflects);
    });
    return () => unsubscribe();
  }, [userProfile, communityMembers]);

  // Fetch conversations (strictly filtered by current community)
  useEffect(() => {
    if (!userProfile || !userProfile.communityId) return;
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userProfile.id)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        // STRICT FILTER: Only show conversations from current community
        .filter(conv => {
          // Must have communityId matching current community
          if (conv.communityId && conv.communityId === userProfile.communityId) {
            return true;
          }
          // Legacy conversations without communityId: check if other participant is in our community
          if (!conv.communityId) {
            const otherUserId = conv.participants?.find(p => p !== userProfile.id);
            if (otherUserId && users.length > 0) {
              const otherUser = users.find(u => u.id === otherUserId);
              // Only show if other user is in our community
              return !!otherUser;
            }
          }
          return false;
        });
      setConversations(convs);
      
      // Calculate unread counts for each conversation
      const counts = {};
      convs.forEach(conv => {
        // Check if there are unread messages (lastMessageAt > lastReadAt for this user)
        const myLastRead = conv.lastReadAt?.[userProfile.id]?.toDate?.() || new Date(0);
        const lastMsg = conv.lastMessageAt?.toDate?.() || new Date(0);
        const lastSender = conv.lastMessageSenderId;
        
        // Unread if: last message is newer than my last read AND I didn't send it
        if (lastMsg > myLastRead && lastSender && lastSender !== userProfile.id) {
          counts[conv.id] = (counts[conv.id] || 0) + 1;
        } else {
          counts[conv.id] = 0;
        }
      });
      setUnreadCounts(counts);
    });
    return () => unsubscribe();
  }, [userProfile, users]);

  // Listen for typing indicator in active conversation
  useEffect(() => {
    if (!activeConversation || !userProfile) return;
    
    const convRef = doc(db, 'conversations', activeConversation);
    const unsubscribe = onSnapshot(convRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const typingUsers = data.typing || {};
        // Check if any OTHER user is typing
        const otherTyping = Object.entries(typingUsers).some(([odId, timestamp]) => {
          if (odId === userProfile.id) return false;
          // Typing is valid for 3 seconds
          const typingTime = timestamp?.toDate?.() || new Date(0);
          return (Date.now() - typingTime.getTime()) < 3000;
        });
        setOtherUserTyping(otherTyping);
      }
    });
    return () => unsubscribe();
  }, [activeConversation, userProfile]);

  // Mark conversation as read when opened
  useEffect(() => {
    if (!activeConversation || !userProfile) return;
    
    // Update lastReadAt for this user
    const markRead = async () => {
      try {
        await updateDoc(doc(db, 'conversations', activeConversation), {
          [`lastReadAt.${userProfile.id}`]: serverTimestamp()
        });
      } catch (err) {
        debug.error('Error marking as read:', err);
      }
    };
    markRead();
  }, [activeConversation, userProfile]);

  // Fetch bookings
  useEffect(() => {
    if (!userProfile) return;
    const q = query(
      collection(db, 'bookings'),
      where('participants', 'array-contains', userProfile.id)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const bookingsData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        // Filter to only show bookings from current community
        .filter(b => !b.communityId || b.communityId === userProfile.communityId);
      // Sort by dateTime on client side
      bookingsData.sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
      setBookings(bookingsData);
    }, (error) => {
      debug.error("Bookings fetch error:", error);
      setBookings([]);
    });
    return () => unsubscribe();
  }, [userProfile]);

  // Auto-complete bookings after 1 hour and trigger feedback
  // NOTE: For production, consider adding a Firebase Cloud Function to handle this server-side
  // This ensures status updates even if all users close their browsers
  useEffect(() => {
    if (!userProfile || !bookings.length) return;
    
    const checkCompletions = async () => {
      const now = new Date();
      
      for (const booking of bookings) {
        // Skip already completed or cancelled bookings
        if (booking.status === 'completed' || booking.status === 'cancelled') continue;
        
        // Calculate start time
        let startTime;
        if (booking.isRoleplayNow && booking.acceptedAt) {
          startTime = booking.acceptedAt?.toDate ? booking.acceptedAt.toDate() : new Date(booking.acceptedAt);
        } else if (booking.dateTime) {
          startTime = new Date(booking.dateTime);
        } else {
          continue;
        }
        
        const meetingEnd = new Date(startTime.getTime() + 60 * 60 * 1000); // +1 hour
        
        // If meeting should have started, mark as in-progress
        if (now >= startTime && now < meetingEnd && booking.status === 'scheduled') {
          try {
            await updateDoc(doc(db, 'bookings', booking.id), { 
              status: 'in-progress',
              startedAt: serverTimestamp()
            });
          } catch (error) {
            debug.error("Error starting booking:", error);
          }
        }
        
        // If meeting should be over (1 hour passed), mark as completed
        if (now >= meetingEnd && (booking.status === 'scheduled' || booking.status === 'in-progress')) {
          // Skip if we've already processed this booking's completion
          if (processedCompletions.current.has(booking.id)) continue;
          
          try {
            await updateDoc(doc(db, 'bookings', booking.id), { 
              status: 'completed',
              completedAt: serverTimestamp()
            });
            
            // Mark this booking as processed to prevent duplicate triggers
            processedCompletions.current.add(booking.id);
            
            // Only show feedback for SCHEDULED calls, not instant roleplay
            // Instant calls skip feedback to reduce friction
            if (booking.type !== 'instant' && !booking.isRoleplayNow) {
              // Check if user already gave feedback for this booking
              const feedbackGiven = booking.feedbackBy?.includes(userProfile.id);
              
              if (!feedbackGiven) {
                // Show feedback modal
                setFeedbackBooking(booking);
                setFeedbackStep(1);
                setFeedbackRating(null);
                setFeedbackReflection('');
                setShowFeedbackModal(true);
              }
            } else {
              // For instant calls, just show a toast
              setToastMessage('Roleplay session completed! Great practice!');
              setShowToast(true);
              setTimeout(() => setShowToast(false), 3000);
            }
          } catch (error) {
            debug.error("Error completing booking:", error);
          }
        }
      }
    };
    
    // Check immediately and every 30 seconds
    checkCompletions();
    const interval = setInterval(checkCompletions, 30000);
    return () => clearInterval(interval);
  }, [userProfile, bookings]);

  // Check for meetings starting in 5-10 minutes and show reminder
  useEffect(() => {
    if (!userProfile || !bookings.length) return;
    
    const checkUpcomingMeetings = async () => {
      const now = new Date();
      
      for (const booking of bookings) {
        if (booking.status !== 'scheduled') continue;
        if (remindedMeetings.has(booking.id)) continue; // Already reminded
        
        // Get meeting time in user's timezone
        const myDetails = booking.participantDetails?.[userProfile.id];
        let meetingTime;
        
        if (myDetails?.localDate && myDetails?.localHour) {
          meetingTime = new Date(`${myDetails.localDate}T${myDetails.localHour}:00`);
        } else if (booking.dateTime) {
          meetingTime = new Date(booking.dateTime);
        } else {
          continue;
        }
        
        // Calculate minutes until meeting
        const minutesUntil = (meetingTime - now) / (1000 * 60);
        
        // Add notification to notification center at 10 minutes (if not already sent)
        if (minutesUntil >= 9 && minutesUntil <= 11 && !sentReminders.current.has(booking.id)) {
          const otherId = booking.participants?.find(p => p !== userProfile.id);
          const otherName = booking.participantNames?.[otherId] || 'your partner';
          
          // Add to notification center
          try {
            await addDoc(collection(db, 'notifications'), {
              userId: userProfile.id,
              type: 'meeting_reminder',
              title: 'Upcoming Roleplay Session',
              message: `Your practice session with ${otherName} starts in 10 minutes!`,
              bookingId: booking.id,
              meetingLink: booking.zoomJoinUrl,
              read: false,
              createdAt: serverTimestamp()
            });
            sentReminders.current.add(booking.id);
          } catch (err) {
            debug.error('Error sending reminder notification:', err);
          }
        }
        
        // Show popup reminder if meeting is 5-10 minutes away
        if (minutesUntil >= 3 && minutesUntil <= 10) {
          const otherId = booking.participants?.find(p => p !== userProfile.id);
          const otherName = booking.participantNames?.[otherId] || 'your partner';
          
          setUpcomingMeeting({
            ...booking,
            otherName,
            minutesUntil: Math.round(minutesUntil)
          });
          setShowMeetingReminder(true);
          setRemindedMeetings(prev => new Set([...prev, booking.id]));
          break; // Only show one reminder at a time
        }
      }
    };
    
    // Check immediately and every minute
    checkUpcomingMeetings();
    const interval = setInterval(checkUpcomingMeetings, 60000);
    return () => clearInterval(interval);
  }, [userProfile, bookings, remindedMeetings]);

  // Live Call Tracker - detect and track in-progress calls
  useEffect(() => {
    if (!userProfile || !bookings.length) {
      setLiveCall(null);
      return;
    }
    
    // Find any in-progress call
    const inProgressCall = bookings.find(b => b.status === 'in-progress');
    
    if (inProgressCall) {
      const partnerId = inProgressCall.participants?.find(p => p !== userProfile.id);
      const partnerName = inProgressCall.participantNames?.[partnerId] || 
                          users.find(u => u.id === partnerId)?.name || 'Partner';
      
      setLiveCall({
        ...inProgressCall,
        partnerName,
        partnerAvatar: inProgressCall.participantAvatars?.[partnerId] || 
                       users.find(u => u.id === partnerId)?.avatar || '👤'
      });
    } else {
      setLiveCall(null);
    }
  }, [userProfile, bookings, users]);

  // Live Call Timer - count up every second when in a call
  useEffect(() => {
    if (!liveCall) {
      setLiveCallTimer(0);
      return;
    }
    
    // Calculate initial elapsed time
    let startTime;
    if (liveCall.acceptedAt) {
      startTime = liveCall.acceptedAt?.toDate ? liveCall.acceptedAt.toDate() : new Date(liveCall.acceptedAt);
    } else if (liveCall.startedAt) {
      startTime = liveCall.startedAt?.toDate ? liveCall.startedAt.toDate() : new Date(liveCall.startedAt);
    } else {
      startTime = new Date();
    }
    
    const updateTimer = () => {
      const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
      setLiveCallTimer(Math.max(0, elapsed));
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [liveCall]);

  // Flash-Prep countdown effect
  useEffect(() => {
    if (!showPrepOverlay) return;
    
    // Countdown from 3 to 0
    if (prepCountdown > 0) {
      const timer = setTimeout(() => {
        setPrepCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
    
    // When countdown reaches 0 and we have a zoom link, redirect
    if (prepCountdown === 0 && prepZoomLink) {
      // Wait a moment to show "GO!" then redirect
      const redirectTimer = setTimeout(() => {
        window.open(prepZoomLink, '_blank');
        setShowPrepOverlay(false);
        setView('messages');
      }, 1000);
      return () => clearTimeout(redirectTimer);
    }
  }, [showPrepOverlay, prepCountdown, prepZoomLink]);

  // 1-Hour Focus Buffer + Active Session Lock - block Roleplay Now
  useEffect(() => {
    if (!userProfile) {
      setRoleplayNowBlocked(false);
      setRoleplayBlockReason('');
      return;
    }
    
    // Check 1: Active Session Lock - is there an in-progress call?
    const activeSession = bookings.find(b => b.status === 'in-progress');
    if (activeSession) {
      const partnerId = activeSession.participants?.find(p => p !== userProfile.id);
      const partnerName = activeSession.participantNames?.[partnerId] || 
                          users.find(u => u.id === partnerId)?.name || 'your partner';
      
      setRoleplayNowBlocked(true);
      setRoleplayBlockReason(`You're in an active session with ${partnerName}`);
      return;
    }
    
    // Check 2: 1-Hour Focus Buffer - scheduled call within 60 min?
    const now = new Date();
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
    
    for (const booking of bookings) {
      if (booking.status !== 'scheduled') continue;
      
      // Get meeting time
      const myDetails = booking.participantDetails?.[userProfile.id];
      let meetingTime;
      
      if (myDetails?.localDate && myDetails?.localHour) {
        meetingTime = new Date(`${myDetails.localDate}T${myDetails.localHour}:00`);
      } else if (booking.dateTime) {
        meetingTime = new Date(booking.dateTime);
      } else {
        continue;
      }
      
      // Check if within 60 minutes
      if (meetingTime >= now && meetingTime <= oneHourFromNow) {
        const minutesUntil = Math.round((meetingTime - now) / (1000 * 60));
        const partnerId = booking.participants?.find(p => p !== userProfile.id);
        const partnerName = booking.participantNames?.[partnerId] || 'your partner';
        
        setRoleplayNowBlocked(true);
        setRoleplayBlockReason(`Scheduled call with ${partnerName} in ${minutesUntil} min`);
        return;
      }
    }
    
    setRoleplayNowBlocked(false);
    setRoleplayBlockReason('');
  }, [userProfile, bookings, users, currentTime]);

  // Fetch user's reflections (filtered by current community)
  useEffect(() => {
    if (!userProfile || !userProfile.communityId) return;
    const q = query(
      collection(db, 'reflections'),
      where('userId', '==', userProfile.id)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reflectionsData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        // Filter to only show reflections from current community
        .filter(r => !r.communityId || r.communityId === userProfile.communityId);
      // Sort by date descending
      reflectionsData.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      });
      setReflections(reflectionsData);
    });
    return () => unsubscribe();
  }, [userProfile]);

  // Check for weekly coach insight (runs on Friday or later)
  useEffect(() => {
    if (reflections.length > 0 && userProfile && !userProfile.isAdmin) {
      checkAndGenerateWeeklyInsight();
    }
  }, [reflections, userProfile]);

  // Fetch notifications - simplified query
  useEffect(() => {
    if (!userProfile) return;
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userProfile.id)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Sort on client side
      notifs.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      });
      
      // Check if there's a NEW unread notification (auto-open feature)
      const newUnreadCount = notifs.filter(n => !n.read).length;
      if (newUnreadCount > unreadCount && newUnreadCount > 0) {
        // New notification arrived - auto-open the notification center
        setShowNotifications(true);
      }
      
      setNotifications(notifs);
      setUnreadCount(newUnreadCount);
    }, (error) => {
      debug.error("Notifications fetch error:", error);
      setNotifications([]);
    });
    return () => unsubscribe();
  }, [userProfile, unreadCount]);

  // Remove a single notification
  const removeNotification = async (notificationId) => {
    try {
      // Notifications are stored in the root 'notifications' collection
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (error) {
      debug.error('Failed to remove notification:', error);
    }
  };

  // Track shown roleplay requests to avoid showing same one twice
  const [shownRoleplayIds, setShownRoleplayIds] = useState(new Set());

  // Listen for when MY roleplay request gets accepted (User A perspective)
  useEffect(() => {
    if (!userProfile || !activeRoleplayRequestId) return;
    
    const unsubscribe = onSnapshot(
      doc(db, 'roleplayNow', activeRoleplayRequestId),
      (docSnapshot) => {
        if (!docSnapshot.exists()) return;
        
        const data = docSnapshot.data();
        
        // If my request was accepted and has meeting link, show prep overlay!
        if (data.status === 'accepted' && data.acceptedBy) {
          debug.log("My roleplay request was accepted by:", data.acceptedBy);
          
          // Find the accepter's info
          const accepter = users.find(u => u.id === data.acceptedBy);
          
          // Show prep overlay for User A (sender)
          if (!showPrepOverlay) {
            setPrepPartner({
              name: accepter?.name || 'Your Partner',
              avatar: accepter?.avatar || '👤'
            });
            setPrepCountdown(3);
            setShowPrepOverlay(true);
          }
          
          // If meeting link is now available, set it
          if (data.meetingLink) {
            debug.log("Meeting link received for User A:", data.meetingLink);
            setPrepZoomLink(data.meetingLink);
            
            // Reset roleplay state only after we have the link
            setIsRoleplayNowActive(false);
            setActiveRoleplayRequestId(null);
            setSuccess('');
            
            // Find the conversation with the person who accepted
            const accepterConv = conversations.find(c => 
              c.participants.includes(data.acceptedBy)
            );
            
            if (accepterConv) {
              setActiveConversation(accepterConv.id);
            }
          }
        }
      },
      (error) => {
        debug.error("Error listening to my roleplay request:", error);
      }
    );
    
    return () => unsubscribe();
  }, [userProfile, activeRoleplayRequestId, conversations, users, showPrepOverlay]);

  // Fetch active Roleplay Now requests - for receiving requests (User B perspective)
  // Admins don't receive roleplay requests
  useEffect(() => {
    if (!userProfile || userProfile.isAdmin) return;
    const q = query(
      collection(db, 'roleplayNow'),
      where('status', '==', 'pending')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Filter on client: not expired, not own request, and same community
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      const validRequests = requests.filter(r => {
        const createdTime = r.createdAt?.toDate?.()?.getTime() || 0;
        return r.senderId !== userProfile.id && 
               createdTime > fiveMinutesAgo &&
               (!r.communityId || r.communityId === userProfile.communityId);
      });
      setRoleplayNowRequests(validRequests);
      
      // If current pending request was cancelled (no longer in valid requests), close modal
      if (pendingRoleplayRequest && !validRequests.find(r => r.id === pendingRoleplayRequest.id)) {
        setShowRoleplayNowModal(false);
        setPendingRoleplayRequest(null);
      }
      
      // Show modal for new pending requests that haven't been shown (only if NOT in DND mode)
      if (!dndMode) {
        const newRequest = validRequests.find(r => !shownRoleplayIds.has(r.id));
        if (newRequest) {
          setPendingRoleplayRequest(newRequest);
          setShowRoleplayNowModal(true);
          setShownRoleplayIds(prev => new Set([...prev, newRequest.id]));
        }
      }
    }, (error) => {
      debug.error("Roleplay Now fetch error:", error);
    });
    return () => unsubscribe();
  }, [userProfile, shownRoleplayIds, dndMode, pendingRoleplayRequest]);

  // Fetch messages
  useEffect(() => {
    if (!activeConversation) { setMessages([]); return; }
    const q = query(
      collection(db, 'conversations', activeConversation, 'messages'),
      orderBy('timestamp', 'asc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [activeConversation]);

  // Collect pending invites from all conversations for calendar display
  useEffect(() => {
    if (!userProfile || conversations.length === 0) return;
    
    const fetchPendingInvites = async () => {
      const allInvites = [];
      
      for (const conv of conversations) {
        try {
          const messagesQuery = query(
            collection(db, 'conversations', conv.id, 'messages'),
            where('type', '==', 'invite')
          );
          const snapshot = await getDocs(messagesQuery);
          
          snapshot.docs.forEach(doc => {
            const msg = { id: doc.id, conversationId: conv.id, ...doc.data() };
            // Only pending invites that are not expired
            if (msg.inviteData?.status === 'pending') {
              const isExpired = msg.timestamp?.toDate && 
                (Date.now() - msg.timestamp.toDate().getTime() > 24 * 60 * 60 * 1000);
              if (!isExpired) {
                // Get the other user's info
                const otherId = conv.participants.find(p => p !== userProfile.id);
                const otherUser = users.find(u => u.id === otherId) || conv.participantDetails?.[otherId];
                allInvites.push({
                  ...msg,
                  otherUser,
                  isSentByMe: msg.senderId === userProfile.id
                });
              }
            }
          });
        } catch (error) {
          debug.error("Error fetching invites from conversation:", error);
        }
      }
      
      setPendingInvites(allInvites);
    };
    
    fetchPendingInvites();
  }, [userProfile, conversations, users]);

  // Scroll to bottom when new messages arrive or conversation changes
  useEffect(() => {
    // Small delay to ensure DOM has updated
    const timer = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [messages, activeConversation]);

  // Auth handlers
  const handleSignUp = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!name.trim()) { setError('Please enter your name'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;
      await setDoc(doc(db, 'users', newUser.uid), {
        name: name.trim(),
        email: email.toLowerCase(),
        avatar: DEFAULT_AVATAR,
        bio: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
        availability: {},
        createdAt: serverTimestamp(),
        hasAccess: false,  // IMPORTANT: New users must enter classroom code
        isAdmin: false,
        communityId: null,
        communityName: null,
        weeklyGoal: 1,
        completedThisWeek: 0,
        isOnline: false,
        dndMode: false
      });
      await sendEmailVerification(newUser);
      setAuthMode('verify');
      setSuccess('Account created! Check your email to verify.');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') setError('Email already in use');
      else if (err.code === 'auth/invalid-email') setError('Invalid email');
      else setError(err.message);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (!userCredential.user.emailVerified) {
        setAuthMode('verify');
        setError('Please verify your email first');
      }
    } catch (err) {
      if (err.code === 'auth/user-not-found') setError('No account found');
      else if (err.code === 'auth/wrong-password') setError('Incorrect password');
      else setError('Failed to sign in');
    }
  };

  const handleResendVerification = async () => {
    try {
      await sendEmailVerification(user);
      setSuccess('Verification email sent!');
    } catch { setError('Failed to send email'); }
  };

  const handleCheckVerification = async () => {
    await user.reload();
    if (user.emailVerified) {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserProfile({ id: user.uid, ...userData });
        
        // After verification, check if user needs to enter classroom code
        if (userData.hasAccess !== true || !userData.communityId) {
          setShowAccessCodeScreen(true);
        }
      }
      setAuthMode('login');
      setSuccess('Email verified! Please enter your classroom code.');
    } else setError('Email not verified yet');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!email) { setError('Enter your email'); return; }
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess('Reset email sent!');
    } catch { setError('Failed to send reset email'); }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setUserProfile(null);
    setShowAccessCodeScreen(false);
    setShowDropdown(false);
    setView('dashboard');
  };

  // Google Sign-In
  const handleGoogleSignIn = async () => {
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Check if user document exists
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // Create new user profile
        const newUser = {
          email: user.email,
          name: user.displayName || user.email.split('@')[0],
          avatar: '🔥',
          createdAt: serverTimestamp(),
          hasAccess: false,
          isAdmin: false,
          availability: {},
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
          weeklyGoal: 1,
          completedThisWeek: 0,
          isOnline: true,
          lastOnline: serverTimestamp(),
          dndMode: false
        };
        await setDoc(doc(db, 'users', user.uid), newUser);
        setUserProfile({ id: user.uid, ...newUser });
        setShowAccessCodeScreen(true);
      }
      // Existing user will be handled by onAuthStateChanged
    } catch (err) {
      debug.error('Google sign-in error:', err);
      setError('Google sign-in failed. Please try again.');
    }
  };

  // Cookie consent handlers
  const acceptAllCookies = () => {
    const consent = { essential: true, analytics: true, marketing: true, timestamp: Date.now() };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setCookiePreferences(consent);
    setShowCookieConsent(false);
  };

  const acceptEssentialOnly = () => {
    const consent = { essential: true, analytics: false, marketing: false, timestamp: Date.now() };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setCookiePreferences(consent);
    setShowCookieConsent(false);
  };

  const saveCookiePreferences = () => {
    const consent = { ...cookiePreferences, timestamp: Date.now() };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowCookieConsent(false);
    setShowCookieSettings(false);
  };

  // Access code verification
  const handleAccessCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      // Look up community by code
      const communityQuery = query(
        collection(db, 'communities'),
        where('code', '==', accessCode.trim())
      );
      const communitySnapshot = await getDocs(communityQuery);
      
      if (communitySnapshot.empty) {
        setError('Invalid classroom code. Please check with your instructor.');
        return;
      }
      
      const communityDoc = communitySnapshot.docs[0];
      const communityData = communityDoc.data();
      
      // Check if community has reached member limit (students only, admin not counted)
      const currentStudents = communityData.memberCount || 0;
      const maxStudents = communityData.maxMembers || 10; // Default to starter plan limit
      
      if (currentStudents >= maxStudents) {
        const planName = communityData.plan === 'pro' ? 'Pro' : 'Starter';
        setError(`This classroom has reached its maximum capacity (${maxStudents} students). Please contact your instructor to upgrade their ${planName} plan.`);
        return;
      }
      
      // Update user with community access
      await updateDoc(doc(db, 'users', userProfile.id), { 
        hasAccess: true,
        communityId: communityDoc.id,
        communityName: communityData.name,
        joinedAt: serverTimestamp()
      });
      
      // Increment member count in community
      await updateDoc(doc(db, 'communities', communityDoc.id), {
        memberCount: currentStudents + 1
      });
      
      setUserProfile({ 
        ...userProfile, 
        hasAccess: true, 
        communityId: communityDoc.id,
        communityName: communityData.name
      });
      setCommunityInfo({ id: communityDoc.id, ...communityData, memberCount: currentStudents + 1 });
      setShowAccessCodeScreen(false);
      setSuccess(`Welcome to ${communityData.name}!`);
    } catch (err) {
      debug.error('Access code error:', err);
      setError('Failed to join classroom. Please try again.');
    }
  };

  // Create community (Admin)
  const handleCreateCommunity = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!communityName.trim()) {
      setError('Please enter a community name');
      return;
    }
    if (!adminEmail.trim() || !adminPassword) {
      setError('Please enter your email and password');
      return;
    }
    if (adminPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!adminName.trim()) {
      setError('Please enter your name');
      return;
    }
    
    try {
      let userId;
      let isExistingUser = false;
      
      // First, try to sign in (for existing users who deleted their community)
      try {
        const signInResult = await signInWithEmailAndPassword(auth, adminEmail, adminPassword);
        userId = signInResult.user.uid;
        isExistingUser = true;
        debug.log('Existing user signed in:', userId);
        
        // Check if user already has a community
        const existingUserDoc = await getDoc(doc(db, 'users', userId));
        if (existingUserDoc.exists()) {
          const userData = existingUserDoc.data();
          if (userData.communityId && userData.hasAccess) {
            setError('You already have an active community. Please delete it first or use a different email.');
            await signOut(auth);
            return;
          }
        }
      } catch (signInErr) {
        // If sign in fails, try to create new account
        if (signInErr.code === 'auth/invalid-credential' || signInErr.code === 'auth/user-not-found' || signInErr.code === 'auth/wrong-password') {
          // Create new account
          try {
            const userCredential = await createUserWithEmailAndPassword(auth, adminEmail, adminPassword);
            userId = userCredential.user.uid;
            debug.log('New user created:', userId);
          } catch (createErr) {
            if (createErr.code === 'auth/email-already-in-use') {
              setError('This email is registered but the password is incorrect. Please try again or reset your password.');
              return;
            }
            throw createErr;
          }
        } else {
          throw signInErr;
        }
      }
      
      // Generate unique community code
      const code = generateCommunityCode();
      
      // Create community document
      const communityRef = await addDoc(collection(db, 'communities'), {
        name: communityName.trim(),
        code: code,
        adminId: userId,
        adminEmail: adminEmail,
        memberCount: 0, // Start at 0 - admin doesn't count toward student limit
        maxMembers: selectedPlan === 'pro' ? 50 : 10, // 10 students for Starter, 50 for Pro (admin not counted)
        plan: selectedPlan || 'starter',
        createdAt: serverTimestamp()
      });
      
      debug.log('Community created with plan:', selectedPlan, 'maxMembers:', selectedPlan === 'pro' ? 50 : 10);
      
      // Create or update admin user profile
      await setDoc(doc(db, 'users', userId), {
        name: adminName,
        email: adminEmail,
        avatar: '🧑‍🏫',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
        availability: {},
        hasAccess: true,
        isAdmin: true,
        communityId: communityRef.id,
        communityName: communityName.trim(),
        createdAt: serverTimestamp(),
        weeklyGoal: 1,
        completedThisWeek: 0,
        isOnline: false,
        dndMode: false
      }, { merge: true }); // merge: true to preserve any existing data
      
      // Send verification email for new users
      if (!isExistingUser && auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
      }
      
      setCreatedCommunityCode(code);
      setSuccess(`Community created! Your classroom code is: ${code}`);
      
    } catch (err) {
      debug.error('Create community error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. If you forgot your password, please reset it.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.');
      } else {
        setError('Failed to create community: ' + err.message);
      }
    }
  };

  // Submit contact sales form
  const handleContactSalesSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      // Save contact request to Firestore - viewable in Firebase Console
      // Go to: Firebase Console > Firestore > contactRequests collection
      await addDoc(collection(db, 'contactRequests'), {
        firstName: contactForm.firstName,
        lastName: contactForm.lastName,
        workEmail: contactForm.workEmail,
        companyName: contactForm.companyName,
        phoneNumber: contactForm.phoneNumber || '',
        specificNeeds: contactForm.specificNeeds || '',
        status: 'new',
        createdAt: serverTimestamp()
      });
      
      setSuccess('Thank you! Our team will contact you within 24 hours.');
      setContactForm({
        workEmail: '',
        firstName: '',
        lastName: '',
        companyName: '',
        phoneNumber: '',
        specificNeeds: ''
      });
      
      // Close modal after 3 seconds
      setTimeout(() => {
        setShowContactSales(false);
        setSuccess('');
      }, 3000);
      
    } catch (err) {
      debug.error('Contact form error:', err);
      setError('Failed to submit. Please try again.');
    }
  };

  // Simulate Stripe checkout (in production, use Stripe API)
  const handleStripeCheckout = async () => {
    if (!selectedPlan) {
      setError('Please select a plan first');
      return;
    }
    
    setIsProcessingPayment(true);
    setError('');
    
    const pricing = getCurrentPricing();
    const amount = selectedPlan === 'starter' ? pricing.starter : pricing.pro;
    
    try {
      // Create Stripe Checkout session via server
      const response = await fetch(`${ZOOM_SERVER_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: STRIPE_PRODUCTS[selectedPlan].productId,
          planName: STRIPE_PRODUCTS[selectedPlan].name,
          currency: pricing.code.toLowerCase(), // Pass currency to Stripe
          amount: amount, // Regional price
          successUrl: window.location.origin + '?checkout=success&plan=' + selectedPlan,
          cancelUrl: window.location.origin + '?checkout=cancelled',
        })
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to create checkout session');
      }
      
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      debug.error('Stripe checkout error:', err);
      setError('Payment error: ' + (err.message || 'Please try again.'));
      setIsProcessingPayment(false);
    }
  };
  
  // Handle Stripe redirect back (success/cancel)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutStatus = urlParams.get('checkout');
    const plan = urlParams.get('plan');
    const sessionId = urlParams.get('session_id');
    
    if (checkoutStatus === 'success' && plan) {
      setHasPaid(true);
      setSelectedPlan(plan);
      setLandingView('payment-success'); // Show success page first!
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Send welcome email via server (async, don't wait)
      if (sessionId) {
        fetch(`${ZOOM_SERVER_URL}/api/send-welcome-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, plan })
        }).catch(err => debug.log('Welcome email:', err));
      }
    } else if (checkoutStatus === 'cancelled') {
      setLandingView('pricing');
      setError('Payment was cancelled. Please try again.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  // Bug report submission
  const handleBugReportSubmit = async (e) => {
    e.preventDefault();
    setBugReportSubmitting(true);
    
    try {
      // Store bug report in Firebase
      await addDoc(collection(db, 'bugReports'), {
        type: bugReportForm.type,
        description: bugReportForm.description,
        email: bugReportForm.email || userProfile?.email || 'anonymous',
        page: bugReportForm.page || window.location.pathname,
        userAgent: navigator.userAgent,
        timestamp: serverTimestamp(),
        status: 'new',
        userId: userProfile?.id || null
      });
      
      setSuccess('Thank you! Your feedback has been submitted.');
      setShowBugReport(false);
      setBugReportForm({ type: 'bug', description: '', email: '', page: '' });
    } catch (err) {
      debug.error('Bug report error:', err);
      setError('Failed to submit feedback. Please try again.');
    }
    setBugReportSubmitting(false);
  };
  
  // Open Stripe Customer Portal
  const openBillingPortal = async () => {
    try {
      setError('');
      const response = await fetch(`${ZOOM_SERVER_URL}/api/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userProfile?.email, // Use email to lookup customer
          returnUrl: window.location.href
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Unable to open billing portal. Please contact support.');
      }
    } catch (err) {
      debug.error('Billing portal error:', err);
      setError('Unable to open billing portal. Please contact support.');
    }
  };

  // Admin: Kick member from community
  const kickMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the community?`)) return;
    
    try {
      // Remove community access from user
      await updateDoc(doc(db, 'users', memberId), {
        hasAccess: false,
        communityId: null,
        communityName: null,
        kickedAt: serverTimestamp()
      });
      
      // Decrement member count
      if (communityInfo) {
        await updateDoc(doc(db, 'communities', communityInfo.id), {
          memberCount: Math.max(0, (communityInfo.memberCount || 1) - 1)
        });
      }
      
      setSuccess(`${memberName} has been removed from the community.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      debug.error('Kick member error:', err);
      setError('Failed to remove member.');
    }
  };

  // Student: Leave community
  const leaveCommunity = async () => {
    try {
      // Remove community access from user
      await updateDoc(doc(db, 'users', userProfile.id), {
        hasAccess: false,
        communityId: null,
        communityName: null,
        availability: {},
        leftAt: serverTimestamp()
      });
      
      // Decrement member count
      if (communityInfo) {
        await updateDoc(doc(db, 'communities', communityInfo.id), {
          memberCount: Math.max(0, (communityInfo.memberCount || 1) - 1)
        });
      }
      
      // Reset local state
      setUserProfile({ ...userProfile, hasAccess: false, communityId: null, communityName: null, availability: {} });
      setCommunityInfo(null);
      setShowLeaveCommunityModal(false);
      setLeaveConfirmStep(1);
      setShowAccessCodeScreen(true);
      setSuccess('You have left the community.');
    } catch (err) {
      debug.error('Leave community error:', err);
      setError('Failed to leave community.');
    }
  };

  // Admin: Delete entire community
  const deleteCommunity = async () => {
    try {
      if (!communityInfo) return;
      
      // Remove all members from community
      const membersToUpdate = communityMembers.filter(m => m.id !== userProfile.id);
      for (const member of membersToUpdate) {
        await updateDoc(doc(db, 'users', member.id), {
          hasAccess: false,
          communityId: null,
          communityName: null,
          availability: {},
          communityDeletedAt: serverTimestamp()
        });
      }
      
      // Delete the community document
      await deleteDoc(doc(db, 'communities', communityInfo.id));
      
      // Remove admin's community access
      await updateDoc(doc(db, 'users', userProfile.id), {
        hasAccess: false,
        communityId: null,
        communityName: null,
        isAdmin: false,
        availability: {}
      });
      
      // Reset local state and log out
      setUserProfile(null);
      setCommunityInfo(null);
      setShowDeleteCommunityModal(false);
      setDeleteConfirmStep(1);
      setDeleteConfirmText('');
      await signOut(auth);
      setShowLanding(true);
      setLandingView('home');
    } catch (err) {
      debug.error('Delete community error:', err);
      setError('Failed to delete community.');
    }
  };

  // Admin: Start chat with member
  const startChatWithMember = async (member) => {
    try {
      // Check if conversation exists
      const existingConv = conversations.find(c => 
        c.participants.includes(member.id) && c.participants.includes(userProfile.id)
      );
      
      if (existingConv) {
        setActiveConversation(existingConv.id);
        setView('messages');
        return;
      }
      
      // Create new conversation
      const convRef = await addDoc(collection(db, 'conversations'), {
        participants: [userProfile.id, member.id],
        participantDetails: {
          [userProfile.id]: { name: userProfile.name, avatar: userProfile.avatar || '👤', timezone: userProfile.timezone },
          [member.id]: { name: member.name, avatar: member.avatar || '👤', timezone: member.timezone }
        },
        communityId: userProfile.communityId,
        createdAt: serverTimestamp(),
        lastMessage: '',
        lastMessageAt: serverTimestamp()
      });
      
      setActiveConversation(convRef.id);
      setView('messages');
    } catch (err) {
      debug.error('Start chat error:', err);
      setError('Failed to start chat.');
    }
  };

  // Get member statistics (scoped to current community)
  const getMemberStats = (member) => {
    const memberBookings = bookings.filter(b => 
      b.participants?.includes(member.id) && 
      b.status === 'completed' &&
      b.communityId === userProfile.communityId
    );
    return {
      totalSessions: memberBookings.length,
      lastActive: member.lastOnline || member.createdAt
    };
  };

  // Avatar selection
  const selectAvatar = async (avatar) => {
    try {
      await updateDoc(doc(db, 'users', userProfile.id), { avatar });
      setUserProfile({ ...userProfile, avatar });
      setProfileAvatar(avatar);
      setShowAvatarPicker(false);
    } catch (err) { setError('Failed to update avatar'); }
  };

  const saveProfile = async () => {
    try {
      const goalValue = profileWeeklyGoal === '' ? null : parseInt(profileWeeklyGoal, 10);
      await updateDoc(doc(db, 'users', userProfile.id), {
        bio: profileBio,
        timezone: profileTimezone,
        avatar: profileAvatar,
        weeklyGoal: goalValue
      });
      setUserProfile({ ...userProfile, bio: profileBio, timezone: profileTimezone, avatar: profileAvatar, weeklyGoal: goalValue });
      setEditingProfile(false);
    } catch { setError('Failed to save profile'); }
  };

  // Calendar helpers - Weekly view
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const start = new Date(today);
    start.setDate(today.getDate() - dayOfWeek); // Start from Sunday
    start.setHours(0, 0, 0, 0);
    return start;
  });

  const getDaysInWeek = (startDate) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const goToPreviousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(currentWeekStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const goToNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(currentWeekStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  const formatWeekRange = (startDate) => {
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    const startMonth = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endMonth = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startMonth} - ${endMonth}`;
  };

  const formatDateKey = (date) => {
    if (!date) return '';
    // Use local date components to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDatePast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getAvailabilityForDate = (dateKey) => {
    return userProfile?.availability?.[dateKey] || [];
  };

  // Get hours that are already booked for a specific date
  const getBookedHoursForDate = (dateKey) => {
    if (!userProfile || !bookings.length) return [];
    
    const bookedHours = [];
    
    for (const booking of bookings) {
      // Only check scheduled or in-progress bookings
      if (booking.status !== 'scheduled' && booking.status !== 'in-progress') continue;
      
      // Check if this booking involves the current user
      if (!booking.participants?.includes(userProfile.id)) continue;
      
      // Get the user's local time for this booking
      const myDetails = booking.participantDetails?.[userProfile.id];
      
      if (myDetails?.localDate === dateKey && myDetails?.localHour) {
        bookedHours.push(myDetails.localHour);
      } else if (booking.dateTime) {
        // Fallback to dateTime
        const bookingDate = booking.dateTime.split('T')[0];
        if (bookingDate === dateKey) {
          const hour = booking.dateTime.split('T')[1]?.split(':')[0];
          if (hour) bookedHours.push(hour);
        }
      }
    }
    
    return bookedHours;
  };

  // Complete onboarding tutorial
  const completeOnboarding = async () => {
    setShowOnboarding(false);
    try {
      await updateDoc(doc(db, 'users', userProfile.id), { hasCompletedOnboarding: true });
    } catch (err) {
      debug.error('Failed to save onboarding status:', err);
    }
  };

  // Skip to specific step in onboarding
  const handleOnboardingAction = (action) => {
    if (action === 'next') {
      if (onboardingStep < 3) {
        setOnboardingStep(onboardingStep + 1);
      } else {
        completeOnboarding();
      }
    } else if (action === 'skip') {
      completeOnboarding();
    } else if (action === 'calendar') {
      completeOnboarding();
      setView('calendar');
    }
  };

  // Availability handlers
  const handleDateClick = (date) => {
    if (isDatePast(date)) return;
    setSelectedDate(date);
    setSelectedHour(null);
    // Store original availability for this date (for cancel logic)
    const dateKey = formatDateKey(date);
    const currentHours = getAvailabilityForDate(dateKey);
    setTempAvailability([...currentHours]);
    setShowTimeModal(true);
  };

  // Toggle hour in temporary state (not saved yet)
  const toggleHourAvailability = (hour) => {
    setTempAvailability(prev => {
      if (prev.includes(hour)) {
        return prev.filter(h => h !== hour);
      } else {
        return [...prev, hour].sort((a, b) => a - b);
      }
    });
  };

  // Save availability changes to Firebase
  const saveAvailability = async () => {
    const dateKey = formatDateKey(selectedDate);
    const newAvailability = { ...userProfile.availability };
    
    if (tempAvailability.length === 0) {
      delete newAvailability[dateKey];
    } else {
      newAvailability[dateKey] = tempAvailability;
    }
    
    try {
      await updateDoc(doc(db, 'users', userProfile.id), { availability: newAvailability });
      setUserProfile({ ...userProfile, availability: newAvailability });
    } catch (error) {
      debug.error("Error saving availability:", error);
      setError("Failed to save availability.");
    }
    setShowTimeModal(false);
  };

  // Cancel availability changes (revert to original)
  const cancelAvailability = () => {
    // Just close modal - temp state is discarded
    setTempAvailability([]);
    setShowTimeModal(false);
  };

  // Reset all availability
  const resetAllAvailability = async () => {
    if (resetConfirmStep === 1) {
      setResetConfirmStep(2);
      return;
    }
    // Step 2 - actually reset
    try {
      await updateDoc(doc(db, 'users', userProfile.id), { availability: {} });
      setUserProfile({ ...userProfile, availability: {} });
      setShowResetModal(false);
      setResetConfirmStep(1);
      setSuccess('All availability slots have been cleared.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      debug.error("Error resetting availability:", error);
      setError("Failed to reset availability.");
    }
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetConfirmStep(1);
  };

  // Feedback submission
  const submitFeedbackRating = (rating) => {
    setFeedbackRating(rating);
    setFeedbackStep(2); // Move to reflection step
  };

  // Generate AI coaching tips from reflection
  // Generate weekly AI coaching insight from reflections
  const generateWeeklyCoachInsight = async () => {
    setIsGeneratingTips(true);
    try {
      // Get current week's reflections
      const now = new Date();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
      weekStart.setHours(0, 0, 0, 0);
      
      const weekReflections = reflections.filter(r => {
        const refDate = r.createdAt?.toDate ? r.createdAt.toDate() : new Date(r.createdAt);
        return refDate >= weekStart;
      });
      
      if (weekReflections.length === 0) {
        return null;
      }
      
      // Compile reflections into a summary
      const reflectionsSummary = weekReflections.map(r => 
        `- ${r.rating === 'great' ? '😊 Great session' : '😐 Okay session'}: "${r.reflection}"`
      ).join('\n');
      
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY || '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'}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `You are a supportive and insightful sales coach. A student has been practicing roleplay calls this week and shared their reflections. 
              
Your job is to:
1. Acknowledge their efforts and any progress
2. Identify patterns in their practice (what went well, common challenges)
3. Give 2-3 specific, actionable tips for next week
4. End with encouragement

Keep your response warm, professional, and under 250 words. Use "you" language to speak directly to the student.`
            },
            {
              role: 'user',
              content: `Here are the student's ${weekReflections.length} reflections from this week:\n\n${reflectionsSummary}\n\nProvide your weekly coaching insight.`
            }
          ],
          max_tokens: 400,
          temperature: 0.7
        })
      });
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }
      
      const data = await response.json();
      const insight = data.choices?.[0]?.message?.content;
      
      return insight || null;
    } catch (error) {
      debug.error('Weekly coach generation error:', error);
      return null;
    } finally {
      setIsGeneratingTips(false);
    }
  };

  // Check if weekly insight should be generated (once per week, end of week)
  const checkAndGenerateWeeklyInsight = async () => {
    if (!userProfile?.id || reflections.length === 0) return;
    
    // Get week number
    const now = new Date();
    const weekNum = Math.floor((now - new Date(now.getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));
    const lastInsightWeek = userProfile.lastCoachInsightWeek || 0;
    
    // Only generate if we haven't this week AND it's Friday or later AND user has reflections this week
    const dayOfWeek = now.getDay(); // 0=Sun, 5=Fri
    if (weekNum > lastInsightWeek && dayOfWeek >= 5) {
      // Check if user has reflections this week
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      weekStart.setHours(0, 0, 0, 0);
      
      const weekReflections = reflections.filter(r => {
        const refDate = r.createdAt?.toDate ? r.createdAt.toDate() : new Date(r.createdAt);
        return refDate >= weekStart;
      });
      
      if (weekReflections.length > 0) {
        const insight = await generateWeeklyCoachInsight();
        
        if (insight) {
          // Save the insight
          await addDoc(collection(db, 'coachInsights'), {
            userId: userProfile.id,
            weekNumber: weekNum,
            insight: insight,
            reflectionCount: weekReflections.length,
            createdAt: serverTimestamp()
          });
          
          // Update user's last insight week
          await updateDoc(doc(db, 'users', userProfile.id), {
            lastCoachInsightWeek: weekNum
          });
          
          // Send notification
          await addDoc(collection(db, 'notifications'), {
            userId: userProfile.id,
            type: 'weekly_coach',
            title: '🤖 Weekly Coach Insight',
            message: 'Your AI coach has analyzed your practice this week!',
            data: { insight: insight },
            read: false,
            createdAt: serverTimestamp()
          });
          
          // Show modal
          setWeeklyCoachInsight(insight);
          setShowWeeklyCoachModal(true);
        }
      }
    }
  };

  const submitFeedbackReflection = async () => {
    if (!feedbackBooking) return;
    
    try {
      // Get partner info
      const partnerId = feedbackBooking.participants.find(p => p !== userProfile.id);
      const partner = users.find(u => u.id === partnerId);
      
      // Save reflection to Firestore (without immediate AI tips)
      if (feedbackReflection.trim()) {
        await addDoc(collection(db, 'reflections'), {
          userId: userProfile.id,
          bookingId: feedbackBooking.id,
          partnerId: partnerId,
          partnerName: partner?.name || feedbackBooking.partnerName || 'Unknown',
          partnerAvatar: partner?.avatar || '👤',
          rating: feedbackRating,
          reflection: feedbackReflection.trim(),
          date: feedbackBooking.date || feedbackBooking.dateTime,
          communityId: userProfile.communityId,
          createdAt: serverTimestamp()
        });
        
        setToastMessage('Reflection saved!');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2000);
      }
      
      setFeedbackStep(3);
    } catch (error) {
      debug.error("Error saving reflection:", error);
      setFeedbackStep(3); // Continue anyway
    }
  };

  // Close weekly coach modal
  const closeWeeklyCoachModal = () => {
    setShowWeeklyCoachModal(false);
    setWeeklyCoachInsight(null);
  };

  // Submit combined feedback (reflection + admin question) without AI
  const submitCombinedFeedback = async () => {
    if (!feedbackBooking) return;
    
    try {
      const partnerId = feedbackBooking.participants.find(p => p !== userProfile.id);
      const partner = users.find(u => u.id === partnerId);
      
      // Save reflection if provided
      if (feedbackReflection.trim()) {
        await addDoc(collection(db, 'reflections'), {
          userId: userProfile.id,
          bookingId: feedbackBooking.id,
          partnerId: partnerId,
          partnerName: partner?.name || 'Partner',
          partnerAvatar: partner?.avatar || '👤',
          rating: feedbackRating,
          reflection: feedbackReflection.trim(),
          date: feedbackBooking.dateTime,
          communityId: userProfile.communityId,
          createdAt: serverTimestamp()
        });
      }
      
      // Save admin question if provided
      if (feedbackAdminQuestion.trim()) {
        const communityId = communityInfo?.id || userProfile?.communityId;
        if (communityId) {
          await addDoc(collection(db, 'adminQuestions'), {
            communityId: communityId,
            question: feedbackAdminQuestion.trim(),
            createdAt: serverTimestamp(),
            read: false
          });
        }
      }
      
      // Mark feedback as given
      const currentFeedbackBy = feedbackBooking.feedbackBy || [];
      await updateDoc(doc(db, 'bookings', feedbackBooking.id), {
        feedbackBy: [...currentFeedbackBy, userProfile.id]
      });
      
      setToastMessage('Feedback saved!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
      
      closeFeedbackModal();
    } catch (error) {
      debug.error("Error saving feedback:", error);
      closeFeedbackModal();
    }
  };

  const submitAdminQuestion = async () => {
    if (!feedbackBooking) return;
    
    try {
      // Save anonymous question to admin if provided
      if (feedbackAdminQuestion.trim()) {
        const communityId = communityInfo?.id || userProfile?.communityId;
        debug.log('Submitting admin question:', { 
          question: feedbackAdminQuestion.trim(),
          communityId,
          communityInfoId: communityInfo?.id,
          userProfileCommunityId: userProfile?.communityId
        });
        
        if (communityId) {
          const docRef = await addDoc(collection(db, 'adminQuestions'), {
            communityId: communityId,
            question: feedbackAdminQuestion.trim(),
            // Anonymous - NO userId or name stored
            createdAt: serverTimestamp(),
            read: false
          });
          debug.log('Admin question saved with ID:', docRef.id);
        } else {
          debug.error('No communityId available for admin question');
        }
      }
      
      // Mark that this user gave feedback
      const currentFeedbackBy = feedbackBooking.feedbackBy || [];
      await updateDoc(doc(db, 'bookings', feedbackBooking.id), {
        feedbackBy: [...currentFeedbackBy, userProfile.id]
      });
      
      // Show final toast
      if (feedbackAdminQuestion.trim()) {
        setToastMessage('Question sent anonymously to your instructor!');
      } else {
        setToastMessage('Feedback complete!');
      }
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      
      // Close modal
      closeFeedbackModal();
    } catch (error) {
      debug.error("Error submitting admin question:", error);
      closeFeedbackModal();
    }
  };

  const skipFeedback = async () => {
    if (!feedbackBooking) return;
    
    try {
      // Mark that this user skipped feedback
      const currentFeedbackBy = feedbackBooking.feedbackBy || [];
      await updateDoc(doc(db, 'bookings', feedbackBooking.id), {
        feedbackBy: [...currentFeedbackBy, userProfile.id]
      });
    } catch (error) {
      debug.error("Error marking feedback skipped:", error);
    }
    
    closeFeedbackModal();
  };

  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    setFeedbackStep(1);
    setFeedbackRating(null);
    setFeedbackReflection('');
    setFeedbackAdminQuestion('');
    setFeedbackBooking(null);
  };

  // Match finding
  const findMatch = () => {
    setIsMatching(true);
    setTimeout(() => {
      const myAvailability = userProfile.availability || {};
      const myTz = userProfile.timezone || 'America/New_York';
      
      debug.log('=== FIND MATCH DEBUG ===');
      debug.log('My ID:', userProfile.id);
      debug.log('My name:', userProfile.name);
      debug.log('My community:', userProfile.communityId);
      debug.log('My availability:', JSON.stringify(myAvailability));
      debug.log('My timezone:', myTz);
      debug.log('');
      debug.log('Total users in state:', users.length);
      
      if (users.length === 0) {
        debug.log('⚠️ NO USERS LOADED - this is the problem!');
        debug.log('Check if other users have:');
        debug.log('  - Same communityId as you');
        debug.log('  - hasAccess: true');
      }
      
      users.forEach(u => {
        debug.log(`User: ${u.name} (${u.id})`);
        debug.log(`  - communityId: ${u.communityId}`);
        debug.log(`  - hasAccess: ${u.hasAccess}`);
        debug.log(`  - isAdmin: ${u.isAdmin}`);
        debug.log(`  - availability: ${JSON.stringify(u.availability)}`);
        debug.log(`  - timezone: ${u.timezone}`);
      });
      
      // Filter to eligible users (not self, not admin, has availability)
      const eligibleUsers = users.filter(u => 
        u.id !== userProfile.id && 
        !u.isAdmin && 
        u.availability && 
        Object.keys(u.availability).length > 0
      );
      
      debug.log('');
      debug.log('Eligible users for matching:', eligibleUsers.length);
      
      if (eligibleUsers.length === 0 && users.length > 0) {
        debug.log('⚠️ Users exist but none eligible. Check:');
        users.forEach(u => {
          if (u.id === userProfile.id) debug.log(`  - ${u.name}: SKIPPED (is self)`);
          else if (u.isAdmin) debug.log(`  - ${u.name}: SKIPPED (is admin)`);
          else if (!u.availability) debug.log(`  - ${u.name}: SKIPPED (no availability object)`);
          else if (Object.keys(u.availability).length === 0) debug.log(`  - ${u.name}: SKIPPED (availability is empty)`);
          else debug.log(`  - ${u.name}: ELIGIBLE`);
        });
      }
      
      for (const [dateKey, hours] of Object.entries(myAvailability)) {
        // Skip past dates
        const dateToCheck = new Date(dateKey + 'T12:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (dateToCheck < today) {
          debug.log('Skipping past date:', dateKey);
          continue;
        }
        
        debug.log(`\nChecking date ${dateKey}, my hours:`, hours);
        
        for (const hour of hours) {
          for (const otherUser of eligibleUsers) {
            const theirTz = otherUser.timezone || 'America/New_York';
            
            // If same timezone, direct comparison
            if (myTz === theirTz) {
              const theirHours = otherUser.availability[dateKey] || [];
              debug.log(`  Comparing with ${otherUser.name}: my hour="${hour}" (type: ${typeof hour}), their hours=`, theirHours.map(h => `"${h}" (type: ${typeof h})`));
              
              // Try multiple comparison methods
              const hasMatch = theirHours.includes(hour) || 
                               theirHours.some(h => h === hour) ||
                               theirHours.some(h => String(h) === String(hour));
              
              debug.log(`  Match result: ${hasMatch}`);
              
              if (hasMatch) {
                debug.log('✅ MATCH FOUND (same tz):', otherUser.name, dateKey, hour);
                setMatchResult({
                  user: otherUser,
                  date: dateKey,
                  hour: hour,
                  theirDate: dateKey,
                  theirHour: hour
                });
                setIsMatching(false);
                setShowMatchModal(true);
                return;
              }
            } else {
              // Different timezone - convert
              const converted = convertTimezone(dateKey, hour, myTz, theirTz);
              const theirHours = otherUser.availability[converted.date] || [];
              
              debug.log(`  Comparing with ${otherUser.name} (diff tz): my ${hour} -> their ${converted.hour} on ${converted.date}`);
              debug.log('  Their hours:', theirHours);
              
              // Try multiple comparison methods
              const hasMatch = theirHours.includes(converted.hour) ||
                               theirHours.some(h => h === converted.hour) ||
                               theirHours.some(h => String(h) === String(converted.hour));
              
              if (hasMatch) {
                debug.log('✅ MATCH FOUND (diff tz):', otherUser.name);
                setMatchResult({
                  user: otherUser,
                  date: dateKey,
                  hour: hour,
                  theirDate: converted.date,
                  theirHour: converted.hour
                });
                setIsMatching(false);
                setShowMatchModal(true);
                return;
              }
            }
          }
        }
      }
      
      debug.log('\n❌ No match found');
      setMatchResult(null);
      setIsMatching(false);
      setShowMatchModal(true);
    }, 1500);
  };

  // Create Zoom meeting via server
  const createZoomMeeting = async (topic, startTime) => {
    try {
      debug.log('Creating Zoom meeting via:', ZOOM_SERVER_URL);
      
      const response = await fetch(`${ZOOM_SERVER_URL}/api/create-meeting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          topic: topic,
          startTime: startTime,
          duration: 60
        })
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        return {
          joinUrl: data.meeting.joinUrl,
          meetingId: data.meeting.id,
          password: data.meeting.password
        };
      } else {
        debug.error('Zoom API error:', data.error);
        throw new Error('Failed to create Zoom meeting: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      debug.error('Failed to create Zoom meeting:', error);
      // Provide more helpful error message
      const errorMsg = error.message.includes('Failed to fetch') 
        ? 'Cannot connect to Zoom server. Make sure the server is running and accessible at: ' + ZOOM_SERVER_URL
        : error.message;
      throw new Error(errorMsg);
    }
  };

  // Book session
  const bookSession = async () => {
    if (!matchResult || isBookingInProgress) return;
    
    try {
      // Show booking overlay immediately to prevent double-clicks
      setIsBookingInProgress(true);
      setShowMatchModal(false);
      setBookingOverlayData({
        partnerName: matchResult.user.name,
        partnerAvatar: matchResult.user.avatar,
        date: matchResult.date,
        hour: matchResult.hour
      });
      setShowBookingOverlay(true);
      
      // RACE CONDITION CHECK: Re-verify the other user's availability before booking
      // Their availability might have changed since we found the match
      const otherUserDoc = await getDoc(doc(db, 'users', matchResult.user.id));
      if (!otherUserDoc.exists()) {
        setShowBookingOverlay(false);
        setIsBookingInProgress(false);
        setError('User no longer available. Finding new match...');
        setTimeout(() => findMatch(), 1000);
        return;
      }
      
      const currentOtherUser = otherUserDoc.data();
      const theirCurrentHours = currentOtherUser.availability?.[matchResult.theirDate] || [];
      
      if (!theirCurrentHours.includes(matchResult.theirHour)) {
        // Slot was taken! Show error and re-search
        setShowBookingOverlay(false);
        setIsBookingInProgress(false);
        setError(`${matchResult.user.name}'s availability changed. Finding new match...`);
        setTimeout(() => findMatch(), 1000);
        return;
      }
      
      // Create Zoom meeting
      const meetingTopic = `MeetSync: ${userProfile.name} & ${matchResult.user.name}`;
      const meetingStartTime = `${matchResult.date}T${matchResult.hour}:00`;
      const zoomMeeting = await createZoomMeeting(meetingTopic, meetingStartTime);
      
      // Create booking - store times for both users in their respective timezones
      const bookingData = {
        participants: [userProfile.id, matchResult.user.id],
        participantNames: {
          [userProfile.id]: userProfile.name,
          [matchResult.user.id]: matchResult.user.name
        },
        participantDetails: {
          [userProfile.id]: {
            name: userProfile.name,
            avatar: userProfile.avatar,
            timezone: userProfile.timezone,
            // This user's local date/time
            localDate: matchResult.date,
            localHour: matchResult.hour
          },
          [matchResult.user.id]: {
            name: matchResult.user.name,
            avatar: matchResult.user.avatar,
            timezone: matchResult.user.timezone,
            // Other user's local date/time
            localDate: matchResult.theirDate,
            localHour: matchResult.theirHour
          }
        },
        // Store the booker's time as reference
        dateTime: `${matchResult.date}T${matchResult.hour}`,
        bookerTimezone: userProfile.timezone,
        meetingLink: zoomMeeting.joinUrl,
        zoomMeetingId: zoomMeeting.meetingId,
        zoomPassword: zoomMeeting.password,
        type: 'scheduled', // Mark as scheduled (triggers feedback on completion)
        status: 'scheduled',
        reminderSent: false,
        communityId: userProfile.communityId, // For admin stats
        createdAt: serverTimestamp()
      };
      
      const bookingRef = await addDoc(collection(db, 'bookings'), bookingData);
      debug.log("Booking created:", bookingRef.id);
      
      // NOTIFY THE MATCHED USER (User B)
      const formattedDate = new Date(matchResult.theirDate).toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric' 
      });
      await addDoc(collection(db, 'notifications'), {
        userId: matchResult.user.id,
        type: 'match_booked',
        title: 'New Match Found!',
        message: `${userProfile.name} has booked a roleplay with you for ${formattedDate} at ${formatHour(matchResult.theirHour)}`,
        data: { 
          bookingId: bookingRef.id,
          bookerName: userProfile.name,
          bookerAvatar: userProfile.avatar,
          date: matchResult.theirDate,
          hour: matchResult.theirHour
        },
        read: false,
        createdAt: serverTimestamp()
      });
      
      // Remove availability from current user
      const myNewAvailability = { ...userProfile.availability };
      const myDateHours = myNewAvailability[matchResult.date] || [];
      myNewAvailability[matchResult.date] = myDateHours.filter(h => h !== matchResult.hour);
      if (myNewAvailability[matchResult.date].length === 0) delete myNewAvailability[matchResult.date];
      
      await updateDoc(doc(db, 'users', userProfile.id), { availability: myNewAvailability });
      setUserProfile({ ...userProfile, availability: myNewAvailability });
      
      // Remove from other user
      const theirNewAvailability = { ...matchResult.user.availability };
      const theirDateHours = theirNewAvailability[matchResult.theirDate] || [];
      theirNewAvailability[matchResult.theirDate] = theirDateHours.filter(h => h !== matchResult.theirHour);
      if (theirNewAvailability[matchResult.theirDate].length === 0) delete theirNewAvailability[matchResult.theirDate];
      
      await updateDoc(doc(db, 'users', matchResult.user.id), { availability: theirNewAvailability });
      
      // Create or update conversation
      let convId;
      const existingConv = conversations.find(c => c.participants.includes(matchResult.user.id));
      if (existingConv) {
        convId = existingConv.id;
        // Update existing conversation with new meeting link
        await updateDoc(doc(db, 'conversations', convId), {
          meetingLink: zoomMeeting.joinUrl,
          lastMessage: `Call scheduled for ${matchResult.date} at ${formatHour(matchResult.hour)}`
        });
      } else {
        const convRef = await addDoc(collection(db, 'conversations'), {
          participants: [userProfile.id, matchResult.user.id],
          participantDetails: bookingData.participantDetails,
          communityId: userProfile.communityId,
          bookingId: bookingRef.id,
          meetingLink: zoomMeeting.joinUrl,
          createdAt: serverTimestamp(),
          lastMessage: `Call scheduled for ${matchResult.date} at ${formatHour(matchResult.hour)}`
        });
        convId = convRef.id;
      }
      
      // Add system message with meeting link
      await addDoc(collection(db, 'conversations', convId, 'messages'), {
        senderId: 'system',
        text: `Call SCHEDULED for ${matchResult.date} at ${formatHour(matchResult.hour)}`,
        timestamp: serverTimestamp()
      });
      
      // Add meeting link message
      await addDoc(collection(db, 'conversations', convId, 'messages'), {
        senderId: 'system',
        text: `Zoom Meeting Link: ${zoomMeeting.joinUrl}`,
        meetingLink: zoomMeeting.joinUrl,
        timestamp: serverTimestamp()
      });
      
      // Close overlay and navigate
      setShowBookingOverlay(false);
      setIsBookingInProgress(false);
      setBookingOverlayData(null);
      setActiveConversation(convId);
      setMatchResult(null);
      setView('messages');
      
      // Show success toast
      setToastMessage('Session booked successfully!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      
    } catch (error) {
      debug.error("Error booking session:", error);
      setShowBookingOverlay(false);
      setIsBookingInProgress(false);
      setBookingOverlayData(null);
      setError("Failed to book session. Please try again.");
    }
  };

  // Cancel a booked session
  const handleCancelBooking = async () => {
    if (!cancellingBooking || isCancelling) return;
    
    setIsCancelling(true);
    
    try {
      const result = await cancelBookingFn({
        bookingId: cancellingBooking.id,
        reason: cancelReason.trim() || undefined
      });
      
      debug.log('Booking cancelled:', result.data);
      
      // Close modal and show success
      setShowCancelModal(false);
      setCancellingBooking(null);
      setCancelReason('');
      
      setToastMessage(result.data.message || 'Session cancelled');
      setToastType('success');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      
    } catch (error) {
      debug.error('Cancel booking error:', error);
      setError(error.message || 'Failed to cancel booking');
    } finally {
      setIsCancelling(false);
    }
  };

  // Open cancel modal for a booking
  const openCancelModal = (booking) => {
    setCancellingBooking(booking);
    setCancelReason('');
    setShowCancelModal(true);
  };

  // Get time until meeting
  const getTimeUntilMeeting = (booking) => {
    const myDetails = booking.participantDetails?.[userProfile?.id];
    let meetingTime;
    if (myDetails?.localDate && myDetails?.localHour) {
      meetingTime = new Date(`${myDetails.localDate}T${myDetails.localHour}`);
    } else {
      meetingTime = new Date(booking.dateTime);
    }
    const now = new Date();
    const diffMs = meetingTime - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffMs < 0) return 'Started';
    if (diffHours > 24) return `${Math.floor(diffHours / 24)}d ${diffHours % 24}h`;
    if (diffHours > 0) return `${diffHours}h ${diffMins}m`;
    return `${diffMins}m`;
  };

  // Check if booking can be cancelled (1 hour before)
  const canCancelBooking = (booking) => {
    if (booking.status === 'cancelled' || booking.status === 'completed' || booking.status === 'in-progress') {
      return false;
    }
    const myDetails = booking.participantDetails?.[userProfile?.id];
    let meetingTime;
    if (myDetails?.localDate && myDetails?.localHour) {
      meetingTime = new Date(`${myDetails.localDate}T${myDetails.localHour}`);
    } else {
      meetingTime = new Date(booking.dateTime);
    }
    const now = new Date();
    const hoursUntil = (meetingTime - now) / (1000 * 60 * 60);
    return hoursUntil > 0; // Can cancel anytime before meeting starts
  };

  // Admin: Send broadcast notification to all students
  const sendBroadcastNotification = async () => {
    if (!broadcastMessage.trim() || isSendingBroadcast) return;
    
    setIsSendingBroadcast(true);
    
    try {
      // Get all students in this community (exclude admin)
      const students = users.filter(u => !u.isAdmin && u.communityId === userProfile.communityId);
      
      // Create notification for each student
      const batch = [];
      for (const student of students) {
        batch.push(addDoc(collection(db, 'notifications'), {
          userId: student.id,
          type: 'admin_broadcast',
          title: '📢 Announcement',
          message: broadcastMessage.trim(),
          data: { 
            fromAdmin: userProfile.name,
            communityId: userProfile.communityId 
          },
          read: false,
          createdAt: serverTimestamp()
        }));
      }
      
      await Promise.all(batch);
      
      setShowBroadcastModal(false);
      setBroadcastMessage('');
      setToastMessage(`Notification sent to ${students.length} student${students.length !== 1 ? 's' : ''}!`);
      setToastType('success');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      
    } catch (error) {
      debug.error('Broadcast error:', error);
      setError('Failed to send notification');
    } finally {
      setIsSendingBroadcast(false);
    }
  };

  // Admin: View student profile with activity graph
  const openStudentProfile = (student) => {
    setSelectedStudent(student);
    setShowStudentProfileModal(true);
  };

  // Get student's completed calls by day (for activity graph)
  const getStudentActivityData = (studentId) => {
    const studentBookings = bookings.filter(
      b => b.participants?.includes(studentId) && 
           b.status === 'completed' &&
           b.communityId === userProfile.communityId
    );
    
    // Get the student's timezone
    const student = users.find(u => u.id === studentId);
    const studentTimezone = student?.timezone || 'UTC';
    
    // Group by date (in student's timezone)
    const activityByDate = {};
    
    studentBookings.forEach(booking => {
      // Get the student's local date for this booking
      const studentDetails = booking.participantDetails?.[studentId];
      let dateStr;
      
      if (studentDetails?.localDate) {
        dateStr = studentDetails.localDate;
      } else if (booking.completedAt) {
        // Convert completedAt to student's timezone
        const completedDate = booking.completedAt.toDate ? booking.completedAt.toDate() : new Date(booking.completedAt);
        dateStr = completedDate.toISOString().split('T')[0];
      } else if (booking.dateTime) {
        dateStr = booking.dateTime.split('T')[0];
      } else {
        return;
      }
      
      activityByDate[dateStr] = (activityByDate[dateStr] || 0) + 1;
    });
    
    // Create array for last 30 days
    const data = [];
    const today = new Date();
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      data.push({
        date: dateStr,
        shortDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        calls: activityByDate[dateStr] || 0
      });
    }
    
    return data;
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    
    const conv = conversations.find(c => c.id === activeConversation);
    const otherUserId = conv?.participants.find(id => id !== userProfile.id);
    
    await addDoc(collection(db, 'conversations', activeConversation, 'messages'), {
      senderId: userProfile.id,
      text: newMessage.trim(),
      timestamp: serverTimestamp()
    });
    
    // Update conversation with last message info and clear typing
    await updateDoc(doc(db, 'conversations', activeConversation), {
      lastMessage: newMessage.trim(),
      lastMessageAt: serverTimestamp(),
      lastMessageSenderId: userProfile.id,
      [`typing.${userProfile.id}`]: null // Clear typing indicator
    });
    
    // Send notification to other user
    if (otherUserId) {
      await addDoc(collection(db, 'notifications'), {
        userId: otherUserId,
        type: 'message',
        title: '💬 New Message',
        message: `${userProfile.name}: ${newMessage.trim().substring(0, 50)}${newMessage.length > 50 ? '...' : ''}`,
        data: { conversationId: activeConversation },
        read: false,
        createdAt: serverTimestamp()
      });
    }
    
    setNewMessage('');
    setIsTyping(false);
  };

  // Handle typing indicator
  const handleTyping = (value) => {
    setNewMessage(value);
    
    if (!activeConversation || !userProfile) return;
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set typing indicator
    if (value.trim() && !isTyping) {
      setIsTyping(true);
      updateDoc(doc(db, 'conversations', activeConversation), {
        [`typing.${userProfile.id}`]: serverTimestamp()
      }).catch(err => debug.error('Typing indicator error:', err));
    }
    
    // Clear typing after 2 seconds of no typing
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      if (activeConversation) {
        updateDoc(doc(db, 'conversations', activeConversation), {
          [`typing.${userProfile.id}`]: null
        }).catch(err => debug.error('Clear typing error:', err));
      }
    }, 2000);
  };

  const getOtherUser = (conv) => {
    const otherId = conv.participants?.find(id => id !== userProfile?.id);
    if (!otherId) return { name: 'Unknown', avatar: '👤' };
    
    // Try to get from participantDetails first (always stored), then from live users
    const fromDetails = conv.participantDetails?.[otherId];
    const fromUsers = users.find(u => u.id === otherId);
    
    return fromUsers || fromDetails || { name: 'Unknown', avatar: '👤' };
  };

  const goToBookingChat = (booking) => {
    const conv = conversations.find(c => c.participants.includes(
      booking.participants.find(p => p !== userProfile.id)
    ));
    if (conv) {
      setActiveConversation(conv.id);
      setView('messages');
    }
  };

  // Generate ICS file for calendar export
  const generateICSFile = (booking) => {
    const myDetails = booking.participantDetails?.[userProfile.id];
    const startDate = myDetails?.localDate || booking.dateTime?.split('T')[0];
    const startHour = myDetails?.localHour || booking.dateTime?.split('T')[1]?.split(':')[0] + ':00';
    
    const start = new Date(`${startDate}T${startHour.replace(':00', ':00:00')}`);
    const end = new Date(start.getTime() + 60 * 60 * 1000); // 1 hour
    
    const formatDate = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    
    const otherId = booking.participants?.find(p => p !== userProfile.id);
    const otherName = booking.participantNames?.[otherId] || 'Partner';
    
    const ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//MeetSync//Roleplay Session//EN
BEGIN:VEVENT
UID:${booking.id}@meetsync.app
DTSTAMP:${formatDate(new Date())}
DTSTART:${formatDate(start)}
DTEND:${formatDate(end)}
SUMMARY:MeetSync Roleplay with ${otherName}
DESCRIPTION:Sales roleplay practice session\\n\\nZoom Link: ${booking.meetingLink || 'TBD'}
LOCATION:${booking.meetingLink || 'Zoom'}
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([ics], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meetsync-${startDate}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Generate Google Calendar URL
  const getGoogleCalendarUrl = (booking) => {
    const myDetails = booking.participantDetails?.[userProfile.id];
    const startDate = myDetails?.localDate || booking.dateTime?.split('T')[0];
    const startHour = myDetails?.localHour || booking.dateTime?.split('T')[1]?.split(':')[0] + ':00';
    
    const start = new Date(`${startDate}T${startHour.replace(':00', ':00:00')}`);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    
    const formatForGoogle = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    
    const otherId = booking.participants?.find(p => p !== userProfile.id);
    const otherName = booking.participantNames?.[otherId] || 'Partner';
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `MeetSync Roleplay with ${otherName}`,
      dates: `${formatForGoogle(start)}/${formatForGoogle(end)}`,
      details: `Sales roleplay practice session\n\nZoom Link: ${booking.meetingLink || 'TBD'}`,
      location: booking.meetingLink || 'Zoom'
    });
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Check if user is online (seen in last 3 minutes)
  const isUserOnline = (user) => {
    if (user.isOnline) return true;
    if (!user.lastSeen) return false;
    const lastSeen = user.lastSeen.toDate ? user.lastSeen.toDate() : new Date(user.lastSeen);
    const threeMinutesAgo = new Date(Date.now() - 3 * 60 * 1000);
    return lastSeen > threeMinutesAgo;
  };

  // Get call status based on time
  const getCallStatus = (dateTime) => {
    const callTime = new Date(dateTime);
    const now = currentTime;
    const oneHourAfter = new Date(callTime.getTime() + 60 * 60 * 1000);
    
    if (now < callTime) return 'SCHEDULED';
    if (now >= callTime && now < oneHourAfter) return 'IN PROGRESS';
    return 'COMPLETED';
  };

  // Start direct chat with a user
  const startDirectChat = async (otherUser) => {
    // Check if conversation already exists
    const existingConv = conversations.find(c => c.participants.includes(otherUser.id));
    
    if (existingConv) {
      setActiveConversation(existingConv.id);
      setView('messages');
      return;
    }

    // Create new conversation
    try {
      const convRef = await addDoc(collection(db, 'conversations'), {
        participants: [userProfile.id, otherUser.id],
        participantDetails: {
          [userProfile.id]: {
            name: userProfile.name,
            avatar: userProfile.avatar || '👤',
            timezone: userProfile.timezone
          },
          [otherUser.id]: {
            name: otherUser.name,
            avatar: otherUser.avatar || '👤',
            timezone: otherUser.timezone
          }
        },
        communityId: userProfile.communityId,
        createdAt: serverTimestamp(),
        lastMessage: 'Conversation started'
      });

      // Add system message
      await addDoc(collection(db, 'conversations', convRef.id, 'messages'), {
        senderId: 'system',
        text: '👋 Conversation started! Say hello!',
        timestamp: serverTimestamp()
      });

      setActiveConversation(convRef.id);
      setView('messages');
    } catch (error) {
      debug.error("Error starting chat:", error);
    }
  };

  // Open invite modal for a user
  const openInviteModal = (otherUser) => {
    setInviteUser(otherUser);
    setInviteWeekStart(getWeekStart(new Date()));
    setSelectedInviteSlot(null);
    setShowInviteModal(true);
  };

  // Get week start date (Monday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Get week days for invite modal
  const getInviteWeekDays = () => {
    const days = [];
    const start = new Date(inviteWeekStart);
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // Get timezone abbreviation
  const getTimezoneAbbr = (tzValue) => {
    if (!tzValue) return 'N/A';
    const tz = TIMEZONES.find(t => t.value === tzValue);
    if (!tz) return tzValue.split('/').pop() || 'N/A';
    // Extract abbreviation from label like "Eastern Time (EST)" -> "EST"
    const match = tz.label.match(/\(([^)]+)\)/);
    return match ? match[1] : tz.label.split(' ')[0];
  };

  // Get booking time in current user's local timezone
  const getMyBookingTime = (booking) => {
    // First try to get from stored participantDetails
    const myDetails = booking.participantDetails?.[userProfile.id];
    if (myDetails?.localDate && myDetails?.localHour) {
      return {
        date: myDetails.localDate,
        hour: myDetails.localHour
      };
    }
    
    // Otherwise convert from booker's timezone
    if (booking.bookerTimezone && booking.dateTime) {
      const bookingDate = booking.dateTime.split('T')[0];
      const bookingHour = booking.dateTime.split('T')[1]?.split(':')[0] + ':00';
      return convertTimezone(bookingDate, bookingHour, booking.bookerTimezone, userProfile.timezone || 'America/New_York');
    }
    
    // Fallback to raw dateTime
    const dateTime = booking.dateTime || booking.date;
    if (dateTime) {
      const parts = dateTime.split('T');
      return {
        date: parts[0],
        hour: (parts[1]?.split(':')[0] || '12') + ':00'
      };
    }
    
    return { date: 'Unknown', hour: '12:00' };
  };

  // Send invite message
  const sendInvite = async () => {
    if (!selectedInviteSlot || !inviteUser || !activeConversation) return;
    
    try {
      // Create invite message - store both timezones' dates/hours
      await addDoc(collection(db, 'conversations', activeConversation, 'messages'), {
        senderId: userProfile.id,
        type: 'invite',
        inviteData: {
          // Inviter's view (my timezone)
          myDate: selectedInviteSlot.myDate,
          myHour: selectedInviteSlot.myHour,
          // Invitee's view (their timezone)  
          theirDate: selectedInviteSlot.theirDate,
          theirHour: selectedInviteSlot.theirHour,
          senderTimezone: userProfile.timezone,
          receiverTimezone: inviteUser.timezone,
          status: 'pending'
        },
        text: `📅 Meeting invite for ${formatHour(selectedInviteSlot.myHour)}`,
        timestamp: serverTimestamp()
      });

      await updateDoc(doc(db, 'conversations', activeConversation), {
        lastMessage: 'Sent a meeting invite',
        lastMessageAt: serverTimestamp()
      });

      // Send notification to invitee - show in THEIR timezone
      await addDoc(collection(db, 'notifications'), {
        userId: inviteUser.id,
        type: 'invite',
        title: 'New Call Invite',
        message: `${userProfile.name} invited you to a call on ${selectedInviteSlot.theirDate} at ${formatHour(selectedInviteSlot.theirHour)}`,
        data: { conversationId: activeConversation },
        read: false,
        createdAt: serverTimestamp()
      });

      setShowInviteModal(false);
      setSelectedInviteSlot(null);
    } catch (error) {
      debug.error("Error sending invite:", error);
      setError("Failed to send invite");
    }
  };

  // Accept invite - creates booking and removes availability
  // Accept invite - creates booking and removes availability
  const acceptInvite = async (messageId, inviteData) => {
    if (!activeConversation) return;
    
    try {
      const conv = conversations.find(c => c.id === activeConversation);
      const otherUserId = conv.participants.find(id => id !== userProfile.id);
      const otherUser = users.find(u => u.id === otherUserId) || conv.participantDetails?.[otherUserId];

      // The receiver (current user accepting) sees theirDate/theirHour
      // The sender sees myDate/myHour
      const receiverDate = inviteData.theirDate || inviteData.date;
      const receiverHour = inviteData.theirHour || inviteData.hour;
      const senderDate = inviteData.myDate || inviteData.date;
      const senderHour = inviteData.myHour || inviteData.hour;

      // Create Zoom meeting
      const meetingTopic = `MeetSync: ${userProfile.name} & ${otherUser?.name}`;
      const meetingStartTime = `${receiverDate}T${receiverHour}`;
      const zoomMeeting = await createZoomMeeting(meetingTopic, meetingStartTime);

      // Create booking with user-specific times
      const bookingData = {
        participants: [userProfile.id, otherUserId],
        participantDetails: {
          [userProfile.id]: {
            name: userProfile.name,
            avatar: userProfile.avatar,
            timezone: userProfile.timezone,
            // Receiver's local time (the invited person, current user accepting)
            localDate: receiverDate,
            localHour: receiverHour
          },
          [otherUserId]: {
            name: otherUser?.name,
            avatar: otherUser?.avatar,
            timezone: otherUser?.timezone,
            // Sender's local time
            localDate: senderDate,
            localHour: senderHour
          }
        },
        dateTime: `${receiverDate}T${receiverHour}`,
        bookerTimezone: inviteData.receiverTimezone || userProfile.timezone,
        meetingLink: zoomMeeting.joinUrl,
        zoomMeetingId: zoomMeeting.meetingId,
        zoomPassword: zoomMeeting.password,
        status: 'scheduled',
        conversationId: activeConversation,
        communityId: userProfile.communityId, // For admin stats
        createdAt: serverTimestamp()
      };
      
      await addDoc(collection(db, 'bookings'), bookingData);

      // Remove from receiver (current user) availability
      const myNewAvailability = { ...userProfile.availability };
      const myDateHours = myNewAvailability[receiverDate] || [];
      myNewAvailability[receiverDate] = myDateHours.filter(h => h !== receiverHour);
      if (myNewAvailability[receiverDate]?.length === 0) delete myNewAvailability[receiverDate];
      
      await updateDoc(doc(db, 'users', userProfile.id), { availability: myNewAvailability });
      setUserProfile({ ...userProfile, availability: myNewAvailability });

      // Remove from sender availability
      const senderDoc = await getDoc(doc(db, 'users', otherUserId));
      if (senderDoc.exists()) {
        const senderData = senderDoc.data();
        const senderAvail = { ...senderData.availability };
        const senderDateHours = senderAvail[senderDate] || [];
        senderAvail[senderDate] = senderDateHours.filter(h => h !== senderHour);
        if (senderAvail[senderDate]?.length === 0) delete senderAvail[senderDate];
        await updateDoc(doc(db, 'users', otherUserId), { availability: senderAvail });
      }

      // Update the invite message status
      await updateDoc(doc(db, 'conversations', activeConversation, 'messages', messageId), {
        'inviteData.status': 'accepted'
      });

      // Add system messages
      await addDoc(collection(db, 'conversations', activeConversation, 'messages'), {
        senderId: 'system',
        text: `🎉 Meeting CONFIRMED for ${receiverDate} at ${formatHour(receiverHour)}!`,
        timestamp: serverTimestamp()
      });

      await addDoc(collection(db, 'conversations', activeConversation, 'messages'), {
        senderId: 'system',
        text: `📹 Zoom Meeting Link`,
        meetingLink: zoomMeeting.joinUrl,
        zoomMeetingId: zoomMeeting.meetingId,
        zoomPassword: zoomMeeting.password,
        timestamp: serverTimestamp()
      });

      await updateDoc(doc(db, 'conversations', activeConversation), {
        lastMessage: 'Meeting confirmed!',
        meetingLink: zoomMeeting.joinUrl,
        lastMessageAt: serverTimestamp()
      });

      // Send notification to sender that invite was accepted
      await addDoc(collection(db, 'notifications'), {
        userId: otherUserId,
        type: 'booking',
        title: 'Invite Accepted!',
        message: `${userProfile.name} accepted your call invite for ${senderDate} at ${formatHour(senderHour)}`,
        data: { conversationId: activeConversation },
        read: false,
        createdAt: serverTimestamp()
      });

      setSuccess('Meeting confirmed!');
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      debug.error("Error accepting invite:", error);
      setError("Failed to accept invite. Please try again.");
    }
  };

  // Decline invite
  const declineInvite = async (messageId) => {
    if (!activeConversation) return;
    
    try {
      await updateDoc(doc(db, 'conversations', activeConversation, 'messages', messageId), {
        'inviteData.status': 'declined'
      });

      await addDoc(collection(db, 'conversations', activeConversation, 'messages'), {
        senderId: 'system',
        text: '❌ Meeting invite was declined',
        timestamp: serverTimestamp()
      });

      await updateDoc(doc(db, 'conversations', activeConversation), {
        lastMessage: 'Meeting invite declined',
        lastMessageAt: serverTimestamp()
      });
    } catch (error) {
      debug.error("Error declining invite:", error);
    }
  };

  // Cancel invite (for sender)
  const cancelInvite = async (messageId) => {
    if (!activeConversation) return;
    
    try {
      await updateDoc(doc(db, 'conversations', activeConversation, 'messages', messageId), {
        'inviteData.status': 'cancelled'
      });

      await addDoc(collection(db, 'conversations', activeConversation, 'messages'), {
        senderId: 'system',
        text: '🚫 Meeting invite was cancelled by sender',
        timestamp: serverTimestamp()
      });

      await updateDoc(doc(db, 'conversations', activeConversation), {
        lastMessage: 'Meeting invite cancelled',
        lastMessageAt: serverTimestamp()
      });
    } catch (error) {
      debug.error("Error cancelling invite:", error);
    }
  };

  // Open event popup
  const openEventPopup = (booking) => {
    setSelectedEvent(booking);
    setShowEventPopup(true);
  };

  // Navigate to specific date on calendar (deep-linking from invite)
  const navigateToCalendarDate = (dateStr) => {
    const targetDate = new Date(dateStr + 'T12:00:00');
    setCalendarDate(targetDate);
    setCalendarViewMode('weekly');
    setView('calls');
  };

  // Accept invite from calendar
  const acceptInviteFromCalendar = async (invite) => {
    try {
      // Switch to the conversation and set it active
      setActiveConversation(invite.conversationId);
      
      // Accept the invite
      await acceptInvite(invite.id, invite.inviteData);
      
      // Refresh pending invites
      setPendingInvites(prev => prev.filter(i => i.id !== invite.id));
    } catch (error) {
      debug.error("Error accepting invite from calendar:", error);
      setError("Failed to accept invite");
    }
  };

  // Decline invite from calendar
  const declineInviteFromCalendar = async (invite) => {
    try {
      await updateDoc(doc(db, 'conversations', invite.conversationId, 'messages', invite.id), {
        'inviteData.status': 'declined'
      });
      
      await addDoc(collection(db, 'conversations', invite.conversationId, 'messages'), {
        senderId: 'system',
        text: '❌ Meeting invite was declined',
        timestamp: serverTimestamp()
      });
      
      // Refresh pending invites
      setPendingInvites(prev => prev.filter(i => i.id !== invite.id));
    } catch (error) {
      debug.error("Error declining invite from calendar:", error);
    }
  };

  // Format member since date
  const formatMemberSince = (timestamp) => {
    if (!timestamp) return 'Recently';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Get completed calls count
  const getCompletedCallsCount = () => {
    return bookings.filter(b => {
      const myDetails = b.participantDetails?.[userProfile?.id];
      let bookingTime;
      if (myDetails?.localDate && myDetails?.localHour) {
        bookingTime = new Date(`${myDetails.localDate}T${myDetails.localHour}`);
      } else {
        bookingTime = new Date(b.dateTime);
      }
      return bookingTime < currentTime;
    }).length;
  };

  // Get upcoming calls (not yet passed)
  const getUpcomingCalls = () => {
    return bookings.filter(b => {
      const myDetails = b.participantDetails?.[userProfile?.id];
      let bookingTime;
      if (myDetails?.localDate && myDetails?.localHour) {
        bookingTime = new Date(`${myDetails.localDate}T${myDetails.localHour}`);
      } else {
        bookingTime = new Date(b.dateTime);
      }
      return bookingTime >= currentTime;
    });
  };

  // Get completed calls
  const getCompletedCalls = () => {
    return bookings.filter(b => {
      const myDetails = b.participantDetails?.[userProfile?.id];
      let bookingTime;
      if (myDetails?.localDate && myDetails?.localHour) {
        bookingTime = new Date(`${myDetails.localDate}T${myDetails.localHour}`);
      } else {
        bookingTime = new Date(b.dateTime);
      }
      return bookingTime < currentTime;
    });
  };

  // Create notification
  const createNotification = async (userId, type, title, message, data = {}) => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId,
        type, // 'invite', 'match', 'message', 'booking'
        title,
        message,
        data,
        read: false,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      debug.error("Error creating notification:", error);
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notifId) => {
    try {
      await updateDoc(doc(db, 'notifications', notifId), { read: true });
    } catch (error) {
      debug.error("Error marking notification read:", error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    try {
      const unreadNotifs = notifications.filter(n => !n.read);
      for (const notif of unreadNotifs) {
        await updateDoc(doc(db, 'notifications', notif.id), { read: true });
      }
    } catch (error) {
      debug.error("Error marking all notifications read:", error);
    }
  };

  // Clear a notification
  const clearNotification = async (notifId) => {
    try {
      await deleteDoc(doc(db, 'notifications', notifId));
    } catch (error) {
      debug.error("Error clearing notification:", error);
    }
  };

  // Format notification time
  const formatNotificationTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Get current time in user's timezone
  const getUserLocalTime = () => {
    const tz = userProfile?.timezone || 'America/New_York';
    try {
      return new Date().toLocaleString('en-US', { timeZone: tz });
    } catch {
      return new Date().toLocaleString('en-US');
    }
  };

  // Format time for user's timezone
  const formatTimeForUser = (dateInput) => {
    const tz = userProfile?.timezone || 'America/New_York';
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    try {
      return date.toLocaleTimeString('en-US', { 
        timeZone: tz, 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }
  };

  // Format date for user's timezone
  const formatDateForUser = (dateInput, options = {}) => {
    const tz = userProfile?.timezone || 'America/New_York';
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    const defaultOptions = { 
      timeZone: tz, 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    };
    try {
      return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
    } catch {
      return date.toLocaleDateString('en-US', options);
    }
  };

  // Roleplay Now - Send blast to all online users
  const sendRoleplayNow = async () => {
    if (isRoleplayNowActive) return;
    
    // Check 1-hour focus buffer
    if (roleplayNowBlocked) {
      setToastMessage(`⏰ ${roleplayBlockReason}. Please prepare for that session instead.`);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
      return;
    }
    
    try {
      setIsRoleplayNowActive(true);
      setSuccess('');
      
      // Create roleplay now request
      const requestRef = await addDoc(collection(db, 'roleplayNow'), {
        senderId: userProfile.id,
        senderName: userProfile.name,
        senderAvatar: userProfile.avatar,
        senderTimezone: userProfile.timezone,
        communityId: userProfile.communityId,
        status: 'pending',
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 5 * 60 * 1000)
      });

      // Store the request ID so we can cancel it
      setActiveRoleplayRequestId(requestRef.id);
      debug.log("Roleplay Now request created:", requestRef.id);

      // Send notification to all other STUDENTS (exclude admins)
      const otherStudents = users.filter(u => u.id !== userProfile.id && !u.isAdmin);
      
      for (const otherUser of otherStudents) {
        // Skip users in DND mode
        if (otherUser.dndMode) continue;
        
        try {
          await addDoc(collection(db, 'notifications'), {
            userId: otherUser.id,
            type: 'roleplay_now',
            title: 'Roleplay Now!',
            message: `${userProfile.name} wants to roleplay right now!`,
            data: { requestId: requestRef.id, senderId: userProfile.id },
            read: false,
            createdAt: serverTimestamp()
          });
        } catch (err) {
          debug.error("Failed to send notification to", otherUser.name, err);
        }
      }

      // Auto-expire after 5 minutes
      setTimeout(async () => {
        try {
          const requestDoc = await getDoc(doc(db, 'roleplayNow', requestRef.id));
          if (requestDoc.exists() && requestDoc.data().status === 'pending') {
            await updateDoc(doc(db, 'roleplayNow', requestRef.id), { status: 'expired' });
          }
        } catch (err) {
          debug.error("Error expiring request:", err);
        }
        setIsRoleplayNowActive(false);
        setActiveRoleplayRequestId(null);
        setSuccess('');
      }, 5 * 60 * 1000);

    } catch (error) {
      debug.error("Error sending roleplay now:", error);
      setError("Failed to send roleplay request. Please try again.");
      setIsRoleplayNowActive(false);
      setActiveRoleplayRequestId(null);
    }
  };

  // Cancel Roleplay Now request
  const cancelRoleplayNow = async () => {
    if (!activeRoleplayRequestId) return;
    
    try {
      await updateDoc(doc(db, 'roleplayNow', activeRoleplayRequestId), { 
        status: 'cancelled' 
      });
      setIsRoleplayNowActive(false);
      setActiveRoleplayRequestId(null);
      setSuccess('');
    } catch (error) {
      debug.error("Error cancelling roleplay request:", error);
    }
  };

  // Accept Roleplay Now request
  const acceptRoleplayNow = async (request) => {
    try {
      const requestRef = doc(db, 'roleplayNow', request.id);
      const now = new Date();
      
      debug.log('=== ACCEPTING ROLEPLAY NOW ===');
      debug.log('Request ID:', request.id);
      debug.log('Sender:', request.senderName);
      
      // Show prep overlay immediately
      setPrepPartner({
        name: request.senderName,
        avatar: request.senderAvatar
      });
      setPrepCountdown(3);
      setShowPrepOverlay(true);
      setShowRoleplayNowModal(false);
      setPendingRoleplayRequest(null);
      
      // Use transaction to ensure atomic acceptance (prevents race conditions)
      const result = await runTransaction(db, async (transaction) => {
        const requestDoc = await transaction.get(requestRef);
        
        if (!requestDoc.exists()) {
          throw new Error('Request no longer exists');
        }
        
        const requestData = requestDoc.data();
        
        if (requestData.status !== 'pending') {
          // Someone else already accepted
          throw new Error('ALREADY_ACCEPTED');
        }
        
        // Mark as accepted atomically - use Date instead of serverTimestamp in transaction
        transaction.update(requestRef, {
          status: 'accepted',
          acceptedBy: userProfile.id,
          acceptedAt: now
        });
        
        return { success: true };
      });
      
      if (!result.success) {
        setShowPrepOverlay(false);
        return;
      }

      debug.log('Transaction successful, creating Zoom meeting...');
      
      // Create Zoom meeting (this takes time - overlay is showing)
      const meetingTopic = `MeetSync Roleplay: ${request.senderName} & ${userProfile.name}`;
      
      let zoomMeeting;
      try {
        zoomMeeting = await createZoomMeeting(meetingTopic);
        debug.log('✅ Zoom meeting created:', zoomMeeting.joinUrl);
      } catch (zoomError) {
        debug.error('❌ Zoom meeting creation failed:', zoomError);
        setShowPrepOverlay(false);
        setError('Failed to create video call. Please try again. Error: ' + zoomError.message);
        return;
      }
      
      // Store zoom link for the overlay
      setPrepZoomLink(zoomMeeting.joinUrl);
      
      // IMPORTANT: Update the roleplayNow document with the meeting link
      // This allows User A (sender) to receive the link via their listener
      await updateDoc(doc(db, 'roleplayNow', request.id), {
        meetingLink: zoomMeeting.joinUrl
      });
      
      debug.log('Updated roleplayNow with meeting link');

      // Create or find conversation
      let convId;
      const existingConv = conversations.find(c => c.participants.includes(request.senderId));
      
      if (existingConv) {
        convId = existingConv.id;
        await updateDoc(doc(db, 'conversations', convId), {
          meetingLink: zoomMeeting.joinUrl,
          lastMessage: 'Roleplay Now session started!',
          lastMessageAt: serverTimestamp()
        });
      } else {
        const convRef = await addDoc(collection(db, 'conversations'), {
          participants: [userProfile.id, request.senderId],
          participantDetails: {
            [userProfile.id]: {
              name: userProfile.name,
              avatar: userProfile.avatar,
              timezone: userProfile.timezone
            },
            [request.senderId]: {
              name: request.senderName,
              avatar: request.senderAvatar,
              timezone: request.senderTimezone || 'America/New_York'
            }
          },
          communityId: userProfile.communityId,
          meetingLink: zoomMeeting.joinUrl,
          createdAt: serverTimestamp(),
          lastMessage: 'Roleplay Now session started!'
        });
        convId = convRef.id;
      }

      // Create booking for Roleplay Now (so it shows in calls and triggers completion)
      await addDoc(collection(db, 'bookings'), {
        participants: [userProfile.id, request.senderId],
        participantNames: {
          [userProfile.id]: userProfile.name,
          [request.senderId]: request.senderName
        },
        participantAvatars: {
          [userProfile.id]: userProfile.avatar,
          [request.senderId]: request.senderAvatar
        },
        date: now.toISOString().split('T')[0],
        dateTime: now.toISOString(),
        type: 'instant', // Mark as instant roleplay (no feedback needed)
        isRoleplayNow: true,
        acceptedAt: serverTimestamp(),
        status: 'in-progress',
        meetingLink: zoomMeeting.joinUrl,
        conversationId: convId,
        communityId: userProfile.communityId,
        createdAt: serverTimestamp()
      });

      // Add system messages
      await addDoc(collection(db, 'conversations', convId, 'messages'), {
        senderId: 'system',
        text: 'Roleplay Now session started!',
        timestamp: serverTimestamp()
      });

      await addDoc(collection(db, 'conversations', convId, 'messages'), {
        senderId: 'system',
        text: 'Join your Zoom call:',
        meetingLink: zoomMeeting.joinUrl,
        timestamp: serverTimestamp()
      });

      // Notify the sender - use subcollection under user
      await addDoc(collection(db, 'users', request.senderId, 'notifications'), {
        type: 'roleplay_accepted',
        title: 'Match Found!',
        message: `${userProfile.name} accepted your Roleplay Now request!`,
        data: { conversationId: convId },
        read: false,
        createdAt: serverTimestamp()
      });

      // The prep overlay will handle navigation
      setActiveConversation(convId);

    } catch (error) {
      setShowPrepOverlay(false);
      if (error.message === 'ALREADY_ACCEPTED') {
        // Someone else got it first - silently close
        setToastMessage('Someone else already accepted this request');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
      } else {
        debug.error("Error accepting roleplay now:", error);
        setError("Failed to accept. Please try again.");
      }
    }
  };

  // Decline Roleplay Now request
  const declineRoleplayNow = () => {
    setShowRoleplayNowModal(false);
    setPendingRoleplayRequest(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading MeetSync...</p>
      </div>
    );
  }

  // Landing Page
  if (showLanding && !user) {
    return (
      <div className="landing-page-2026">
        {/* Contact Sales Modal */}
        {showContactSales && (
          <div className="modal-overlay" onClick={() => setShowContactSales(false)}>
            <div className="modal contact-sales-modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowContactSales(false)}>×</button>
              <div className="modal-header">
                <h2>🏢 Contact Enterprise Sales</h2>
                <p>Tell us about your needs and our team will reach out within 24 hours.</p>
              </div>
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              
              {!success && (
                <form onSubmit={handleContactSalesSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input 
                        type="text" 
                        value={contactForm.firstName}
                        onChange={e => setContactForm({...contactForm, firstName: e.target.value})}
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input 
                        type="text" 
                        value={contactForm.lastName}
                        onChange={e => setContactForm({...contactForm, lastName: e.target.value})}
                        required 
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Work Email *</label>
                    <input 
                      type="email" 
                      value={contactForm.workEmail}
                      onChange={e => setContactForm({...contactForm, workEmail: e.target.value})}
                      placeholder="you@company.com"
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>Company Name *</label>
                    <input 
                      type="text" 
                      value={contactForm.companyName}
                      onChange={e => setContactForm({...contactForm, companyName: e.target.value})}
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input 
                      type="tel" 
                      value={contactForm.phoneNumber}
                      onChange={e => setContactForm({...contactForm, phoneNumber: e.target.value})}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div className="form-group">
                    <label>Specific Needs</label>
                    <textarea 
                      value={contactForm.specificNeeds}
                      onChange={e => setContactForm({...contactForm, specificNeeds: e.target.value})}
                      placeholder="Tell us about your team size, use case, and any specific requirements..."
                      rows={4}
                    />
                  </div>
                  <button type="submit" className="btn-primary">Send Request</button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Cookie Consent Banner */}
        {showCookieConsent && (
          <div className="cookie-consent-banner">
            <div className="cookie-content">
              <div className="cookie-text">
                <Cookie size={20} />
                <p>We use cookies to enhance your experience. Essential cookies are required for the app to function. You can choose to enable analytics and marketing cookies.</p>
              </div>
              <div className="cookie-actions">
                <button className="btn-cookie-settings" onClick={() => setShowCookieSettings(true)}>
                  Manage Preferences
                </button>
                <button className="btn-cookie-essential" onClick={acceptEssentialOnly}>
                  Essential Only
                </button>
                <button className="btn-cookie-accept" onClick={acceptAllCookies}>
                  Accept All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cookie Settings Modal */}
        {showCookieSettings && (
          <div className="modal-overlay" onClick={() => setShowCookieSettings(false)}>
            <div className="modal cookie-settings-modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowCookieSettings(false)}>×</button>
              <div className="modal-header">
                <h2><Cookie size={24} /> Cookie Preferences</h2>
                <p>Manage how we use cookies on this site</p>
              </div>
              
              <div className="cookie-settings-list">
                <div className="cookie-setting-item">
                  <div className="cookie-setting-info">
                    <h4>Essential Cookies</h4>
                    <p>Required for the app to function. These handle login sessions, security, and core features.</p>
                  </div>
                  <div className="cookie-toggle disabled">
                    <span className="toggle-always-on">Always On</span>
                  </div>
                </div>
                
                <div className="cookie-setting-item">
                  <div className="cookie-setting-info">
                    <h4>Analytics Cookies</h4>
                    <p>Help us understand how you use MeetSync so we can improve the experience.</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={cookiePreferences.analytics}
                      onChange={e => setCookiePreferences({...cookiePreferences, analytics: e.target.checked})}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                
                <div className="cookie-setting-item">
                  <div className="cookie-setting-info">
                    <h4>Marketing Cookies</h4>
                    <p>Used to show you relevant ads and measure campaign effectiveness.</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={cookiePreferences.marketing}
                      onChange={e => setCookiePreferences({...cookiePreferences, marketing: e.target.checked})}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
              
              <div className="cookie-settings-actions">
                <button className="btn-secondary" onClick={() => setShowCookieSettings(false)}>Cancel</button>
                <button className="btn-primary" onClick={saveCookiePreferences}>Save Preferences</button>
              </div>
            </div>
          </div>
        )}

        {/* Privacy Policy Modal */}
        {showPrivacyPolicy && (
          <div className="modal-overlay" onClick={() => setShowPrivacyPolicy(false)}>
            <div className="modal legal-modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowPrivacyPolicy(false)}>×</button>
              <div className="legal-content">
                <h1>Privacy Policy</h1>
                <p className="legal-updated">Last updated: January 2026</p>
                
                <h2>1. Information We Collect</h2>
                <p>MeetSync collects information you provide directly, including:</p>
                <ul>
                  <li><strong>Account Information:</strong> Name, email address, and password when you create an account</li>
                  <li><strong>Profile Information:</strong> Avatar, timezone, bio, and preferences</li>
                  <li><strong>Usage Data:</strong> Roleplay sessions, scheduling data, and feature interactions</li>
                  <li><strong>Communications:</strong> Messages sent through the platform</li>
                </ul>
                
                <h2>2. How We Use Your Information</h2>
                <p>We use collected information to:</p>
                <ul>
                  <li>Provide and improve the MeetSync service</li>
                  <li>Match you with roleplay partners based on availability</li>
                  <li>Send notifications about sessions and platform updates</li>
                  <li>Generate AI coaching tips based on your reflections</li>
                  <li>Analyze usage patterns to improve features</li>
                </ul>
                
                <h2>3. Session Data & Recordings</h2>
                <p>MeetSync integrates with Zoom for video sessions. We do not record or store video/audio from your roleplay sessions. Zoom's own privacy policy governs any recordings made through their platform.</p>
                <p>We do store:</p>
                <ul>
                  <li>Session metadata (date, time, duration, participants)</li>
                  <li>Your personal reflections and notes (private to you)</li>
                  <li>Anonymized questions submitted to instructors</li>
                </ul>
                
                <h2>4. Data Security</h2>
                <p>We implement industry-standard security measures including:</p>
                <ul>
                  <li>256-bit SSL encryption for all data transmission</li>
                  <li>Secure payment processing through Stripe (PCI compliant)</li>
                  <li>Firebase security rules protecting user data</li>
                  <li>Regular security audits and updates</li>
                </ul>
                
                <h2>5. Third-Party Services</h2>
                <p>We use the following third-party services:</p>
                <ul>
                  <li><strong>Firebase:</strong> Authentication and database</li>
                  <li><strong>Stripe:</strong> Payment processing</li>
                  <li><strong>Zoom:</strong> Video conferencing</li>
                  <li><strong>OpenAI:</strong> AI coaching tips (anonymized data only)</li>
                </ul>
                
                <h2>6. Your Rights</h2>
                <p>You have the right to:</p>
                <ul>
                  <li>Access your personal data</li>
                  <li>Correct inaccurate data</li>
                  <li>Delete your account and associated data</li>
                  <li>Export your data</li>
                  <li>Opt-out of marketing communications</li>
                </ul>
                
                <h2>7. Contact Us</h2>
                <p>For privacy-related questions, contact us at privacy@meetsync.com</p>
              </div>
            </div>
          </div>
        )}

        {/* Terms of Service Modal */}
        {showTermsOfService && (
          <div className="modal-overlay" onClick={() => setShowTermsOfService(false)}>
            <div className="modal legal-modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowTermsOfService(false)}>×</button>
              <div className="legal-content">
                <h1>Terms of Service</h1>
                <p className="legal-updated">Last updated: January 2026</p>
                
                <h2>1. Acceptance of Terms</h2>
                <p>By accessing or using MeetSync, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree, please do not use our service.</p>
                
                <h2>2. Description of Service</h2>
                <p>MeetSync is a sales roleplay practice platform that enables teams to:</p>
                <ul>
                  <li>Schedule and conduct roleplay sessions</li>
                  <li>Match with partners based on availability</li>
                  <li>Track progress and receive AI-powered coaching</li>
                  <li>Integrate with Zoom for video sessions</li>
                </ul>
                
                <h2>3. User Accounts</h2>
                <p>You are responsible for:</p>
                <ul>
                  <li>Maintaining the confidentiality of your account</li>
                  <li>All activities that occur under your account</li>
                  <li>Notifying us immediately of unauthorized use</li>
                </ul>
                
                <h2>4. Acceptable Use</h2>
                <p>You agree NOT to:</p>
                <ul>
                  <li>Use the service for any unlawful purpose</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Share inappropriate or offensive content</li>
                  <li>Attempt to gain unauthorized access to systems</li>
                  <li>Reverse engineer or copy the platform</li>
                </ul>
                
                <h2>5. Subscription & Billing</h2>
                <p>Paid subscriptions:</p>
                <ul>
                  <li>Are billed monthly in advance</li>
                  <li>Auto-renew until cancelled</li>
                  <li>Can be cancelled at any time (access continues until end of billing period)</li>
                </ul>
                <p>For information about refunds, please see our <button onClick={() => { setShowTermsOfService(false); setShowRefundPolicy(true); }} style={{ background: 'none', border: 'none', color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>Refund Policy</button>.</p>
                
                <h2>6. Intellectual Property</h2>
                <p>MeetSync and its content, features, and functionality are owned by MeetSync and protected by copyright, trademark, and other intellectual property laws.</p>
                
                <h2>7. Limitation of Liability</h2>
                <p>MeetSync is provided "as is" without warranties of any kind. We are not liable for any indirect, incidental, special, or consequential damages arising from your use of the service.</p>
                
                <h2>8. Termination</h2>
                <p>We may terminate or suspend your account immediately, without prior notice, for conduct that we determine violates these Terms or is harmful to other users or the service.</p>
                
                <h2>9. Changes to Terms</h2>
                <p>We reserve the right to modify these terms at any time. We will notify users of significant changes via email or in-app notification.</p>
                
                <h2>10. Contact</h2>
                <p>For questions about these Terms, contact us at legal@meetsync.com</p>
              </div>
            </div>
          </div>
        )}

        {/* Refund Policy Modal */}
        {showRefundPolicy && (
          <div className="modal-overlay" onClick={() => setShowRefundPolicy(false)}>
            <div className="modal legal-modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowRefundPolicy(false)}>×</button>
              <div className="legal-content">
                <h1>Refund Policy</h1>
                <p className="legal-updated">Last updated: January 2026</p>
                
                <h2>Our Commitment to Your Satisfaction</h2>
                <p>At MeetSync, we want you to be completely satisfied with your subscription. We understand that sometimes a product may not be the right fit, which is why we offer a straightforward refund policy.</p>
                
                <h2>7-Day Refund Window</h2>
                <p>You may request a full refund within <strong>7 days</strong> of your initial subscription purchase. This gives you enough time to explore MeetSync's features and determine if it meets your team's needs.</p>
                
                <h2>Eligibility Requirements</h2>
                <p>To be eligible for a refund, you must:</p>
                <ul>
                  <li>Request the refund within 7 days of your original purchase date</li>
                  <li>Be on your first subscription period (refunds do not apply to renewals)</li>
                  <li>Submit your request through our official support channels</li>
                </ul>
                
                <h2>How to Request a Refund</h2>
                <p>To request a refund, please:</p>
                <ul>
                  <li>Email us at <strong>billing@meetsync.com</strong> with subject line "Refund Request"</li>
                  <li>Include your account email and the reason for your request</li>
                  <li>Our team will process your request within 3-5 business days</li>
                </ul>
                
                <h2>After the 7-Day Window</h2>
                <p>After the initial 7-day period:</p>
                <ul>
                  <li>Subscriptions are non-refundable for partial months</li>
                  <li>You may cancel your subscription at any time to prevent future charges</li>
                  <li>Access continues until the end of your current billing period</li>
                </ul>
                
                <h2>Processing Time</h2>
                <p>Approved refunds are processed within 5-10 business days. The refund will be credited to the original payment method. Please note that your bank may take additional time to reflect the refund in your account.</p>
                
                <h2>Plan Changes</h2>
                <p>If you'd like to downgrade or change your plan instead of canceling, please contact our support team. We're happy to help find a solution that works for you.</p>
                
                <h2>Contact Us</h2>
                <p>If you have any questions about our refund policy, please don't hesitate to reach out:</p>
                <ul>
                  <li>Email: billing@meetsync.com</li>
                  <li>Response time: Within 24 hours on business days</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Sticky Navigation */}
        <header className="landing-header-sticky">
          <div className="header-container">
            <div className="landing-logo" onClick={() => setLandingView('home')} style={{cursor: 'pointer'}}>
              <Calendar size={24} className="logo-icon-svg" />
              <span className="logo-text">MeetSync</span>
            </div>
            <nav className="landing-nav-desktop">
              <button className="nav-link" onClick={() => setLandingView('home')}>Home</button>
              <button className="nav-link" onClick={() => { setLandingView('home'); setTimeout(() => document.getElementById('features')?.scrollIntoView({behavior: 'smooth'}), 100); }}>Features</button>
              <button className="nav-link" onClick={() => { setLandingView('pricing'); window.scrollTo(0, 0); }}>Pricing</button>
            </nav>
            <div className="landing-nav-actions">
              <button className="btn-nav-ghost" onClick={() => { setShowLanding(false); setAuthMode('login'); }}>
                Log In
              </button>
              <button className="btn-nav-primary" onClick={() => { setLandingView('pricing'); window.scrollTo(0, 0); }}>
                Get Started
              </button>
            </div>
            {/* Mobile menu button */}
            <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="mobile-menu">
              <button onClick={() => { setLandingView('home'); setMobileMenuOpen(false); }}>Home</button>
              <button onClick={() => { setLandingView('home'); setMobileMenuOpen(false); setTimeout(() => document.getElementById('features')?.scrollIntoView({behavior: 'smooth'}), 100); }}>Features</button>
              <button onClick={() => { setLandingView('pricing'); setMobileMenuOpen(false); window.scrollTo(0, 0); }}>Pricing</button>
              <button onClick={() => { setShowLanding(false); setAuthMode('login'); setMobileMenuOpen(false); }}>Log In</button>
              <button className="btn-mobile-primary" onClick={() => { setLandingView('pricing'); setMobileMenuOpen(false); window.scrollTo(0, 0); }}>Get Started</button>
            </div>
          )}
        </header>

        {landingView === 'home' && (
          <>
            {/* Hero Section - 10 Second Value Prop */}
            <section className="hero-2026">
              <div className="hero-content-2026">
                {/* Live Activity Notification */}
                <div className="live-activity-badge">
                  <span className="pulse-dot"></span>
                  <span>{liveStats.currentlyRoleplaying} reps currently roleplaying</span>
                </div>
                
                <h1 className="hero-headline">
                  Turn your sales reps into <span className="gradient-text-2026">top closers</span> with 24/7 on-demand roleplay
                </h1>
                <p className="hero-subheadline">
                  The practice platform that matches your team instantly. No scheduling headaches. Real improvement.
                </p>
                
                <div className="hero-cta-group">
                  <button className="btn-hero-primary-2026" onClick={() => { setLandingView('pricing'); window.scrollTo(0, 0); }}>
                    Create Your Community
                    <span className="btn-arrow">→</span>
                  </button>
                  <button className="btn-hero-secondary-2026" onClick={() => { setShowLanding(false); setAuthMode('login'); }}>
                    Join Existing Team
                  </button>
                </div>
                
                <p className="hero-microcopy">Setup in 2 minutes • Invite your team instantly</p>
              </div>
              
              {/* Immersive Product Preview */}
              <div className="hero-preview-container">
                <div className="hero-preview-frame">
                  <div className="preview-header-bar">
                    <div className="preview-dots">
                      <span></span><span></span><span></span>
                    </div>
                    <span className="preview-title">MeetSync Dashboard</span>
                  </div>
                  <div className="preview-content">
                    {/* Animated Matching Demo */}
                    <div className="preview-matching-demo">
                      <div className="matching-animation">
                        <div className="user-card left">
                          <img src="/images/person1.png" alt="You" className="user-photo-anim" />
                          <span>You</span>
                        </div>
                        <div className="matching-pulse">
                          <div className="pulse-ring"></div>
                          <div className="pulse-ring delay"></div>
                          <span className="match-icon">⚡</span>
                        </div>
                        <div className="user-card right">
                          <img src="/images/person2.png" alt="Partner" className="user-photo-anim" />
                          <span>Partner</span>
                        </div>
                      </div>
                      <div className="matching-status">
                        <span className="status-text">Finding your perfect roleplay partner...</span>
                        <div className="status-bar">
                          <div className="status-fill"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Stats Cards */}
                <div className="floating-stat-card top-right">
                  <span className="floating-icon">🎯</span>
                  <div className="floating-content">
                    <span className="floating-number">{liveStats.matchesThisMonth.toLocaleString()}+</span>
                    <span className="floating-label">matches this month</span>
                  </div>
                </div>
                <div className="floating-stat-card bottom-left">
                  <span className="floating-icon">⚡</span>
                  <div className="floating-content">
                    <span className="floating-number">&lt;30s</span>
                    <span className="floating-label">avg match time</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Social Proof - Logo Marquee */}
            <section className="social-proof-section">
              <p className="social-proof-label">Trusted by sales teams from</p>
              <div className="logo-marquee-container">
                <div className="logo-marquee">
                  <div className="logo-marquee-content">
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="18" fontWeight="bold">zoom</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="14" fontWeight="bold">LinkedIn</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="12" fontWeight="bold">Salesforce</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="14" fontWeight="bold">HubSpot</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="16" fontWeight="bold">Gong</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="14" fontWeight="bold">Outreach</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="14" fontWeight="bold">Clari</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="14" fontWeight="bold">ZoomInfo</text>
                      </svg>
                    </div>
                  </div>
                  {/* Duplicate for seamless loop */}
                  <div className="logo-marquee-content">
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="18" fontWeight="bold">zoom</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="14" fontWeight="bold">LinkedIn</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="12" fontWeight="bold">Salesforce</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="14" fontWeight="bold">HubSpot</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="16" fontWeight="bold">Gong</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="14" fontWeight="bold">Outreach</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="14" fontWeight="bold">Clari</text>
                      </svg>
                    </div>
                    <div className="company-logo">
                      <svg viewBox="0 0 100 30" className="logo-svg">
                        <text x="50" y="22" textAnchor="middle" fill="#6b7280" fontSize="14" fontWeight="bold">ZoomInfo</text>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Bento Grid Features */}
            <section id="features" className="bento-section">
              <div className="section-header">
                <span className="section-badge">Features</span>
                <h2>Everything you need to <span className="gradient-text-2026">close more deals</span></h2>
                <p>Built by sales leaders, for sales teams that want to win.</p>
              </div>
              
              <div className="bento-grid">
                {/* The Matcher - Large Card */}
                <div className="bento-card bento-large glass-card">
                  <div className="bento-icon-badge">⚡</div>
                  <h3>The Matcher</h3>
                  <p>Get paired with a practice partner in under 30 seconds. Our smart algorithm considers timezone, skill level, and availability.</p>
                  <div className="bento-visual">
                    <div className="mini-demo-matcher">
                      <div className="mini-user">🧑‍💼</div>
                      <div className="mini-connection">
                        <span className="mini-line"></span>
                        <span className="mini-bolt">⚡</span>
                        <span className="mini-line"></span>
                      </div>
                      <div className="mini-user">👩‍💼</div>
                    </div>
                  </div>
                </div>
                
                {/* The Dashboard - Medium Card */}
                <div className="bento-card bento-medium glass-card">
                  <div className="bento-icon-badge">📊</div>
                  <h3>The Dashboard</h3>
                  <p>Track weekly roleplay goals. See who's crushing it and who needs coaching.</p>
                  <div className="bento-mini-chart">
                    <div className="mini-bar" style={{height: '60%'}}></div>
                    <div className="mini-bar" style={{height: '80%'}}></div>
                    <div className="mini-bar" style={{height: '45%'}}></div>
                    <div className="mini-bar active" style={{height: '95%'}}></div>
                    <div className="mini-bar" style={{height: '70%'}}></div>
                  </div>
                </div>
                
                {/* The Feedback - Medium Card */}
                <div className="bento-card bento-medium glass-card">
                  <div className="bento-icon-badge">🤖</div>
                  <h3>AI Coach</h3>
                  <p>Get instant AI-powered feedback after every call. 3 actionable tips to improve your next pitch.</p>
                  <div className="bento-ai-preview">
                    <div className="ai-tip-mini">💡 Mirror the prospect's language</div>
                    <div className="ai-tip-mini">💡 Ask more discovery questions</div>
                  </div>
                </div>
                
                {/* Zoom Integration - Small Card */}
                <div className="bento-card bento-small glass-card">
                  <div className="bento-icon-badge"><Video size={20} /></div>
                  <h3>Auto Zoom</h3>
                  <p>1-click video calls. No links to share.</p>
                </div>
                
                {/* Private Teams - Small Card */}
                <div className="bento-card bento-small glass-card">
                  <div className="bento-icon-badge"><Lock size={20} /></div>
                  <h3>Private Teams</h3>
                  <p>Your team, your data. Completely isolated.</p>
                </div>
                
                {/* Reflections - Small Card */}
                <div className="bento-card bento-small glass-card">
                  <div className="bento-icon-badge"><BookOpen size={20} /></div>
                  <h3>Reflections</h3>
                  <p>Build your personal improvement journal.</p>
                </div>
              </div>
            </section>

            {/* Testimonial Section - Carousel */}
            <section className="testimonial-section">
              <div className="section-header">
                <h2>Trusted by sales teams worldwide</h2>
                <p>See what leaders are saying about MeetSync</p>
              </div>
              
              <div className="testimonial-carousel">
                <div className="testimonial-track" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                  {testimonials.map((t, idx) => (
                    <div key={idx} className="testimonial-slide">
                      <div className="testimonial-card glass-card">
                        <div className="testimonial-quote">"{t.quote}"</div>
                        <div className="testimonial-author">
                          <img src={t.photo} alt={t.author} className="author-photo" />
                          <div className="author-info">
                            <span className="author-name">{t.author}</span>
                            <span className="author-title">{t.title}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="testimonial-nav">
                  <button 
                    className="testimonial-nav-btn" 
                    onClick={() => setActiveTestimonial(prev => prev === 0 ? testimonials.length - 1 : prev - 1)}
                  >
                    ←
                  </button>
                  <div className="testimonial-dots">
                    {testimonials.map((_, idx) => (
                      <button 
                        key={idx} 
                        className={`testimonial-dot ${idx === activeTestimonial ? 'active' : ''}`}
                        onClick={() => setActiveTestimonial(idx)}
                      />
                    ))}
                  </div>
                  <button 
                    className="testimonial-nav-btn" 
                    onClick={() => setActiveTestimonial(prev => prev === testimonials.length - 1 ? 0 : prev + 1)}
                  >
                    →
                  </button>
                </div>
              </div>
            </section>

            {/* Final CTA Section */}
            <section className="final-cta-section">
              <div className="cta-content">
                <h2>Ready to build a team of closers?</h2>
                <p>Join {liveStats.matchesThisMonth.toLocaleString()}+ sales professionals already sharpening their skills.</p>
                <button className="btn-hero-primary-2026 large" onClick={() => { setLandingView('pricing'); window.scrollTo(0, 0); }}>
                  Create Your Community
                  <span className="btn-arrow">→</span>
                </button>
                <p className="cta-microcopy">Setup in minutes • Invite unlimited teammates</p>
              </div>
            </section>

            {/* Footer */}
            <footer className="footer-2026">
              <div className="footer-container">
                <div className="footer-brand">
                  <div className="footer-logo">
                    <Calendar size={24} className="logo-icon-svg" />
                    <span className="logo-text">MeetSync</span>
                  </div>
                  <p>Turn practice into performance.</p>
                  
                  {/* Security Badges */}
                  <div className="footer-security-badges">
                    <div className="security-badge">
                      <Shield size={16} />
                      <span>256-bit SSL</span>
                    </div>
                    <div className="security-badge">
                      <Lock size={16} />
                      <span>Stripe Secured</span>
                    </div>
                  </div>
                </div>
                <div className="footer-links-grid">
                  <div className="footer-column">
                    <h4>Product</h4>
                    <button onClick={() => document.getElementById('features')?.scrollIntoView({behavior: 'smooth'})}>Features</button>
                    <button onClick={() => { setLandingView('pricing'); window.scrollTo(0, 0); }}>Pricing</button>
                  </div>
                  <div className="footer-column">
                    <h4>Company</h4>
                    <button onClick={() => setShowContactSales(true)}>Contact Sales</button>
                  </div>
                  <div className="footer-column">
                    <h4>Legal</h4>
                    <button onClick={() => setShowPrivacyPolicy(true)}>Privacy Policy</button>
                    <button onClick={() => setShowTermsOfService(true)}>Terms of Service</button>
                    <button onClick={() => setShowRefundPolicy(true)}>Refund Policy</button>
                    <button onClick={() => setShowCookieSettings(true)}>Cookie Settings</button>
                  </div>
                </div>
              </div>
              <div className="footer-bottom">
                <p>© 2026 MeetSync. All rights reserved.</p>
                
                {/* Currency Selector */}
                <div className="currency-selector">
                  <Globe size={16} />
                  <select 
                    value={currency} 
                    onChange={(e) => updateCurrency(e.target.value)}
                    className="currency-select"
                  >
                    {Object.entries(REGIONAL_PRICING).map(([code, info]) => (
                      <option key={code} value={code}>
                        {info.symbol} {code} - {info.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="footer-bottom-links">
                  <button onClick={() => setShowPrivacyPolicy(true)}>Privacy</button>
                  <span>•</span>
                  <button onClick={() => setShowTermsOfService(true)}>Terms</button>
                  <span>•</span>
                  <button onClick={() => setShowCookieSettings(true)}>Cookies</button>
                </div>
              </div>
            </footer>
          </>
        )}

        {/* Pricing Page - Rule of Three */}
        {landingView === 'pricing' && (
          <div className="pricing-page-2026">
            <div className="pricing-header">
              <span className="section-badge">Pricing</span>
              <h1>Simple pricing for <span className="gradient-text-2026">every team</span></h1>
              <p>Start free, scale as you grow. No hidden fees.</p>
              
              {/* Currency Indicator */}
              <div className="pricing-currency-indicator">
                <Globe size={14} />
                <span>Prices shown in {getCurrentPricing().name} ({getCurrentPricing().code})</span>
                <button 
                  className="change-currency-btn"
                  onClick={() => document.querySelector('.currency-select')?.focus()}
                >
                  Change
                </button>
              </div>
            </div>
            
            <div className="pricing-grid-three">
              {/* Starter Plan */}
              <div className={`pricing-card-2026 glass-card ${selectedPlan === 'starter' ? 'selected' : ''}`}>
                <div className="pricing-card-top">
                  <span className="plan-badge">Starter</span>
                  <h3>For small teams</h3>
                  <div className="price-display">
                    <span className="price-currency">{getCurrentPricing().symbol}</span>
                    <span className="price-amount">{getCurrentPricing().starter.toLocaleString()}</span>
                    <span className="price-period">/month</span>
                  </div>
                  <p className="price-description">Up to 10 members</p>
                </div>
                <ul className="pricing-features-list">
                  <li><Check size={16} className="feature-check-icon" /> All core features</li>
                  <li><Check size={16} className="feature-check-icon" /> Zoom integration</li>
                  <li><Check size={16} className="feature-check-icon" /> Basic analytics</li>
                  <li><Check size={16} className="feature-check-icon" /> Email support</li>
                  <li><Check size={16} className="feature-check-icon" /> AI coaching tips</li>
                </ul>
                <button 
                  className={`btn-pricing-2026 ${selectedPlan === 'starter' ? 'selected' : ''}`} 
                  onClick={() => { setSelectedPlan('starter'); setLandingView('checkout'); window.scrollTo(0, 0); }}
                >
                  Get Started
                </button>
              </div>
              
              {/* Pro Plan - Most Popular */}
              <div className={`pricing-card-2026 glass-card featured ${selectedPlan === 'pro' ? 'selected' : ''}`}>
                <div className="popular-badge">Most Popular</div>
                <div className="pricing-card-top">
                  <span className="plan-badge pro">Pro</span>
                  <h3>For growing teams</h3>
                  <div className="price-display">
                    <span className="price-currency">{getCurrentPricing().symbol}</span>
                    <span className="price-amount">{getCurrentPricing().pro.toLocaleString()}</span>
                    <span className="price-period">/month</span>
                  </div>
                  <p className="price-description">Up to 50 members</p>
                </div>
                <ul className="pricing-features-list">
                  <li><Check size={16} className="feature-check-icon" /> Everything in Starter</li>
                  <li><Check size={16} className="feature-check-icon" /> Advanced analytics</li>
                  <li><Check size={16} className="feature-check-icon" /> Leaderboards</li>
                  <li><Check size={16} className="feature-check-icon" /> Priority support</li>
                  <li><Check size={16} className="feature-check-icon" /> Weekly reports</li>
                  <li><Check size={16} className="feature-check-icon" /> Custom onboarding</li>
                </ul>
                <button 
                  className={`btn-pricing-2026 featured ${selectedPlan === 'pro' ? 'selected' : ''}`} 
                  onClick={() => { setSelectedPlan('pro'); setLandingView('checkout'); window.scrollTo(0, 0); }}
                >
                  Get Started
                </button>
              </div>
              
              {/* Enterprise Plan */}
              <div className="pricing-card-2026 glass-card">
                <div className="pricing-card-top">
                  <span className="plan-badge enterprise">Enterprise</span>
                  <h3>For teams of 50+</h3>
                  <div className="price-display">
                    <span className="price-amount custom">Custom</span>
                  </div>
                  <p className="price-description">Unlimited members</p>
                </div>
                <ul className="pricing-features-list">
                  <li><Check size={16} className="feature-check-icon" /> Everything in Pro</li>
                  <li><Check size={16} className="feature-check-icon" /> SSO / SAML</li>
                  <li><Check size={16} className="feature-check-icon" /> Custom branding</li>
                  <li><Check size={16} className="feature-check-icon" /> Dedicated CSM</li>
                  <li><Check size={16} className="feature-check-icon" /> API access</li>
                  <li><Check size={16} className="feature-check-icon" /> SLA guarantee</li>
                </ul>
                <button className="btn-pricing-2026 enterprise" onClick={() => setShowContactSales(true)}>
                  Contact Sales
                </button>
              </div>
            </div>
            
            <div className="pricing-faq-2026">
              <h3>Questions? We've got answers.</h3>
              <div className="faq-grid">
                <div className="faq-item glass-card">
                  <h4>Can I switch plans?</h4>
                  <p>Yes, upgrade or downgrade anytime. Changes take effect on your next billing cycle.</p>
                </div>
                <div className="faq-item glass-card">
                  <h4>How quickly can I get started?</h4>
                  <p>Setup takes less than 2 minutes. Create your community, share the code, and start roleplaying immediately.</p>
                </div>
                <div className="faq-item glass-card">
                  <h4>How does billing work?</h4>
                  <p>We accept all major credit cards via Stripe. Enterprise customers can pay by invoice.</p>
                </div>
                <div className="faq-item glass-card">
                  <h4>Is my data secure?</h4>
                  <p>Yes, we use bank-level encryption and are SOC 2 compliant. Your data never leaves our servers.</p>
                </div>
              </div>
            </div>
            
            <button className="back-link" onClick={() => setLandingView('home')}>← Back to home</button>
          </div>
        )}

        {/* Checkout Page (Stripe) */}
        {landingView === 'checkout' && (
          <div className="checkout-page">
            <button className="back-btn" onClick={() => { setLandingView('pricing'); window.scrollTo(0, 0); }}>← Back to Pricing</button>
            
            <div className="checkout-container">
              <div className="checkout-summary">
                <h2>Order Summary</h2>
                {selectedPlan ? (
                  <>
                    <div className="checkout-plan-card">
                      <div className="checkout-plan-badge">{STRIPE_PRODUCTS[selectedPlan].name}</div>
                      <div className="checkout-plan-info">
                        <span className="checkout-plan-name">MeetSync {STRIPE_PRODUCTS[selectedPlan].name} Plan</span>
                        <span className="checkout-plan-members">Up to {STRIPE_PRODUCTS[selectedPlan].members} team members</span>
                      </div>
                    </div>
                    <div className="checkout-item">
                      <span>Monthly subscription</span>
                      <span>{getCurrentPricing().symbol}{(selectedPlan === 'starter' ? getCurrentPricing().starter : getCurrentPricing().pro).toLocaleString()}/mo</span>
                    </div>
                    <div className="checkout-divider"></div>
                    <div className="checkout-total">
                      <span>Total today</span>
                      <span>{getCurrentPricing().symbol}{(selectedPlan === 'starter' ? getCurrentPricing().starter : getCurrentPricing().pro).toLocaleString()} {getCurrentPricing().code}</span>
                    </div>
                  </>
                ) : (
                  <p className="checkout-no-plan">Please select a plan first</p>
                )}
                
                <div className="checkout-features">
                  <h4>What's included:</h4>
                  <ul>
                    <li><Check size={14} /> All core roleplay features</li>
                    <li><Check size={14} /> Auto Zoom meeting creation</li>
                    <li><Check size={14} /> AI coaching after sessions</li>
                    <li><Check size={14} /> Timezone-smart matching</li>
                    <li><Check size={14} /> Cancel anytime</li>
                  </ul>
                </div>
              </div>
              
              <div className="checkout-form">
                <h2>Complete Your Purchase</h2>
                <p className="checkout-secure"><Lock size={16} /> Secured by Stripe</p>
                
                {error && <div className="error-message">{error}</div>}
                
                <div className="checkout-info">
                  <p>You'll be redirected to Stripe's secure checkout to complete your payment.</p>
                  
                  <div className="checkout-trust-badges">
                    <div className="trust-badge">
                      <Shield size={20} />
                      <span>Secure Payment</span>
                    </div>
                    <div className="trust-badge">
                      <RefreshCw size={20} />
                      <span>Cancel Anytime</span>
                    </div>
                  </div>
                </div>
                
                <button 
                  className="btn-checkout" 
                  onClick={handleStripeCheckout}
                  disabled={!selectedPlan || isProcessingPayment}
                >
                  {isProcessingPayment ? (
                    <><Loader size={18} className="spin" /> Processing...</>
                  ) : (
                    <><CreditCard size={18} /> Pay {getCurrentPricing().symbol}{selectedPlan ? (selectedPlan === 'starter' ? getCurrentPricing().starter : getCurrentPricing().pro).toLocaleString() : '0'}</>
                  )}
                </button>
                
                <p className="checkout-note">
                  By subscribing, you agree to our Terms of Service. Your subscription will auto-renew monthly until cancelled.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payment Success Page */}
        {landingView === 'payment-success' && (
          <div className="payment-success-page">
            {/* Animated background particles */}
            <div className="success-particles">
              {[...Array(20)].map((_, i) => (
                <div key={i} className="particle" style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${3 + Math.random() * 2}s`
                }}></div>
              ))}
            </div>
            
            <div className="success-card-pro">
              {/* Animated checkmark */}
              <div className="success-icon-wrapper">
                <svg className="success-checkmark-svg" viewBox="0 0 100 100">
                  <circle className="checkmark-circle" cx="50" cy="50" r="45" fill="none" stroke="#10B981" strokeWidth="6"/>
                  <path className="checkmark-check" d="M30 50 L45 65 L70 35" fill="none" stroke="#10B981" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              
              <h1 className="success-title">Welcome to MeetSync!</h1>
              <p className="success-subtitle">Your payment was successful. You're all set to transform your sales team.</p>
              
              <div className="success-plan-card">
                <div className="plan-icon-badge">
                  {selectedPlan === 'pro' ? <Crown size={20} /> : <Zap size={20} />}
                </div>
                <div className="plan-details">
                  <span className="plan-name">{STRIPE_PRODUCTS[selectedPlan]?.name || 'Pro'} Plan</span>
                  <span className="plan-price">{getCurrentPricing().symbol}{(selectedPlan === 'starter' ? getCurrentPricing().starter : getCurrentPricing().pro).toLocaleString()}/month</span>
                </div>
                <div className="plan-members">
                  <Users size={16} />
                  <span>Up to {selectedPlan === 'starter' ? '10' : '50'} members</span>
                </div>
              </div>
              
              <div className="success-roadmap">
                <h3>Here's what happens next:</h3>
                <div className="roadmap-steps">
                  <div className="roadmap-step">
                    <div className="step-icon">
                      <Building size={20} />
                    </div>
                    <div className="step-line"></div>
                    <div className="step-info">
                      <h4>Create Your Community</h4>
                      <p>Set up your private workspace with a unique join code for your team.</p>
                    </div>
                  </div>
                  <div className="roadmap-step">
                    <div className="step-icon">
                      <Users size={20} />
                    </div>
                    <div className="step-line"></div>
                    <div className="step-info">
                      <h4>Invite Your Team</h4>
                      <p>Share the code with your sales reps. They'll sign up and join instantly.</p>
                    </div>
                  </div>
                  <div className="roadmap-step">
                    <div className="step-icon">
                      <Play size={20} />
                    </div>
                    <div className="step-info">
                      <h4>Start Practicing</h4>
                      <p>Members set availability, get matched, and start roleplaying with Zoom.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="success-email-badge">
                <div className="email-icon-pulse">
                  <Mail size={18} />
                </div>
                <span>We've sent a confirmation email with your receipt and getting started guide.</span>
              </div>
              
              <button 
                className="btn-success-primary"
                onClick={() => setLandingView('create-community')}
              >
                <span>Create Your Community</span>
                <ArrowRight size={18} />
              </button>
              
              <p className="success-support">
                Questions? <button onClick={() => setShowContactSales(true)}>Contact our team</button>
              </p>
            </div>
          </div>
        )}

        {/* Create Community Form (after payment) */}
        {landingView === 'create-community' && (
          <div className="create-community-page">
            <div className="create-community-card">
              <button className="back-btn-minimal" onClick={() => { setLandingView('payment-success'); window.scrollTo(0, 0); }}>
                <ChevronLeft size={18} /> Back
              </button>
              
              <div className="create-header">
                <div className="create-icon">
                  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="4" y="14" width="40" height="30" rx="3" fill="#E0E7FF"/>
                    <rect x="8" y="18" width="14" height="10" rx="2" fill="#6366F1"/>
                    <rect x="26" y="18" width="14" height="10" rx="2" fill="#6366F1"/>
                    <rect x="8" y="32" width="32" height="8" rx="2" fill="#6366F1"/>
                    <path d="M24 4L30 14H18L24 4Z" fill="#6366F1"/>
                    <circle cx="24" cy="8" r="2" fill="#E0E7FF"/>
                  </svg>
                </div>
                <h1>Create Your Community</h1>
                <p>Set up a private space for your class or team</p>
                {hasPaid && (
                  <div className="payment-complete-badge">
                    <CheckCircle size={16} />
                    <span>Payment Complete</span>
                  </div>
                )}
                {selectedPlan && (
                  <div className="plan-limit-info">
                    <Users size={14} />
                    <span>Up to {selectedPlan === 'starter' ? '10' : '50'} team members</span>
                  </div>
                )}
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              {createdCommunityCode ? (
                <div className="community-created-success">
                  <div className="success-glow"></div>
                  <div className="code-display-pro">
                    <span className="code-label">Your Classroom Code</span>
                    <div className="code-box">
                      <span className="code-value">{createdCommunityCode}</span>
                      <button 
                        className="copy-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(createdCommunityCode);
                          setSuccess('Code copied!');
                        }}
                      >
                        <Copy size={18} />
                      </button>
                    </div>
                    <p className="code-hint">Share this code with your team members to join</p>
                  </div>
                  
                  <div className="verify-step-card">
                    <div className="verify-icon-animated">
                      <Mail size={24} />
                    </div>
                    <div className="verify-content">
                      <h3>Verify Your Email</h3>
                      <p>We sent a verification link to <strong>{adminEmail}</strong></p>
                      <p className="verify-subtext">Click the link in your email to access your admin dashboard</p>
                    </div>
                  </div>
                  
                  <button 
                    className="btn-verify-continue"
                    onClick={() => { setShowLanding(false); setAuthMode('verify'); }}
                  >
                    I've Verified My Email
                    <ArrowRight size={18} />
                  </button>
                </div>
              ) : (
                <form onSubmit={handleCreateCommunity} className="create-form">
                  <div className="form-group-pro">
                    <label>
                      <Building size={16} />
                      Community Name
                    </label>
                    <input 
                      type="text" 
                      value={communityName} 
                      onChange={(e) => setCommunityName(e.target.value)} 
                      placeholder="e.g., Acme Sales Team 2026"
                      required 
                    />
                  </div>
                  <div className="form-group-pro">
                    <label>
                      <User size={16} />
                      Your Name (Admin)
                    </label>
                    <input 
                      type="text" 
                      value={adminName} 
                      onChange={(e) => setAdminName(e.target.value)} 
                      placeholder="Your full name"
                      required 
                    />
                  </div>
                  <div className="form-group-pro">
                    <label>
                      <Mail size={16} />
                      Email
                    </label>
                    <input 
                      type="email" 
                      value={adminEmail} 
                      onChange={(e) => setAdminEmail(e.target.value)} 
                      placeholder="admin@company.com"
                      required 
                    />
                  </div>
                  <div className="form-group-pro">
                    <label>
                      <Lock size={16} />
                      Password
                    </label>
                    <input 
                      type="password" 
                      value={adminPassword} 
                      onChange={(e) => setAdminPassword(e.target.value)} 
                      placeholder="••••••••"
                      required 
                      minLength={6}
                    />
                    <span className="input-hint">Minimum 6 characters • Use existing password if you have an account</span>
                  </div>
                  <button type="submit" className="btn-create-community">
                    <Sparkles size={18} />
                    Create Community
                  </button>
                  <p className="existing-account-hint">
                    Already have an account? Use the same email and password to create a new community.
                  </p>
                </form>
              )}
              
              <p className="auth-switch">
                Already have an account? <button type="button" onClick={() => { setShowLanding(false); setAuthMode('login'); }}>Sign in</button>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Auth screens
  if (!user || !user.emailVerified || !userProfile) {
    return (
      <div className={"auth-container"}>
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <button className="back-to-landing" onClick={() => { setShowLanding(true); setError(''); setSuccess(''); }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Home
        </button>
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-icon">{authMode === 'verify' ? <Mail size={32} /> : authMode === 'forgot' ? <Lock size={32} /> : <Calendar size={32} />}</div>
            <h1>{authMode === 'verify' ? 'Verify Email' : authMode === 'forgot' ? 'Reset Password' : authMode === 'signup' ? 'Join MeetSync' : 'Welcome Back'}</h1>
            <p>{authMode === 'verify' ? 'Check your inbox' : authMode === 'forgot' ? 'Enter your email' : authMode === 'signup' ? 'Create your account' : 'Sign in to your account'}</p>
          </div>
          {error && <div className="error-message">{error}</div>}
          {success && authMode === 'verify' && <div className="success-message">{success}</div>}
          
          {authMode === 'verify' ? (
            <div className="verify-section">
              <p>We sent a verification email to <strong>{user?.email}</strong></p>
              <button onClick={handleCheckVerification} className="btn-primary">I've Verified</button>
              <button onClick={handleResendVerification} className="btn-secondary">Resend Email</button>
              <button onClick={handleSignOut} className="btn-link">Use different email</button>
            </div>
          ) : authMode === 'forgot' ? (
            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
              </div>
              <button type="submit" className="btn-primary">Send Reset Link</button>
              <p className="auth-switch">Remember password? <button type="button" onClick={() => setAuthMode('login')}>Sign in</button></p>
            </form>
          ) : (
            <form onSubmit={authMode === 'signup' ? handleSignUp : handleSignIn}>
              {authMode === 'signup' && (
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required />
                </div>
              )}
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} />
              </div>
              {authMode === 'login' && <button type="button" onClick={() => setAuthMode('forgot')} className="forgot-link">Forgot password?</button>}
              <button type="submit" className="btn-primary">{authMode === 'signup' ? 'Create Account' : 'Sign In'}</button>
              
              <div className="auth-divider">
                <span>or</span>
              </div>
              
              <button type="button" className="btn-google" onClick={handleGoogleSignIn}>
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
              
              <p className="auth-switch">
                {authMode === 'signup' ? <>Have an account? <button type="button" onClick={() => { setAuthMode('login'); setError(''); setSuccess(''); }}>Sign in</button></> 
                  : <>No account? <button type="button" onClick={() => { setAuthMode('signup'); setError(''); setSuccess(''); }}>Create one</button></>}
              </p>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Access Code Screen (Community Code)
  if (showAccessCodeScreen) {
    return (
      <div className={"auth-container"}>
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-icon"><GraduationCap size={32} /></div>
            <h1>Join Your Classroom</h1>
            <p>Enter the code provided by your instructor</p>
          </div>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
          <form onSubmit={handleAccessCodeSubmit}>
            <div className="form-group">
              <label>Classroom Code</label>
              <input 
                type="text" 
                value={accessCode} 
                onChange={(e) => setAccessCode(e.target.value)} 
                placeholder="e.g., ABC123xyz" 
                required 
                autoFocus
                className="code-input"
              />
            </div>
            <button type="submit" className="btn-primary">Join Classroom</button>
          </form>
          
          <p className="access-code-note">
            Don't have a code? Ask your instructor for the classroom code.
          </p>
          
          <button onClick={handleSignOut} className="btn-link">Sign out</button>
        </div>
      </div>
    );
  }

  // Main App
  return (
    <div className={`app-container ${sidebarOpen ? 'sidebar-open' : ''}`} onClick={() => showDropdown && setShowDropdown(false)}>
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>

      {/* Left Sidebar - Desktop */}
      <aside className="sidebar">
        <div className="sidebar-logo">
          <span className="logo-icon"><Calendar size={28} /></span>
          <span className="logo-text">MeetSync</span>
        </div>
        
        <nav className="sidebar-nav">
          {[
            { id: 'dashboard', icon: <Home size={20} />, label: 'Dashboard', showFor: 'all' },
            { id: 'calendar', icon: <Calendar size={20} />, label: 'Calendar', showFor: 'student' },
            { id: 'calls', icon: <Phone size={20} />, label: 'Upcoming Calls', showFor: 'student' },
            { id: 'analytics', icon: <BarChart3 size={20} />, label: 'Analytics', showFor: 'student' },
            { id: 'questions', icon: <HelpCircle size={20} />, label: 'Questions', showFor: 'admin' },
            { id: 'contact-requests', icon: <Mail size={20} />, label: 'Inquiries', showFor: 'superadmin' },
            { id: 'messages', icon: <MessageSquare size={20} />, label: 'Messages', showFor: 'all' },
            { id: 'profile', icon: <User size={20} />, label: 'Profile', showFor: 'all' },
          ]
            .filter(item => {
              if (item.showFor === 'all') return true;
              if (item.showFor === 'admin' && userProfile.isAdmin) return true;
              if (item.showFor === 'student' && !userProfile.isAdmin) return true;
              if (item.showFor === 'superadmin' && userProfile.email === 'simonegalvan.it@gmail.com') return true;
              return false;
            })
            .map(item => (
            <button 
              key={item.id} 
              onClick={() => userProfile.hasAccess || item.id === 'dashboard' ? setView(item.id) : setError('Please enter access code first')} 
              className={`sidebar-item ${view === item.id ? 'active' : ''} ${!userProfile.hasAccess && item.id !== 'dashboard' ? 'disabled' : ''}`}
            >
              <span className="sidebar-icon">{item.icon}</span>
              <span className="sidebar-label">{item.label}</span>
              {item.id === 'contact-requests' && contactRequests.filter(r => r.status === 'new').length > 0 && (
                <span className="sidebar-badge">{contactRequests.filter(r => r.status === 'new').length}</span>
              )}
            </button>
          ))}
        </nav>
        
        <div className="sidebar-footer">
          {userProfile.isAdmin && (
            <button onClick={openBillingPortal} className="sidebar-item billing" title="Manage Subscription">
              <span className="sidebar-icon"><CreditCard size={20} /></span>
              <span className="sidebar-label">Billing</span>
            </button>
          )}
          <button onClick={() => setShowBugReport(true)} className="sidebar-item bug-report" title="Report Bug or Feedback">
            <span className="sidebar-icon"><AlertCircle size={20} /></span>
            <span className="sidebar-label">Feedback</span>
          </button>
          <button onClick={handleSignOut} className="sidebar-item logout">
            <span className="sidebar-icon"><LogOut size={20} /></span>
            <span className="sidebar-label">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}>
          <aside className="sidebar mobile-sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="sidebar-logo">
              <span className="logo-icon"><Calendar size={24} /></span>
              <span className="logo-text">MeetSync</span>
              <button className="sidebar-close" onClick={() => setSidebarOpen(false)}><X size={20} /></button>
            </div>
            
            <nav className="sidebar-nav">
              {[
                { id: 'dashboard', icon: <Home size={20} />, label: 'Dashboard', showFor: 'all' },
                { id: 'calendar', icon: <Calendar size={20} />, label: 'Calendar', showFor: 'student' },
                { id: 'calls', icon: <Phone size={20} />, label: 'Upcoming Calls', showFor: 'student' },
                { id: 'analytics', icon: <BarChart3 size={20} />, label: 'Analytics', showFor: 'student' },
                { id: 'questions', icon: <HelpCircle size={20} />, label: 'Questions', showFor: 'admin' },
                { id: 'contact-requests', icon: <Mail size={20} />, label: 'Inquiries', showFor: 'superadmin' },
                { id: 'messages', icon: <MessageSquare size={20} />, label: 'Messages', showFor: 'all' },
                { id: 'profile', icon: <User size={20} />, label: 'Profile', showFor: 'all' },
              ]
                .filter(item => {
                  if (item.showFor === 'all') return true;
                  if (item.showFor === 'admin' && userProfile.isAdmin) return true;
                  if (item.showFor === 'student' && !userProfile.isAdmin) return true;
                  if (item.showFor === 'superadmin' && userProfile.email === 'simonegalvan.it@gmail.com') return true;
                  return false;
                })
                .map(item => (
                <button 
                  key={item.id} 
                  onClick={() => {
                    if (userProfile.hasAccess || item.id === 'dashboard') {
                      setView(item.id);
                      setSidebarOpen(false);
                    } else {
                      setError('Please enter access code first');
                    }
                  }} 
                  className={`sidebar-item ${view === item.id ? 'active' : ''}`}
                >
                  <span className="sidebar-icon">{item.icon}</span>
                  <span className="sidebar-label">{item.label}</span>
                </button>
              ))}
            </nav>
            
            <div className="sidebar-footer">
              <button onClick={() => { handleSignOut(); setSidebarOpen(false); }} className="sidebar-item logout">
                <span className="sidebar-icon"><LogOut size={20} /></span>
                <span className="sidebar-label">Logout</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Top Header - Minimalist with date/time and notifications */}
      <header className="header">
        <div className="header-content">
          {/* Mobile hamburger */}
          <button className="hamburger-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <Menu size={20} />
          </button>
          
          {/* Mobile logo */}
          <div className="mobile-logo"><Calendar size={20} /> MeetSync</div>
          
          {/* Community name badge - Desktop */}
          {communityInfo && (
            <div className="header-community">
              <span className="community-badge"><Building size={14} /> {communityInfo.name}</span>
            </div>
          )}
          
          <div className="header-actions">
            {/* Do Not Disturb Toggle */}
            <div className="dnd-toggle" title={dndMode ? 'Do Not Disturb: ON' : 'Do Not Disturb: OFF'}>
              <button 
                className={`dnd-btn ${dndMode ? 'active' : ''}`}
                onClick={async () => {
                  const newDndMode = !dndMode;
                  setDndMode(newDndMode);
                  // Persist to Firestore
                  try {
                    await updateDoc(doc(db, 'users', userProfile.id), { dndMode: newDndMode });
                    // Show professional toast with icon
                    setToastMessage(
                      newDndMode 
                        ? '🌙 Do Not Disturb enabled' 
                        : '☀️ Do Not Disturb disabled'
                    );
                    setToastType(newDndMode ? 'dnd-on' : 'dnd-off');
                    setShowToast(true);
                    setTimeout(() => setShowToast(false), 2500);
                  } catch (err) {
                    debug.error('Failed to save DND mode:', err);
                  }
                }}
              >
                {dndMode ? <Moon size={18} /> : <Sun size={18} />}
              </button>
            </div>
            
            {/* Notification Bell */}
            <div className="notification-wrapper">
              <button 
                className={`notification-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell size={20} />
                {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
              </button>
              {showNotifications && (
                <div className="notification-dropdown">
                  <div className="notification-header">
                    <h4>Notifications</h4>
                    {unreadCount > 0 && (
                      <button onClick={markAllNotificationsRead} className="mark-all-read">
                        <Check size={14} /> Mark all read
                      </button>
                    )}
                  </div>
                  <div className="notification-list">
                    {notifications.length === 0 ? (
                      <div className="no-notifications">
                        <Bell size={24} />
                        <p>No notifications yet</p>
                      </div>
                    ) : (
                      notifications.slice(0, 10).map(notif => (
                        <div 
                          key={notif.id} 
                          className={`notification-item ${!notif.read ? 'unread' : ''}`}
                          onClick={() => {
                            markNotificationRead(notif.id);
                            setShowNotifications(false);
                            
                            // Navigate based on notification type
                            if (notif.type === 'roleplay_now' && notif.data?.requestId) {
                              const request = roleplayNowRequests.find(r => r.id === notif.data.requestId);
                              if (request && !dndMode) {
                                setPendingRoleplayRequest(request);
                                setShowRoleplayNowModal(true);
                              }
                            } else if (notif.type === 'match_booked') {
                              // Someone booked a call with you - go to calendar
                              setView('calls');
                            } else if (notif.type === 'invite' && notif.data?.conversationId) {
                              // Go to messages and open the conversation
                              setActiveConversation(notif.data.conversationId);
                              setView('messages');
                            } else if (notif.type === 'message' && notif.data?.conversationId) {
                              // Go to messages and open the conversation
                              setActiveConversation(notif.data.conversationId);
                              setView('messages');
                            } else if (notif.type === 'booking' && notif.data?.conversationId) {
                              // Go to upcoming calls
                              setView('calls');
                            } else if (notif.type === 'match') {
                              // Go to dashboard
                              setView('dashboard');
                            } else if (notif.type === 'roleplay_accepted' && notif.data?.conversationId) {
                              // Go to messages
                              setActiveConversation(notif.data.conversationId);
                              setView('messages');
                            } else if (notif.type === 'reminder') {
                              // Meeting reminder - go to calls
                              setView('calls');
                            }
                          }}
                        >
                          <span className="notification-icon">
                            {notif.type === 'invite' && <Calendar size={18} />}
                            {notif.type === 'match' && <CheckCircle size={18} />}
                            {notif.type === 'match_booked' && <Calendar size={18} />}
                            {notif.type === 'message' && <MessageSquare size={18} />}
                            {notif.type === 'booking' && <Check size={18} />}
                            {notif.type === 'roleplay_now' && <Zap size={18} />}
                            {notif.type === 'roleplay_accepted' && <CheckCircle size={18} />}
                            {notif.type === 'reminder' && <Clock size={18} />}
                            {!['invite', 'match', 'match_booked', 'message', 'booking', 'roleplay_now', 'roleplay_accepted', 'reminder'].includes(notif.type) && <Bell size={18} />}
                          </span>
                          <div className="notification-content">
                            <span className="notification-title">{notif.title}</span>
                            <span className="notification-message">{notif.message}</span>
                            <span className="notification-time">{formatNotificationTime(notif.createdAt)}</span>
                          </div>
                          <button 
                            className="notification-remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeNotification(notif.id);
                            }}
                            title="Remove notification"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Profile Icon Only - Click to go to profile */}
            <button 
              className="header-profile-btn" 
              onClick={() => setView('profile')}
              title={userProfile.name}
            >
              <span className="header-avatar">{userProfile.avatar}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        {/* Admin Dashboard - Only for Admins */}
        {view === 'dashboard' && userProfile.isAdmin && (
          <div className="admin-dashboard">
            <div className="admin-header">
              <div className="admin-title">
                <h1><Building size={28} /> Admin Dashboard</h1>
                <p>{communityInfo?.name || 'Your Community'}</p>
              </div>
              <div className="admin-header-right">
                <div className="admin-plan-badge">
                  {communityInfo?.plan === 'pro' ? <Crown size={14} /> : <Zap size={14} />}
                  <span>{communityInfo?.plan === 'pro' ? 'Pro' : 'Starter'} Plan</span>
                </div>
                <div className="admin-code-display">
                  <span className="code-label">Classroom Code:</span>
                  <span className="code-value-small">{communityInfo?.code || 'N/A'}</span>
                  <button 
                    className="btn-copy-small"
                    onClick={() => {
                      navigator.clipboard.writeText(communityInfo?.code || '');
                      setToastMessage('Code copied to clipboard!');
                      setToastType('success');
                      setShowToast(true);
                      setTimeout(() => setShowToast(false), 2500);
                    }}
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            </div>

            {/* Member Capacity Bar - Students only (admin not counted) */}
            <div className="member-capacity-bar">
              <div className="capacity-info">
                <Users size={18} />
                <span className="capacity-text">
                  <strong>{users.filter(u => !u.isAdmin && u.communityId === userProfile.communityId).length}</strong> of {communityInfo?.maxMembers || 10} students
                </span>
              </div>
              <div className="capacity-progress">
                <div 
                  className="capacity-fill"
                  style={{ 
                    width: `${Math.min(100, (users.filter(u => !u.isAdmin && u.communityId === userProfile.communityId).length / (communityInfo?.maxMembers || 10)) * 100)}%`,
                    backgroundColor: (users.filter(u => !u.isAdmin && u.communityId === userProfile.communityId).length / (communityInfo?.maxMembers || 10)) > 0.9 ? '#EF4444' : 
                                     (users.filter(u => !u.isAdmin && u.communityId === userProfile.communityId).length / (communityInfo?.maxMembers || 10)) > 0.7 ? '#F59E0B' : '#10B981'
                  }}
                ></div>
              </div>
              {(users.filter(u => !u.isAdmin && u.communityId === userProfile.communityId).length / (communityInfo?.maxMembers || 10)) >= 0.8 && (
                <button className="upgrade-hint" onClick={() => setShowContactSales(true)}>
                  Need more seats? Upgrade your plan →
                </button>
              )}
            </div>

            {success && <div className="success-message">{success}</div>}
            {error && <div className="error-message">{error}</div>}

            {/* Admin Stats - Using community-wide data */}
            <div className="admin-stats-grid">
              <div className="admin-stat-card">
                <div className="stat-icon"><Users size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">{communityMembers.filter(m => !m.isAdmin).length}</span>
                  <span className="stat-label">Students</span>
                </div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-icon"><CheckCircle size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">
                    {communityBookings.filter(b => b.status === 'completed').length}
                  </span>
                  <span className="stat-label">Sessions Completed</span>
                </div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-icon"><Calendar size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">
                    {communityBookings.filter(b => b.status === 'scheduled').length}
                  </span>
                  <span className="stat-label">Upcoming Sessions</span>
                </div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-icon"><Activity size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">
                    {communityMembers.filter(m => m.isOnline === true).length}
                  </span>
                  <span className="stat-label">Online Now</span>
                </div>
              </div>
            </div>

            {/* Admin Tabs */}
            <div className="admin-tabs">
              <button 
                className={`admin-tab ${adminViewMode === 'overview' ? 'active' : ''}`}
                onClick={() => setAdminViewMode('overview')}
              >
                <BarChart3 size={16} /> Overview
              </button>
              <button 
                className={`admin-tab ${adminViewMode === 'leaderboard' ? 'active' : ''}`}
                onClick={() => setAdminViewMode('leaderboard')}
              >
                <Award size={16} /> Leaderboard
              </button>
              <button 
                className={`admin-tab ${adminViewMode === 'members' ? 'active' : ''}`}
                onClick={() => setAdminViewMode('members')}
              >
                <Users size={16} /> Members
              </button>
              <button 
                className={`admin-tab ${adminViewMode === 'analytics' ? 'active' : ''}`}
                onClick={() => setAdminViewMode('analytics')}
              >
                <TrendingUp size={16} /> Analytics
              </button>
              <button 
                className="admin-tab broadcast-btn"
                onClick={() => setShowBroadcastModal(true)}
              >
                <Bell size={16} /> Send Notification
              </button>
            </div>

            {/* Overview Tab */}
            {adminViewMode === 'overview' && (
              <div className="admin-overview">
                <div className="overview-section">
                  <h3>Recent Activity</h3>
                  <div className="activity-list">
                    {communityActivity.slice(0, 10).map((activity, idx) => {
                      const user1 = communityMembers.find(m => m.id === activity.participants?.[0]);
                      const user2 = communityMembers.find(m => m.id === activity.participants?.[1]);
                      return (
                        <div key={activity.id || idx} className="activity-item">
                          <span className="activity-icon">
                            {activity.status === 'completed' ? <CheckCircle size={16} /> : activity.status === 'scheduled' ? <Calendar size={16} /> : <Zap size={16} />}
                          </span>
                          <div className="activity-info">
                            <span className="activity-text">
                              {user1?.name || activity.participantDetails?.[activity.participants?.[0]]?.name || 'User'} & {user2?.name || activity.participantDetails?.[activity.participants?.[1]]?.name || 'User'}
                            </span>
                            <span className="activity-time">
                              {activity.status === 'completed' ? 'Completed session' : 'Scheduled session'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {communityActivity.length === 0 && (
                      <p className="no-activity">No sessions yet. Share your classroom code to invite members!</p>
                    )}
                  </div>
                </div>

                <div className="overview-section">
                  <h3><AlertCircle size={16} /> Needs Follow-up</h3>
                  <div className="followup-list">
                    {communityMembers
                      .filter(m => !m.isAdmin)
                      .filter(m => {
                        const memberBookings = communityBookings.filter(b => 
                          b.participants?.includes(m.id) && b.status === 'completed'
                        );
                        return memberBookings.length === 0;
                      })
                      .slice(0, 5)
                      .map(m => (
                        <div key={m.id} className="followup-item">
                          <span className="followup-avatar">{m.avatar}</span>
                          <span className="followup-name">{m.name}</span>
                          <span className="followup-badge">0 sessions</span>
                          <button 
                            className="followup-action"
                            onClick={() => startChatWithMember(m)}
                          >
                            <MessageSquare size={16} />
                          </button>
                        </div>
                      ))
                    }
                    {communityMembers.filter(m => !m.isAdmin).filter(m => {
                      const memberBookings = communityBookings.filter(b => 
                        b.participants?.includes(m.id) && b.status === 'completed'
                      );
                      return memberBookings.length === 0;
                    }).length === 0 && (
                      <p className="no-activity">All students have participated! <Sparkles size={14} /></p>
                    )}
                  </div>
                </div>

                <div className="overview-section">
                  <h3>Quick Actions</h3>
                  <div className="quick-actions">
                    <button className="quick-action-btn" onClick={() => setAdminViewMode('leaderboard')}>
                      🏆 View Leaderboard
                    </button>
                    <button className="quick-action-btn" onClick={() => setView('messages')}>
                      💬 Messages
                    </button>
                    <button 
                      className="quick-action-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(communityInfo?.code || '');
                        setSuccess('Code copied!');
                        setTimeout(() => setSuccess(''), 2000);
                      }}
                    >
                      <Copy size={14} /> Copy Invite Code
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Members Tab */}
            {adminViewMode === 'members' && (
              <div className="admin-members">
                <div className="members-header">
                  <h3>Community Members ({communityMembers.length})</h3>
                </div>
                <div className="members-table">
                  <div className="members-table-header">
                    <span>Member</span>
                    <span>Sessions</span>
                    <span>Last Active</span>
                    <span>Actions</span>
                  </div>
                  {communityMembers.map(member => {
                    const stats = getMemberStats(member);
                    const isOnline = member.lastOnline && 
                      (Date.now() - (member.lastOnline.toDate ? member.lastOnline.toDate() : new Date(member.lastOnline)).getTime()) < 5 * 60 * 1000;
                    
                    return (
                      <div 
                        key={member.id} 
                        className={`member-row ${!member.isAdmin ? 'clickable' : ''}`}
                        onClick={() => !member.isAdmin && openStudentProfile(member)}
                      >
                        <div className="member-info">
                          <span className="member-avatar">{member.avatar || '👤'}</span>
                          <div className="member-details">
                            <span className="member-name">
                              {member.name}
                              {member.isAdmin && <span className="admin-badge">Admin</span>}
                              {isOnline && <span className="online-dot">●</span>}
                            </span>
                            <span className="member-email">{member.email}</span>
                          </div>
                        </div>
                        <span className="member-sessions">{stats.totalSessions}</span>
                        <span className="member-last-active">
                          {member.lastOnline ? 
                            new Date(member.lastOnline.toDate ? member.lastOnline.toDate() : member.lastOnline).toLocaleDateString() 
                            : 'Never'}
                        </span>
                        <div className="member-actions" onClick={e => e.stopPropagation()}>
                          {!member.isAdmin && (
                            <button 
                              className="action-btn view"
                              onClick={() => openStudentProfile(member)}
                              title="View Profile"
                            >
                              <BarChart3 size={14} />
                            </button>
                          )}
                          <button 
                            className="action-btn chat"
                            onClick={() => startChatWithMember(member)}
                            title="Message"
                          >
                            💬
                          </button>
                          {!member.isAdmin && (
                            <button 
                              className="action-btn kick"
                              onClick={() => kickMember(member.id, member.name)}
                              title="Remove from community"
                            >
                              🚫
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Leaderboard Tab */}
            {adminViewMode === 'leaderboard' && (
              <div className="admin-leaderboard">
                <h3>🏆 Weekly Leaderboard</h3>
                <p className="leaderboard-subtitle">Performance rankings for the current week</p>
                
                <div className="leaderboard-table">
                  <div className="leaderboard-header">
                    <span className="lb-rank">#</span>
                    <span className="lb-name">Student</span>
                    <span className="lb-calls">Calls</span>
                    <span className="lb-goal">Goal</span>
                    <span className="lb-rating">Avg. Rating</span>
                    <span className="lb-active">Last Active</span>
                  </div>
                  {(() => {
                    // Calculate this week's start
                    const now = new Date();
                    const dayOfWeek = now.getDay();
                    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                    const weekStart = new Date(now);
                    weekStart.setDate(weekStart.getDate() - daysFromMonday);
                    weekStart.setHours(0, 0, 0, 0);
                    
                    // Build leaderboard data
                    const leaderboardData = communityMembers
                      .filter(m => !m.isAdmin)
                      .map(member => {
                        // This week's completed calls
                        const weekCalls = communityBookings.filter(b => 
                          b.participants?.includes(member.id) && 
                          b.status === 'completed' &&
                          new Date(b.dateTime) >= weekStart
                        ).length;
                        
                        // Total completed calls
                        const totalCalls = communityBookings.filter(b => 
                          b.participants?.includes(member.id) && 
                          b.status === 'completed'
                        ).length;
                        
                        // Average rating from reflections
                        const memberReflections = communityReflections.filter(r => r.userId === member.id);
                        const avgRating = memberReflections.length > 0
                          ? memberReflections.reduce((sum, r) => {
                              const score = r.rating === 'happy' ? 3 : r.rating === 'neutral' ? 2 : 1;
                              return sum + score;
                            }, 0) / memberReflections.length
                          : 0;
                        
                        // Goal status
                        const goalMet = member.weeklyGoal ? weekCalls >= member.weeklyGoal : null;
                        
                        return {
                          ...member,
                          weekCalls,
                          totalCalls,
                          avgRating,
                          goalMet
                        };
                      })
                      .sort((a, b) => b.weekCalls - a.weekCalls);
                    
                    return leaderboardData.map((member, idx) => (
                      <div key={member.id} className={`leaderboard-row ${idx < 3 ? 'top-three' : ''} ${member.weekCalls === 0 ? 'zero-calls' : ''}`}>
                        <span className="lb-rank">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                        </span>
                        <div className="lb-name">
                          <span className="lb-avatar">{member.avatar}</span>
                          <span>{member.name}</span>
                          {member.isOnline && <span className="online-indicator">●</span>}
                        </div>
                        <span className="lb-calls">{member.weekCalls}</span>
                        <span className={`lb-goal ${member.goalMet === true ? 'met' : member.goalMet === false ? 'not-met' : ''}`}>
                          {member.goalMet === true ? <CheckCircle size={14} className="goal-met" /> : member.goalMet === false ? <XCircle size={14} className="goal-missed" /> : '—'}
                        </span>
                        <span className="lb-rating">
                          {member.avgRating > 0 ? (
                            <>
                              {member.avgRating >= 1.5 ? <ThumbsUp size={14} className="rating-icon great" /> : <Meh size={14} className="rating-icon okay" />}
                              <span className="rating-num">{member.avgRating.toFixed(1)}</span>
                            </>
                          ) : '—'}
                        </span>
                        <span className="lb-active">
                          {member.lastOnline ? 
                            new Date(member.lastOnline.toDate ? member.lastOnline.toDate() : member.lastOnline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : 'Never'}
                        </span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {adminViewMode === 'analytics' && (
              <div className="admin-analytics">
                <h3>Community Analytics</h3>
                <div className="analytics-grid">
                  <div className="analytics-card">
                    <h4>Sessions This Week</h4>
                    <div className="analytics-value">
                      {communityBookings.filter(b => {
                        const bookingDate = new Date(b.dateTime);
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return bookingDate >= weekAgo && b.status === 'completed';
                      }).length}
                    </div>
                  </div>
                  <div className="analytics-card">
                    <h4>Most Active Member</h4>
                    <div className="analytics-value">
                      {(() => {
                        const memberSessions = {};
                        communityBookings.filter(b => b.status === 'completed').forEach(b => {
                          b.participants?.forEach(p => {
                            const member = communityMembers.find(m => m.id === p);
                            if (member && !member.isAdmin) {
                              memberSessions[p] = (memberSessions[p] || 0) + 1;
                            }
                          });
                        });
                        const topMemberId = Object.entries(memberSessions).sort((a, b) => b[1] - a[1])[0]?.[0];
                        const topMember = communityMembers.find(m => m.id === topMemberId);
                        return topMember ? `${topMember.avatar} ${topMember.name}` : 'N/A';
                      })()}
                    </div>
                  </div>
                  <div className="analytics-card">
                    <h4>Avg Sessions/Member</h4>
                    <div className="analytics-value">
                      {communityMembers.filter(m => !m.isAdmin).length > 0 ? 
                        (communityBookings.filter(b => b.status === 'completed').length / communityMembers.filter(m => !m.isAdmin).length).toFixed(1) 
                        : '0'}
                    </div>
                  </div>
                  <div className="analytics-card">
                    <h4>Participation Rate</h4>
                    <div className="analytics-value">
                      {communityMembers.filter(m => !m.isAdmin).length > 0 ? 
                        Math.round((communityMembers.filter(m => !m.isAdmin).filter(m => {
                          return communityBookings.some(b => b.participants?.includes(m.id) && b.status === 'completed');
                        }).length / communityMembers.filter(m => !m.isAdmin).length) * 100) + '%'
                        : '0%'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Regular Dashboard - Only for Non-Admins */}
        {view === 'dashboard' && !userProfile.isAdmin && (
          <div className="dashboard">
            {/* Weekly Progress Tracker - Top of Dashboard */}
            <div className="weekly-tracker-card">
              <div className="tracker-content">
                <div className="tracker-text">
                  <h2>
                    Welcome back, {userProfile.name.split(' ')[0]}! You've completed{' '}
                    <span className="tracker-highlight">
                      {weeklyCompletedCount}/{userProfile.weeklyGoal || '∞'}
                    </span>{' '}
                    roleplays this week.{' '}
                    {weeklyCompletedCount > 0 && <span className="tracker-encouragement">Keep it up! 💪</span>}
                  </h2>
                </div>
                <div className="tracker-bar-section">
                  {userProfile.weeklyGoal ? (
                    <div className="segmented-progress">
                      {[...Array(userProfile.weeklyGoal)].map((_, i) => (
                        <div 
                          key={i} 
                          className={`progress-segment ${i < weeklyCompletedCount ? 'filled' : ''}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="progress-bar unlimited">
                      <div 
                        className="progress-fill animated" 
                        style={{ width: `${Math.min(weeklyCompletedCount * 15, 100)}%` }}
                      ></div>
                    </div>
                  )}
                  <div className="tracker-meta">
                    <span className="tracker-reset-time">
                      Resets Monday • {getTimezoneAbbr(userProfile.timezone)}
                    </span>
                    {!userProfile.weeklyGoal && (
                      <button onClick={() => { setEditingProfile(true); setView('profile'); }} className="set-goal-link">
                        Set a goal →
                      </button>
                    )}
                    {weeklyCompletedCount >= (userProfile.weeklyGoal || 999) && (
                      <span className="goal-badge">🏆 Goal Complete!</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Modern Date/Time Widget */}
            <div className="datetime-widget">
              <div className="datetime-main">
                <div className="datetime-time">
                  {currentTime.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: true,
                    timeZone: userProfile.timezone || 'America/New_York'
                  }).replace(' ', '')}
                </div>
                <div className="datetime-seconds">
                  :{currentTime.toLocaleString('en-US', { second: '2-digit', timeZone: userProfile.timezone || 'America/New_York' }).padStart(2, '0')}
                </div>
              </div>
              <div className="datetime-date">
                <span className="datetime-weekday">
                  {currentTime.toLocaleDateString('en-US', { weekday: 'long', timeZone: userProfile.timezone || 'America/New_York' })}
                </span>
                <span className="datetime-full">
                  {currentTime.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: userProfile.timezone || 'America/New_York' })}
                </span>
              </div>
              <div className="datetime-footer">
                <span className="datetime-tz">{getTimezoneAbbr(userProfile.timezone)}</span>
                {communityInfo && <span className="datetime-community">• {communityInfo.name}</span>}
              </div>
            </div>

            {/* Live Call Tracker - Cyberpunk Command Center */}
            {liveCall && (
              <div className="cyber-call-tracker">
                <div className="cyber-glow-border"></div>
                <div className="cyber-content">
                  <div className="cyber-header">
                    <div className="cyber-live-badge">
                      <span className="live-dot"></span>
                      <span>LIVE SESSION</span>
                    </div>
                    <div className="cyber-timer">
                      {String(Math.floor(liveCallTimer / 60)).padStart(2, '0')}:
                      {String(liveCallTimer % 60).padStart(2, '0')}
                    </div>
                  </div>
                  
                  <div className="cyber-partner">
                    <div className="cyber-avatar-ring">
                      <div className="cyber-avatar">{liveCall.partnerAvatar}</div>
                    </div>
                    <div className="cyber-partner-info">
                      <span className="cyber-label">CONNECTED WITH</span>
                      <span className="cyber-name">{liveCall.partnerName}</span>
                    </div>
                  </div>
                  
                  <div className="cyber-progress">
                    <div className="cyber-progress-track">
                      <div 
                        className="cyber-progress-fill" 
                        style={{ width: `${Math.min(100, (liveCallTimer / 3600) * 100)}%` }}
                      >
                        <div className="cyber-progress-glow"></div>
                        <div className="cyber-scanline"></div>
                      </div>
                    </div>
                    <div className="cyber-progress-labels">
                      <span>0:00</span>
                      <span className="cyber-duration">SESSION: 60 MIN</span>
                      <span>60:00</span>
                    </div>
                  </div>
                  
                  {liveCall.meetingLink && (
                    <a 
                      href={liveCall.meetingLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="cyber-rejoin-btn"
                    >
                      <Video size={18} />
                      <span>REJOIN ZOOM</span>
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Roleplay Now - Prominent Button */}
            <div className="roleplay-now-section">
              {!isRoleplayNowActive ? (
                <button 
                  className={`roleplay-now-btn ${roleplayNowBlocked ? 'blocked' : ''}`}
                  onClick={sendRoleplayNow}
                  disabled={roleplayNowBlocked}
                  title={roleplayNowBlocked ? roleplayBlockReason : 'Start instant roleplay'}
                >
                  <span className="roleplay-now-icon">
                    {roleplayNowBlocked ? <Clock size={28} /> : <Zap size={28} />}
                  </span>
                  <div>
                    <span className="roleplay-now-text">
                      {roleplayNowBlocked ? 'Scheduled Call Soon' : 'Roleplay Now'}
                    </span>
                    <span className="roleplay-now-subtitle">
                      {roleplayNowBlocked ? roleplayBlockReason : 'Instant match with available users'}
                    </span>
                  </div>
                </button>
              ) : (
                <div className="roleplay-now-pending">
                  <div className="roleplay-now-pending-info">
                    <span className="pending-spinner"><Loader size={24} className="spin" /></span>
                    <div>
                      <span className="roleplay-now-text">Looking for partner...</span>
                      <span className="roleplay-now-subtitle">
                        Request sent to {users.filter(u => u.id !== userProfile.id && !u.isAdmin).length} users
                      </span>
                    </div>
                  </div>
                  <button 
                    className="btn-cancel-roleplay"
                    onClick={cancelRoleplayNow}
                  >
                    <X size={16} /> Cancel
                  </button>
                </div>
              )}
              <div className="online-count">
                <span className="online-dot"></span>
                {users.filter(u => isUserOnline(u) && !u.isAdmin).length} online now
              </div>
            </div>

            {/* Success/Error Messages */}
            {success && view === 'dashboard' && (
              <div className="success-message">{success}</div>
            )}
            {error && view === 'dashboard' && (
              <div className="error-message">{error}</div>
            )}

            <div className="dashboard-grid">
              <div className="card">
                <h3><Calendar size={20} /> Your Schedule</h3>
                <p>{Object.keys(userProfile.availability || {}).length} days with availability set</p>
                <button onClick={() => setView('calendar')} className="btn-secondary">Manage Calendar →</button>
              </div>
              <div className="card highlight">
                <h3><Target size={20} className="card-icon" /> Find a Match</h3>
                <p>Match with someone who shares your available times.</p>
                <button onClick={findMatch} disabled={isMatching} className="btn-primary">
                  {isMatching ? <><Loader size={16} className="spin" /> Searching...</> : <><Search size={16} /> Find Match</>}
                </button>
              </div>
              <div className="card">
                <h3><Phone size={20} className="card-icon" /> Upcoming Calls</h3>
                <p>{getUpcomingCalls().length} scheduled calls</p>
                <button onClick={() => setView('calls')} className="btn-secondary">View All <ArrowRight size={14} /></button>
              </div>
            </div>
            {users.length > 0 && (
              <div className="card">
                <h3><Users size={20} className="card-icon" /> Community Members</h3>
                <div className="users-grid">
                  {users.slice(0, 6).map(u => {
                    const online = isUserOnline(u);
                    return (
                      <div key={u.id} className="user-card">
                        <div className="user-card-avatar-wrapper">
                          <span className="user-card-avatar">{u.avatar}</span>
                          <span className={`online-indicator ${online ? 'online' : 'offline'}`}></span>
                        </div>
                        <div className="user-card-info">
                          <span className="user-card-name">{u.name}</span>
                          <span className="user-card-status">
                            {online ? '🟢 Online' : '⚫ Offline'} • {Object.values(u.availability || {}).flat().length} slots
                          </span>
                        </div>
                        <button onClick={() => startDirectChat(u)} className="user-card-chat-btn">💬</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Admin Questions View */}
        {view === 'questions' && userProfile.isAdmin && (
          <div className="questions-view">
            <div className="page-header">
              <h1><HelpCircle size={28} /> Student Questions</h1>
              <p>Anonymous questions from your students</p>
            </div>
            
            <div className="questions-container">
              {adminQuestions.length === 0 ? (
                <div className="empty-questions">
                  <MessageSquare size={48} className="empty-icon-svg" />
                  <h3>No questions yet</h3>
                  <p>When students complete roleplays, they can send you anonymous questions that will appear here.</p>
                </div>
              ) : (
                <div className="questions-list">
                  {adminQuestions.map(q => (
                    <div key={q.id} className={`question-card ${q.read ? '' : 'unread'}`}>
                      <div className="question-header">
                        <span className="question-badge">Anonymous</span>
                        <span className="question-date">
                          {q.createdAt?.toDate ? 
                            q.createdAt.toDate().toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) 
                            : 'Just now'}
                        </span>
                      </div>
                      <p className="question-text">{q.question}</p>
                      {!q.read && (
                        <button 
                          className="mark-read-btn"
                          onClick={async () => {
                            await updateDoc(doc(db, 'adminQuestions', q.id), { read: true });
                          }}
                        >
                          <Check size={14} /> Mark as Read
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact Requests View - Only for simonegalvan.it@gmail.com */}
        {view === 'contact-requests' && userProfile.email === 'simonegalvan.it@gmail.com' && (
          <div className="contact-requests-view">
            <div className="page-header">
              <h1><Mail size={28} /> Enterprise Inquiries</h1>
              <p>Contact requests from potential enterprise customers</p>
            </div>
            
            <div className="requests-container">
              {contactRequests.length === 0 ? (
                <div className="empty-requests">
                  <Mail size={48} className="empty-icon-svg" />
                  <h3>No inquiries yet</h3>
                  <p>When someone fills out the enterprise contact form, their request will appear here.</p>
                </div>
              ) : (
                <div className="requests-list">
                  {contactRequests.map(req => (
                    <div key={req.id} className={`request-card ${req.status === 'new' ? 'unread' : ''}`}>
                      <div className="request-header">
                        <span className="request-company">{req.companyName}</span>
                        <span className="request-date">
                          {req.createdAt?.toDate ? 
                            req.createdAt.toDate().toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) 
                            : 'Just now'}
                        </span>
                      </div>
                      <div className="request-contact">
                        <strong>{req.firstName} {req.lastName}</strong>
                        <a href={`mailto:${req.workEmail}`}>{req.workEmail}</a>
                        {req.phoneNumber && <span><Phone size={12} /> {req.phoneNumber}</span>}
                      </div>
                      {req.specificNeeds && (
                        <p className="request-needs">{req.specificNeeds}</p>
                      )}
                      <div className="request-actions">
                        <a href={`mailto:${req.workEmail}?subject=Re: MeetSync Enterprise Inquiry`} className="btn-primary">
                          <Mail size={14} /> Reply
                        </a>
                        {req.status === 'new' && (
                          <button 
                            className="btn-secondary"
                            onClick={async () => {
                              await updateDoc(doc(db, 'contactRequests', req.id), { status: 'contacted' });
                            }}
                          >
                            <Check size={14} /> Mark Contacted
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar */}
        {view === 'calendar' && !userProfile.isAdmin && (
          <div className="availability-calendar-view">
            <div className="page-header">
              <div className="page-header-content">
                <div>
                  <h1>Your Availability Calendar</h1>
                  <p>Click on a day to set your available hours</p>
                </div>
                {Object.keys(userProfile.availability || {}).length > 0 && (
                  <button onClick={() => setShowResetModal(true)} className="btn-reset">
                    <Trash2 size={16} /> Reset All
                  </button>
                )}
              </div>
            </div>
            {success && <div className="success-message"><CheckCircle size={16} /> {success}</div>}
            <div className="card">
              <div className="calendar-nav">
                <button onClick={goToPreviousWeek} className="cal-nav-btn"><ChevronLeft size={20} /></button>
                <h2>{formatWeekRange(currentWeekStart)}</h2>
                <button onClick={goToNextWeek} className="cal-nav-btn"><ChevronRight size={20} /></button>
              </div>
              <div className="weekly-calendar">
                {getDaysInWeek(currentWeekStart).map((date, i) => {
                  const dateKey = formatDateKey(date);
                  const hours = getAvailabilityForDate(dateKey);
                  const isPast = isDatePast(date);
                  const isToday = formatDateKey(new Date()) === dateKey;
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                  const dayNum = date.getDate();
                  const monthName = date.toLocaleDateString('en-US', { month: 'short' });
                  
                  return (
                    <div
                      key={i}
                      onClick={() => !isPast && handleDateClick(date)}
                      className={`weekly-day ${isPast ? 'past' : ''} ${isToday ? 'today' : ''} ${hours.length > 0 ? 'has-slots' : ''}`}
                    >
                      <div className="weekly-day-header">
                        <span className="weekly-day-name">{dayName}</span>
                        <span className="weekly-day-date">{monthName} {dayNum}</span>
                      </div>
                      <div className="weekly-day-content">
                        {hours.length > 0 ? (
                          <>
                            <span className="weekly-slot-count">{hours.length} slots</span>
                            <div className="weekly-hours-preview">
                              {hours.slice(0, 3).map(h => (
                                <span key={h} className="weekly-hour-chip">{formatHour(h)}</span>
                              ))}
                              {hours.length > 3 && <span className="weekly-hour-more">+{hours.length - 3} more</span>}
                            </div>
                          </>
                        ) : (
                          <span className="weekly-no-slots">{isPast ? 'Past' : 'Click to add'}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="calendar-actions">
              <button onClick={findMatch} disabled={isMatching} className="btn-primary btn-find-match">
                {isMatching ? <><Loader size={18} className="spin" /> Searching...</> : <><Target size={18} /> Find Match</>}
              </button>
            </div>
          </div>
        )}

        {/* Upcoming Calls - Full Calendar View (students only) */}
        {view === 'calls' && !userProfile.isAdmin && (
          <div className="calendar-view">
            {/* Calendar Header */}
            <div className="calendar-header">
              <div className="calendar-title">
                <button className="cal-nav-btn" onClick={() => {
                  const newDate = new Date(calendarDate);
                  if (calendarViewMode === 'daily') newDate.setDate(newDate.getDate() - 1);
                  else if (calendarViewMode === 'weekly') newDate.setDate(newDate.getDate() - 7);
                  else newDate.setMonth(newDate.getMonth() - 1);
                  setCalendarDate(newDate);
                }}>←</button>
                <h1>
                  {calendarViewMode === 'daily' 
                    ? calendarDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                    : calendarViewMode === 'weekly'
                    ? `${calendarDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${new Date(calendarDate.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                    : calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                  }
                </h1>
                <button className="cal-nav-btn" onClick={() => {
                  const newDate = new Date(calendarDate);
                  if (calendarViewMode === 'daily') newDate.setDate(newDate.getDate() + 1);
                  else if (calendarViewMode === 'weekly') newDate.setDate(newDate.getDate() + 7);
                  else newDate.setMonth(newDate.getMonth() + 1);
                  setCalendarDate(newDate);
                }}>→</button>
              </div>
              
              <div className="calendar-controls">
                <div className="view-toggle">
                  <button 
                    className={`view-btn ${calendarViewMode === 'daily' ? 'active' : ''}`}
                    onClick={() => setCalendarViewMode('daily')}
                  >Daily</button>
                  <button 
                    className={`view-btn ${calendarViewMode === 'weekly' ? 'active' : ''}`}
                    onClick={() => setCalendarViewMode('weekly')}
                  >Weekly</button>
                  <button 
                    className={`view-btn ${calendarViewMode === 'monthly' ? 'active' : ''}`}
                    onClick={() => setCalendarViewMode('monthly')}
                  >Monthly</button>
                </div>
                <button className="btn-primary" onClick={() => setView('calendar')}>
                  + Create Event
                </button>
              </div>
            </div>

            {/* Weekly View */}
            {calendarViewMode === 'weekly' && (
              <div className="week-calendar">
                <div className="week-header">
                  <div className="week-timezone">
                    {getTimezoneAbbr(userProfile.timezone)}
                  </div>
                  {(() => {
                    const days = [];
                    // Start from calendarDate, not Sunday
                    for (let i = 0; i < 7; i++) {
                      const day = new Date(calendarDate);
                      day.setDate(calendarDate.getDate() + i);
                      const isToday = day.toDateString() === new Date().toDateString();
                      days.push(
                        <div key={i} className={`week-day-header ${isToday ? 'today' : ''}`}>
                          <span className="day-name">{day.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                          <span className="day-number">{day.getDate()}</span>
                        </div>
                      );
                    }
                    return days;
                  })()}
                </div>
                
                <div className="week-body">
                  <div className="time-column">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23].map(hour => (
                      <div key={hour} className="time-slot-label">
                        {formatHour(hour)}
                      </div>
                    ))}
                  </div>
                  
                  <div className="days-grid">
                    {(() => {
                      // Start from calendarDate, not Sunday
                      return [...Array(7)].map((_, dayIndex) => {
                        const day = new Date(calendarDate);
                        day.setDate(calendarDate.getDate() + dayIndex);
                        const dayStr = day.toISOString().split('T')[0];
                        const isToday = day.toDateString() === new Date().toDateString();
                        
                        // Get bookings for this day
                        const dayBookings = bookings.filter(b => {
                          const bookingDate = b.dateTime?.split('T')[0] || b.date;
                          return bookingDate === dayStr && b.status !== 'completed';
                        });
                        
                        // Get pending invites for this day
                        const dayPendingInvites = pendingInvites.filter(invite => {
                          // For invites I received, check theirDate (my view)
                          // For invites I sent, check myDate (my view)
                          const inviteDate = invite.isSentByMe 
                            ? invite.inviteData?.myDate 
                            : invite.inviteData?.theirDate;
                          return inviteDate === dayStr;
                        });
                        
                        return (
                          <div key={dayIndex} className={`day-column ${isToday ? 'today' : ''}`}>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23].map(hour => {
                              const hourBookings = dayBookings.filter(b => {
                                const bookingHour = parseInt(b.dateTime?.split('T')[1]?.split(':')[0] || '0');
                                return bookingHour === hour;
                              });
                              
                              // Get pending invites for this hour
                              const hourInvites = dayPendingInvites.filter(invite => {
                                const inviteHour = invite.isSentByMe
                                  ? invite.inviteData?.myHour
                                  : invite.inviteData?.theirHour;
                                const h = typeof inviteHour === 'string' ? parseInt(inviteHour.split(':')[0]) : inviteHour;
                                return h === hour;
                              });
                              
                              return (
                                <div key={hour} className="hour-cell">
                                  {/* Show confirmed bookings */}
                                  {hourBookings.map(booking => {
                                    const otherUserId = booking.participants?.find(p => p !== userProfile.id);
                                    const otherUser = booking.participantDetails?.[otherUserId] || users.find(u => u.id === otherUserId);
                                    const isLive = booking.status === 'in-progress';
                                    const statusClass = booking.status === 'in-progress' ? 'in-progress' : 
                                                        booking.status === 'completed' ? 'completed' : 'scheduled';
                                    
                                    return (
                                      <div 
                                        key={booking.id} 
                                        className={`calendar-event ${statusClass}`}
                                        onClick={() => openEventPopup(booking)}
                                      >
                                        <div className="event-time">
                                          <Clock size={12} />
                                          {formatHour(hour)} - {formatHour(hour + 1)}
                                        </div>
                                        <div className="event-participants">
                                          <span className="event-avatar">{otherUser?.avatar || <User size={14} />}</span>
                                          <span className="event-name">{otherUser?.name || 'Partner'}</span>
                                        </div>
                                        {isLive && (
                                          <div className="event-status-badge live">
                                            <Radio size={10} /> Live
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  
                                  {/* Show pending invites */}
                                  {hourInvites.map(invite => (
                                    <div 
                                      key={invite.id} 
                                      className={`calendar-event pending-invite ${invite.isSentByMe ? 'sent' : 'received'}`}
                                      style={{ backgroundColor: invite.isSentByMe ? '#fef3c7' : '#fee2e2' }}
                                    >
                                      <div className="event-time">
                                        {invite.isSentByMe ? <Send size={12} /> : <Mail size={12} />}
                                        {invite.isSentByMe ? 'Invite sent' : 'Invite received'}
                                      </div>
                                      <div className="event-participants">
                                        <span className="event-avatar">{invite.otherUser?.avatar || <User size={14} />}</span>
                                        <span className="event-name">{invite.otherUser?.name}</span>
                                      </div>
                                      {!invite.isSentByMe && (
                                        <div className="invite-actions-mini">
                                          <button 
                                            className="btn-accept-mini" 
                                            onClick={(e) => { e.stopPropagation(); acceptInviteFromCalendar(invite); }}
                                          >
                                            <Check size={14} />
                                          </button>
                                          <button 
                                            className="btn-decline-mini" 
                                            onClick={(e) => { e.stopPropagation(); declineInviteFromCalendar(invite); }}
                                          >
                                            <X size={14} />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Daily View */}
            {calendarViewMode === 'daily' && (
              <div className="daily-calendar">
                <div className="daily-header">
                  <span className="daily-date-big">{calendarDate.getDate()}</span>
                  <span className="daily-day-name">{calendarDate.toLocaleDateString('en-US', { weekday: 'long' })}</span>
                </div>
                <div className="daily-schedule">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23].map(hour => {
                    const dayStr = calendarDate.toISOString().split('T')[0];
                    const hourBookings = bookings.filter(b => {
                      const bookingDate = b.dateTime?.split('T')[0] || b.date;
                      const bookingHour = parseInt(b.dateTime?.split('T')[1]?.split(':')[0] || '0');
                      return bookingDate === dayStr && bookingHour === hour && b.status !== 'completed';
                    });
                    
                    return (
                      <div key={hour} className="daily-hour-row">
                        <span className="daily-hour-label">{formatHour(hour)}</span>
                        <div className="daily-hour-content">
                          {hourBookings.map(booking => {
                            const otherUserId = booking.participants?.find(p => p !== userProfile.id);
                            const otherUser = booking.participantDetails?.[otherUserId] || users.find(u => u.id === otherUserId);
                            
                            return (
                              <div key={booking.id} className="daily-event" onClick={() => openEventPopup(booking)}>
                                <div className="daily-event-info">
                                  <span className="daily-event-title">Roleplay with {otherUser?.name || 'Partner'}</span>
                                  <span className="daily-event-time">{formatHour(hour)} - {formatHour(hour + 1)}</span>
                                </div>
                                {booking.meetingLink && (
                                  <a href={booking.meetingLink} target="_blank" rel="noopener noreferrer" className="btn-join-small" onClick={(e) => e.stopPropagation()}>Join</a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Monthly View */}
            {calendarViewMode === 'monthly' && (
              <div className="monthly-calendar">
                <div className="month-grid-header">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="month-day-header">{day}</div>
                  ))}
                </div>
                <div className="month-grid">
                  {(() => {
                    const firstDay = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1);
                    const lastDay = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 0);
                    const startPadding = firstDay.getDay();
                    const totalDays = lastDay.getDate();
                    const cells = [];
                    
                    for (let i = 0; i < startPadding; i++) {
                      cells.push(<div key={`pad-${i}`} className="month-cell empty"></div>);
                    }
                    
                    for (let day = 1; day <= totalDays; day++) {
                      const date = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day);
                      const dayStr = date.toISOString().split('T')[0];
                      const isToday = date.toDateString() === new Date().toDateString();
                      const dayBookings = bookings.filter(b => {
                        const bookingDate = b.dateTime?.split('T')[0] || b.date;
                        return bookingDate === dayStr;
                      });
                      
                      cells.push(
                        <div 
                          key={day} 
                          className={`month-cell ${isToday ? 'today' : ''} ${dayBookings.length > 0 ? 'has-events' : ''}`}
                          onClick={() => { setCalendarDate(date); setCalendarViewMode('daily'); }}
                        >
                          <span className="month-day-number">{day}</span>
                          {dayBookings.slice(0, 2).map((booking, idx) => (
                            <div key={idx} className="month-event-dot" title={`Roleplay at ${formatHour(parseInt(booking.dateTime?.split('T')[1]?.split(':')[0] || 0))}`}>
                              {booking.isRoleplayNow ? <Zap size={14} /> : <Calendar size={14} />}
                            </div>
                          ))}
                          {dayBookings.length > 2 && (
                            <span className="month-more-events">+{dayBookings.length - 2}</span>
                          )}
                        </div>
                      );
                    }
                    
                    return cells;
                  })()}
                </div>
              </div>
            )}

            {/* Sidebar with upcoming events */}
            <div className="calendar-sidebar">
              <h3><Calendar size={18} /> Upcoming</h3>
              <div className="upcoming-list">
                {getUpcomingCalls().length === 0 ? (
                  <p className="no-upcoming">No upcoming calls</p>
                ) : (
                  getUpcomingCalls().slice(0, 5).map(booking => {
                    const otherUserId = booking.participants?.find(p => p !== userProfile.id);
                    const otherUser = booking.participantDetails?.[otherUserId] || users.find(u => u.id === otherUserId);
                    
                    // Get MY local time from participantDetails, or convert if not available
                    const myDetails = booking.participantDetails?.[userProfile.id];
                    let displayDate, displayHour;
                    
                    if (myDetails?.localDate && myDetails?.localHour) {
                      // Use stored local time for current user
                      displayDate = myDetails.localDate;
                      displayHour = myDetails.localHour;
                    } else if (booking.bookerTimezone && booking.dateTime) {
                      // Convert from booker's timezone to my timezone
                      const bookingDate = booking.dateTime.split('T')[0];
                      const bookingHour = booking.dateTime.split('T')[1]?.split(':')[0] + ':00';
                      const converted = convertTimezone(bookingDate, bookingHour, booking.bookerTimezone, userProfile.timezone || 'America/New_York');
                      displayDate = converted.date;
                      displayHour = converted.hour;
                    } else {
                      // Fallback to stored dateTime
                      const dateObj = new Date(booking.dateTime || booking.date);
                      displayDate = dateObj.toISOString().split('T')[0];
                      displayHour = booking.dateTime?.split('T')[1]?.split(':')[0] + ':00' || '12:00';
                    }
                    
                    const displayDateObj = new Date(displayDate + 'T12:00:00');
                    
                    return (
                      <div key={booking.id} className="upcoming-card" onClick={() => goToBookingChat(booking)}>
                        <div className="upcoming-card-header">
                          <div className="upcoming-avatar">{otherUser?.avatar || <User size={20} />}</div>
                          <div className="upcoming-info">
                            <span className="upcoming-name">{otherUser?.name || 'Partner'}</span>
                            <span className="upcoming-date">
                              {displayDateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        </div>
                        <div className="upcoming-card-time">
                          <Clock size={14} />
                          <span>{formatHour(displayHour)}</span>
                        </div>
                        <div className="upcoming-card-actions">
                          <button 
                            className="upcoming-action-btn" 
                            onClick={(e) => { e.stopPropagation(); window.open(getGoogleCalendarUrl(booking), '_blank'); }}
                            title="Add to Google Calendar"
                          >
                            <Calendar size={14} />
                          </button>
                          <button 
                            className="upcoming-action-btn" 
                            onClick={(e) => { e.stopPropagation(); generateICSFile(booking); }}
                            title="Download .ics file"
                          >
                            <Download size={14} />
                          </button>
                          {booking.meetingLink && (
                            <a href={booking.meetingLink} target="_blank" rel="noopener noreferrer" className="upcoming-join-btn" onClick={e => e.stopPropagation()}>
                              <Video size={14} /> Join
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* Analytics Dashboard */}
        {view === 'analytics' && !userProfile.isAdmin && (
          <div className="analytics-view">
            <div className="analytics-header">
              <h1><BarChart3 size={28} /> Your Analytics</h1>
              <p>Track your practice progress and patterns</p>
            </div>
            
            {analyticsData ? (
              <>
                {/* Stats Overview Cards */}
                <div className="analytics-stats-grid">
                  <div className="analytics-stat-card">
                    <div className="stat-icon sessions"><Target size={24} /></div>
                    <div className="stat-info">
                      <span className="stat-value">{analyticsData.totalSessions}</span>
                      <span className="stat-label">Total Sessions</span>
                    </div>
                  </div>
                  <div className="analytics-stat-card">
                    <div className="stat-icon hours"><Clock size={24} /></div>
                    <div className="stat-info">
                      <span className="stat-value">{analyticsData.totalHours}h</span>
                      <span className="stat-label">Practice Hours</span>
                    </div>
                  </div>
                  <div className="analytics-stat-card">
                    <div className="stat-icon partners"><Users size={24} /></div>
                    <div className="stat-info">
                      <span className="stat-value">{analyticsData.uniquePartners}</span>
                      <span className="stat-label">Unique Partners</span>
                    </div>
                  </div>
                  <div className="analytics-stat-card">
                    <div className="stat-icon streak"><Zap size={24} /></div>
                    <div className="stat-info">
                      <span className="stat-value">{analyticsData.currentStreak}</span>
                      <span className="stat-label">Day Streak</span>
                    </div>
                  </div>
                </div>
                
                {/* Sessions Over Time Chart */}
                <div className="analytics-chart-card">
                  <h3><TrendingUp size={20} /> Sessions Over Time</h3>
                  <p className="chart-subtitle">Last 12 weeks</p>
                  <div className="bar-chart">
                    {analyticsData.sessionsOverTime.map((week, idx) => (
                      <div key={idx} className="bar-item">
                        <div className="bar-container">
                          <div 
                            className="bar-fill" 
                            style={{ 
                              height: `${Math.max(4, (week.sessions / Math.max(...analyticsData.sessionsOverTime.map(w => w.sessions), 1)) * 100)}%` 
                            }}
                          >
                            {week.sessions > 0 && <span className="bar-value">{week.sessions}</span>}
                          </div>
                        </div>
                        <span className="bar-label">{week.week}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Two Column Layout */}
                <div className="analytics-two-col">
                  {/* Practice Partners */}
                  <div className="analytics-chart-card">
                    <h3><Users size={20} /> Top Practice Partners</h3>
                    {analyticsData.partners.length > 0 ? (
                      <div className="partners-list">
                        {analyticsData.partners.map((partner, idx) => (
                          <div key={idx} className="partner-row">
                            <span className="partner-rank">#{idx + 1}</span>
                            <span className="partner-name">{partner.name}</span>
                            <span className="partner-sessions">{partner.sessions} sessions</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-chart">
                        <Users size={32} />
                        <p>Complete sessions to see your partners</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Day Distribution */}
                  <div className="analytics-chart-card">
                    <h3><Calendar size={20} /> Practice by Day</h3>
                    <div className="day-distribution">
                      {analyticsData.dayDistribution.map((day, idx) => (
                        <div key={idx} className="day-bar-item">
                          <span className="day-label">{day.day}</span>
                          <div className="day-bar-container">
                            <div 
                              className="day-bar-fill"
                              style={{ 
                                width: `${Math.max(4, (day.count / Math.max(...analyticsData.dayDistribution.map(d => d.count), 1)) * 100)}%` 
                              }}
                            />
                          </div>
                          <span className="day-count">{day.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Peak Times Heatmap */}
                <div className="analytics-chart-card">
                  <h3><Clock size={20} /> Peak Practice Times</h3>
                  <p className="chart-subtitle">Your most active hours</p>
                  <div className="time-heatmap">
                    {analyticsData.peakTimes.filter(t => t.hour >= 6 && t.hour <= 22).map((time, idx) => {
                      const maxCount = Math.max(...analyticsData.peakTimes.map(t => t.count), 1);
                      const intensity = time.count / maxCount;
                      return (
                        <div 
                          key={idx} 
                          className={`heatmap-cell ${time.count > 0 ? 'active' : ''}`}
                          style={{ 
                            backgroundColor: time.count > 0 
                              ? `rgba(99, 102, 241, ${0.2 + intensity * 0.8})` 
                              : 'var(--bg)' 
                          }}
                          title={`${time.label}: ${time.count} sessions`}
                        >
                          <span className="heatmap-hour">{time.label}</span>
                          {time.count > 0 && <span className="heatmap-count">{time.count}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="analytics-empty">
                <div className="empty-icon"><BarChart3 size={48} /></div>
                <h3>No Data Yet</h3>
                <p>Complete your first practice session to start tracking your progress!</p>
                <button className="btn-primary" onClick={() => setView('calendar')}>
                  <Calendar size={16} /> Set Your Availability
                </button>
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        {view === 'messages' && (
          <div className="messages-view">
            <div className="conversations-sidebar">
              <h3>Conversations</h3>
              <div className="conversations-list">
                {conversations.length === 0 ? (
                  <div className="no-conversations"><span>💬</span><p>No conversations yet</p></div>
                ) : [...conversations].sort((a, b) => {
                  // Sort by lastMessageAt descending (most recent first)
                  const aTime = a.lastMessageAt?.toDate?.() || a.lastMessageAt || new Date(0);
                  const bTime = b.lastMessageAt?.toDate?.() || b.lastMessageAt || new Date(0);
                  return bTime - aTime;
                }).map(conv => {
                  const other = getOtherUser(conv);
                  const unread = unreadCounts[conv.id] || 0;
                  return (
                    <button key={conv.id} onClick={() => setActiveConversation(conv.id)} className={`conv-btn ${activeConversation === conv.id ? 'active' : ''} ${unread > 0 ? 'has-unread' : ''}`}>
                      <span className="conv-avatar">{other?.avatar || '👤'}</span>
                      <div className="conv-info">
                        <span className="conv-name">{other?.name}</span>
                        <span className="conv-last">{conv.lastMessage}</span>
                      </div>
                      {unread > 0 && <span className="unread-badge">{unread}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="chat-area">
              {activeConversation ? (
                <>
                  <div className="chat-header">
                    {(() => {
                      const conv = conversations.find(c => c.id === activeConversation);
                      const otherId = conv?.participants.find(p => p !== userProfile.id);
                      const otherFromUsers = users.find(u => u.id === otherId);
                      const otherFromConv = conv?.participantDetails?.[otherId];
                      // Prefer real-time user data over stored participantDetails
                      const other = otherFromUsers || otherFromConv;
                      const otherTimezone = otherFromUsers?.timezone || otherFromConv?.timezone;
                      const otherIsAdmin = otherFromUsers?.isAdmin || false;
                      return (<>
                        <span className="chat-avatar">{other?.avatar || '👤'}</span>
                        <div className="chat-user-info">
                          <span className="chat-name">
                            {other?.name}
                            {otherIsAdmin && <span className="admin-badge-small">Admin</span>}
                            <span className="chat-timezone">{getTimezoneAbbr(otherTimezone)}</span>
                          </span>
                        </div>
                        <div className="chat-header-actions">
                          {/* Don't show invite button if other person is admin/teacher */}
                          {!otherIsAdmin && !userProfile.isAdmin && (
                            <button 
                              className="btn-invite" 
                              onClick={() => openInviteModal(other)}
                            >
                              <Calendar size={14} /> Invite
                            </button>
                          )}
                        </div>
                      </>);
                    })()}
                  </div>
                  <div className="chat-messages">
                    {messages.map(msg => {
                      // Check if invite is expired (24 hours)
                      const isExpired = msg.type === 'invite' && msg.inviteData?.status === 'pending' && 
                        msg.timestamp?.toDate && (Date.now() - msg.timestamp.toDate().getTime() > 24 * 60 * 60 * 1000);
                      
                      // For invites, determine which time to show based on who's viewing
                      let inviteDisplayDate, inviteDisplayHour;
                      if (msg.type === 'invite' && msg.inviteData) {
                        if (msg.senderId === userProfile.id) {
                          // I sent it - show my time
                          inviteDisplayDate = msg.inviteData.myDate || msg.inviteData.date;
                          inviteDisplayHour = msg.inviteData.myHour || msg.inviteData.hour;
                        } else {
                          // I received it - show their time (which is my view)
                          inviteDisplayDate = msg.inviteData.theirDate || msg.inviteData.date;
                          inviteDisplayHour = msg.inviteData.theirHour || msg.inviteData.hour;
                        }
                      }
                      
                      return (
                        <div key={msg.id} className={`message ${msg.senderId === userProfile.id ? 'sent' : msg.senderId === 'system' ? 'system' : 'received'}`}>
                          <div className="message-bubble">
                            {msg.type === 'invite' ? (
                              <div className="invite-message">
                                <div className="invite-message-header"><Calendar size={14} /> Meeting Invite</div>
                                <div className="invite-message-time">
                                  {inviteDisplayDate} at {formatHour(inviteDisplayHour)}
                                  <span className="invite-tz-note">({getTimezoneAbbr(userProfile.timezone)})</span>
                                </div>
                                {msg.inviteData?.status === 'cancelled' ? (
                                  <div className="invite-status cancelled"><XCircle size={12} /> Cancelled</div>
                                ) : isExpired ? (
                                  <div className="invite-status expired"><Clock size={12} /> Expired</div>
                                ) : msg.inviteData?.status === 'pending' && msg.senderId !== userProfile.id ? (
                                  <div className="invite-message-actions">
                                    <button className="btn-accept" onClick={() => acceptInvite(msg.id, msg.inviteData)}>
                                      <Check size={14} /> Accept
                                    </button>
                                    <button className="btn-decline" onClick={() => declineInvite(msg.id)}>
                                      <X size={14} /> Decline
                                    </button>
                                    <button className="btn-check-calendar" onClick={() => navigateToCalendarDate(msg.inviteData?.theirDate || msg.inviteData?.date)}>
                                      <Calendar size={14} /> Calendar
                                    </button>
                                  </div>
                                ) : msg.inviteData?.status === 'pending' && msg.senderId === userProfile.id ? (
                                  <div className="invite-message-actions">
                                    <div className="invite-status pending-status"><Loader size={12} className="spin" /> Awaiting response...</div>
                                    <button className="btn-cancel-invite" onClick={() => cancelInvite(msg.id)}>
                                      <X size={14} /> Cancel
                                    </button>
                                  </div>
                                ) : msg.inviteData?.status === 'accepted' ? (
                                  <div className="invite-status accepted"><Check size={12} /> Accepted</div>
                                ) : msg.inviteData?.status === 'declined' ? (
                                  <div className="invite-status declined"><X size={12} /> Declined</div>
                                ) : msg.inviteData?.status === 'cancelled' ? (
                                  <div className="invite-status cancelled"><XCircle size={12} /> Cancelled</div>
                                ) : (
                                  <div className="invite-status" style={{background: 'rgba(123, 97, 255, 0.1)', color: '#7b61ff'}}>Awaiting response...</div>
                                )}
                              </div>
                            ) : msg.meetingLink ? (
                              <div className="meeting-link-message">
                                <p><Video size={16} /> Zoom Meeting</p>
                                <a href={msg.meetingLink} target="_blank" rel="noopener noreferrer" className="meeting-link-btn">
                                  Join Call
                                </a>
                              </div>
                            ) : (
                              <p>{msg.text}</p>
                            )}
                            {msg.timestamp && <span className="message-time">{msg.timestamp.toDate?.().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: userProfile.timezone || 'America/New_York' })}</span>}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                    {otherUserTyping && (
                      <div className="typing-indicator">
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                        <span className="typing-text">typing...</span>
                      </div>
                    )}
                  </div>
                  <div className="chat-input">
                    <input 
                      value={newMessage} 
                      onChange={(e) => handleTyping(e.target.value)} 
                      onKeyDown={(e) => e.key === 'Enter' && sendMessage()} 
                      placeholder="Type a message..." 
                    />
                    <button onClick={sendMessage} className="btn-primary">Send</button>
                  </div>
                </>
              ) : (
                <div className="no-chat"><span>👈</span><p>Select a conversation</p></div>
              )}
            </div>
          </div>
        )}

        {/* Profile */}
        {view === 'profile' && (
          <div className="profile-view">
            <div className="page-header">
              <h1>Your Profile</h1>
              {userProfile.isAdmin && <span className="admin-profile-badge">👑 Administrator</span>}
            </div>
            <div className="card profile-card">
              <div className="profile-avatar-section">
                <div className="profile-avatar">{userProfile.avatar}</div>
                <button onClick={() => setShowAvatarPicker(true)} className="change-avatar-btn">
                  🎨 Change Icon
                </button>
              </div>
              <div className="profile-info">
                <h2 className="profile-name">{userProfile.name}</h2>
                <p className="profile-email">{userProfile.email}</p>
                <p className="profile-member">Member since {formatMemberSince(userProfile.createdAt)}</p>
                
                {/* Profile Stats - Hidden for admins */}
                {!userProfile.isAdmin && (
                  <div className="profile-stats">
                    <div className="stat-item">
                      <span className="stat-value">{getCompletedCallsCount()}</span>
                      <span className="stat-label">Calls Completed</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{getUpcomingCalls().length}</span>
                      <span className="stat-label">Upcoming</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{Object.values(userProfile.availability || {}).flat().length}</span>
                      <span className="stat-label">Available Slots</span>
                    </div>
                  </div>
                )}
                
                {editingProfile ? (
                  <div className="profile-edit-section">
                    <h3>Edit Profile</h3>
                    {/* Bio - Hidden for admins */}
                    {!userProfile.isAdmin && (
                      <div className="form-group">
                        <label>About Me</label>
                        <textarea value={profileBio} onChange={(e) => setProfileBio(e.target.value)} placeholder="Tell others about yourself..." rows={4} />
                      </div>
                    )}
                    <div className="form-group timezone-selector">
                      <label>🌍 Select Your Country & Timezone</label>
                      <div className="country-timezone-picker">
                        {/* Search box */}
                        <div className="country-search">
                          <input
                            type="text"
                            placeholder="🔍 Search country or city..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                          />
                        </div>
                        
                        {/* Country list */}
                        <div className="country-list">
                          {COUNTRIES
                            .filter(c => 
                              countrySearch === '' || 
                              c.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
                              (c.regions && c.regions.some(r => r.region.toLowerCase().includes(countrySearch.toLowerCase())))
                            )
                            .map(country => {
                              const isSelected = country.timezone === profileTimezone || 
                                (country.regions && country.regions.some(r => r.timezone === profileTimezone));
                              
                              return (
                                <div key={country.country} className={`country-item ${isSelected ? 'selected' : ''}`}>
                                  {country.regions ? (
                                    // Country with multiple timezones
                                    <div className="country-with-regions">
                                      <div className="country-header">
                                        <span className="country-flag">{country.flag}</span>
                                        <span className="country-name">{country.country}</span>
                                      </div>
                                      <div className="country-regions">
                                        {country.regions.map(region => (
                                          <button
                                            key={region.timezone}
                                            className={`region-btn ${profileTimezone === region.timezone ? 'active' : ''}`}
                                            onClick={() => setProfileTimezone(region.timezone)}
                                          >
                                            {region.region}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  ) : (
                                    // Country with single timezone
                                    <button
                                      className={`country-btn ${profileTimezone === country.timezone ? 'active' : ''}`}
                                      onClick={() => setProfileTimezone(country.timezone)}
                                    >
                                      <span className="country-flag">{country.flag}</span>
                                      <span className="country-name">{country.country}</span>
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                        
                        {/* Selected timezone display */}
                        <div className="selected-country-display">
                          <div className="selected-country-info">
                            <span className="selected-label">Your timezone:</span>
                            <span className="selected-value">
                              {(() => {
                                const countryInfo = getCountryFromTimezone(profileTimezone);
                                const tzInfo = TIMEZONES.find(t => t.value === profileTimezone);
                                if (countryInfo) {
                                  return `${countryInfo.flag} ${countryInfo.country}${countryInfo.region ? ` (${countryInfo.region})` : ''} - ${tzInfo?.label?.split('(')[1]?.replace(')', '') || ''}`;
                                }
                                return tzInfo?.label || profileTimezone;
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Weekly Goal - Hidden for admins */}
                    {!userProfile.isAdmin && (
                      <div className="form-group">
                        <label>Weekly Roleplay Goal</label>
                        <input 
                          type="number" 
                          min="1" 
                          max="100"
                          value={profileWeeklyGoal} 
                          onChange={(e) => setProfileWeeklyGoal(e.target.value)} 
                          placeholder="Leave empty for unlimited"
                        />
                        <span className="form-hint">Set a weekly target to track your progress</span>
                      </div>
                    )}
                    {/* Dark mode toggle hidden for now - keeping state for future use */}
                    {false && (
                    <div className="form-group">
                      <label>Appearance</label>
                      <div className="dark-mode-toggle">
                        <button 
                          className={`mode-btn ${!darkMode ? 'active' : ''}`}
                          onClick={async () => {
                            setDarkMode(false);
                            await updateDoc(doc(db, 'users', userProfile.id), { darkMode: false });
                          }}
                        >
                          <Sun size={16} /> Light
                        </button>
                        <button 
                          className={`mode-btn ${darkMode ? 'active' : ''}`}
                          onClick={async () => {
                            setDarkMode(true);
                            await updateDoc(doc(db, 'users', userProfile.id), { darkMode: true });
                          }}
                        >
                          <Moon size={16} /> Dark
                        </button>
                      </div>
                    </div>
                    )}
                    <div className="profile-actions">
                      <button onClick={() => setEditingProfile(false)} className="btn-secondary">Cancel</button>
                      <button onClick={saveProfile} className="btn-primary">Save Changes</button>
                    </div>
                  </div>
                ) : (
                  <div className="profile-display">
                    {/* Bio - Hidden for admins */}
                    {!userProfile.isAdmin && (
                      <div className="profile-section">
                        <h4>About Me</h4>
                        <p className={userProfile.bio ? 'profile-bio' : 'profile-bio-empty'}>
                          {userProfile.bio || 'No bio yet - click Edit to add one!'}
                        </p>
                      </div>
                    )}
                    <div className="profile-section">
                      <h4>Timezone</h4>
                      <p className="profile-bio">{TIMEZONES.find(t => t.value === userProfile.timezone)?.label || userProfile.timezone}</p>
                    </div>
                    {/* Weekly Goal - Hidden for admins */}
                    {!userProfile.isAdmin && (
                      <div className="profile-section">
                        <h4>Weekly Goal</h4>
                        <p className="profile-bio">{userProfile.weeklyGoal ? `${userProfile.weeklyGoal} roleplays per week` : 'Unlimited (no goal set)'}</p>
                      </div>
                    )}
                    <div className="profile-actions-row">
                      <button onClick={() => setEditingProfile(true)} className="btn-secondary"><Edit size={16} /> Edit Profile</button>
                      {/* Reflections Book - Hidden for admins */}
                      {!userProfile.isAdmin && (
                        <button onClick={() => setShowReflectionsBook(true)} className="btn-secondary"><BookOpen size={16} /> My Reflections</button>
                      )}
                    </div>
                    
                    {/* Leave Community - Students only */}
                    {!userProfile.isAdmin && userProfile.communityId && (
                      <div className="danger-zone">
                        <h4><AlertCircle size={16} /> Danger Zone</h4>
                        <p>Leaving the community will remove all your availability and session history.</p>
                        <button 
                          onClick={() => setShowLeaveCommunityModal(true)} 
                          className="btn-danger-outline"
                        >
                          <LogOut size={16} /> Leave {userProfile.communityName || 'Community'}
                        </button>
                      </div>
                    )}
                    
                    {/* Delete Community - Admins only */}
                    {userProfile.isAdmin && communityInfo && (
                      <div className="danger-zone">
                        <h4><AlertCircle size={16} /> Danger Zone</h4>
                        <p>Deleting the community will remove all members and permanently delete all data.</p>
                        <button 
                          onClick={() => setShowDeleteCommunityModal(true)} 
                          className="btn-danger-outline"
                        >
                          <Trash2 size={16} /> Delete {communityInfo.name || 'Community'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Time Selection Modal */}
      {showTimeModal && selectedDate && (
        <div className="modal-overlay" onClick={saveAvailability}>
          <div className="modal time-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={cancelAvailability}>
              <X size={20} />
            </button>
            <div className="time-modal-header">
              <Calendar size={24} className="time-modal-icon" />
              <div className="time-modal-date">
                <span className="date-weekday">{selectedDate.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase()}</span>
                <span className="date-full">{selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
            </div>
            <p className="modal-instruction">Click or drag to select hours</p>
            {(() => {
              const dateKey = formatDateKey(selectedDate);
              const bookedHours = getBookedHoursForDate(dateKey);
              
              // Check if this is today - if so, block past hours
              const isToday = formatDateKey(new Date()) === dateKey;
              const currentHour = new Date().getHours();
              
              // Check if hour is in the past (for today only)
              const isHourPast = (hour) => {
                if (!isToday) return false;
                const hourNum = parseInt(hour.split(':')[0]);
                return hourNum <= currentHour; // Block current hour and past
              };
              
              // Handle drag start
              const handleDragStart = (hour, isBlocked) => {
                if (isBlocked) return;
                const isCurrentlySelected = tempAvailability.includes(hour);
                setIsDragging(true);
                setDragStart(hour);
                setDragMode(isCurrentlySelected ? 'deselect' : 'select');
                // Toggle immediately on click
                toggleHourAvailability(hour);
              };
              
              // Handle drag enter (hovering while dragging)
              const handleDragEnter = (hour, isBlocked) => {
                if (!isDragging || isBlocked) return;
                const isCurrentlySelected = tempAvailability.includes(hour);
                if (dragMode === 'select' && !isCurrentlySelected) {
                  setTempAvailability(prev => [...prev, hour]);
                } else if (dragMode === 'deselect' && isCurrentlySelected) {
                  setTempAvailability(prev => prev.filter(h => h !== hour));
                }
              };
              
              // Handle drag end
              const handleDragEnd = () => {
                setIsDragging(false);
                setDragStart(null);
                setDragMode(null);
              };
              
              return (
            <div 
              className="time-slots-grid"
              onMouseLeave={handleDragEnd}
              onMouseUp={handleDragEnd}
            >
              <div className="time-period">
                <span className="period-label">Morning</span>
                <div className="period-slots">
                  {HOURS.filter(h => parseInt(h) >= 6 && parseInt(h) < 12).map(hour => {
                    const isSelected = tempAvailability.includes(hour);
                    const isBooked = bookedHours.includes(hour);
                    const isPast = isHourPast(hour);
                    const isBlocked = isBooked || isPast;
                    return (
                      <button 
                        key={hour} 
                        onMouseDown={() => handleDragStart(hour, isBlocked)}
                        onMouseEnter={() => handleDragEnter(hour, isBlocked)}
                        className={`time-slot-btn ${isSelected ? 'active' : ''} ${isBlocked ? 'booked' : ''} ${isDragging ? 'dragging' : ''}`}
                        disabled={isBlocked}
                        title={isPast ? 'This time has passed' : isBooked ? 'You have a booking at this time' : ''}
                      >
                        {isBlocked ? <Lock size={14} /> : isSelected ? <Check size={14} /> : null}
                        {formatHour(hour)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="time-period">
                <span className="period-label">Afternoon</span>
                <div className="period-slots">
                  {HOURS.filter(h => parseInt(h) >= 12 && parseInt(h) < 18).map(hour => {
                    const isSelected = tempAvailability.includes(hour);
                    const isBooked = bookedHours.includes(hour);
                    const isPast = isHourPast(hour);
                    const isBlocked = isBooked || isPast;
                    return (
                      <button 
                        key={hour} 
                        onMouseDown={() => handleDragStart(hour, isBlocked)}
                        onMouseEnter={() => handleDragEnter(hour, isBlocked)}
                        className={`time-slot-btn ${isSelected ? 'active' : ''} ${isBlocked ? 'booked' : ''} ${isDragging ? 'dragging' : ''}`}
                        disabled={isBlocked}
                        title={isPast ? 'This time has passed' : isBooked ? 'You have a booking at this time' : ''}
                      >
                        {isBlocked ? <Lock size={14} /> : isSelected ? <Check size={14} /> : null}
                        {formatHour(hour)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="time-period">
                <span className="period-label">Evening</span>
                <div className="period-slots">
                  {HOURS.filter(h => parseInt(h) >= 18 || parseInt(h) < 6).map(hour => {
                    const isSelected = tempAvailability.includes(hour);
                    const isBooked = bookedHours.includes(hour);
                    const isPast = isHourPast(hour);
                    const isBlocked = isBooked || isPast;
                    return (
                      <button 
                        key={hour} 
                        onMouseDown={() => handleDragStart(hour, isBlocked)}
                        onMouseEnter={() => handleDragEnter(hour, isBlocked)}
                        className={`time-slot-btn ${isSelected ? 'active' : ''} ${isBlocked ? 'booked' : ''} ${isDragging ? 'dragging' : ''}`}
                        disabled={isBlocked}
                        title={isPast ? 'This time has passed' : isBooked ? 'You have a booking at this time' : ''}
                      >
                        {isBlocked ? <Lock size={14} /> : isSelected ? <Check size={14} /> : null}
                        {formatHour(hour)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
              );
            })()}
            <div className="time-modal-footer">
              <div className="selected-summary">
                <Clock size={16} />
                <span><strong>{tempAvailability.length}</strong> hours selected</span>
              </div>
              <div className="modal-actions">
                <button onClick={cancelAvailability} className="btn-secondary">Cancel</button>
                <button onClick={saveAvailability} className="btn-primary">
                  <Check size={16} /> Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Match Modal */}
      {showMatchModal && (
        <div className="modal-overlay" onClick={() => setShowMatchModal(false)}>
          <div className="modal match-modal" onClick={(e) => e.stopPropagation()}>
            {matchResult ? (
              <>
                <div className="modal-header">
                  <CheckCircle size={48} className="match-success-icon" />
                  <h2>Match Found!</h2>
                </div>
                <div className="match-profile">
                  <div className="match-avatar">{matchResult.user.avatar}</div>
                  <h3>{matchResult.user.name}</h3>
                  {matchResult.user.bio && <p className="match-bio">{matchResult.user.bio}</p>}
                  <p className="match-member">Member since {formatMemberSince(matchResult.user.createdAt)}</p>
                </div>
                <div className="match-time-info">
                  <div className="match-time-item">
                    <Calendar size={18} />
                    <span>{new Date(matchResult.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div className="match-time-item">
                    <Clock size={18} />
                    <span>{formatHour(matchResult.hour)} (your time)</span>
                  </div>
                </div>
                <div className="modal-actions">
                  <button onClick={() => setShowMatchModal(false)} className="btn-secondary">Cancel</button>
                  <button onClick={bookSession} className="btn-primary">
                    <Calendar size={16} /> Book Session
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-header">
                  <AlertCircle size={48} className="no-match-icon" />
                  <h2>No Match Found</h2>
                  <p>No one is available at your times yet</p>
                </div>
                <div className="no-match-tips">
                  <p><strong>Tips to find a match:</strong></p>
                  <ul>
                    <li><Plus size={14} /> Add more available slots</li>
                    <li><Calendar size={14} /> Try different dates</li>
                    <li><RefreshCw size={14} /> Check back later</li>
                  </ul>
                </div>
                <div className="modal-actions">
                  <button onClick={() => { setShowMatchModal(false); setView('calendar'); }} className="btn-primary">
                    <Calendar size={16} /> Update Calendar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Avatar Picker Modal */}
      {showAvatarPicker && (
        <div className="modal-overlay" onClick={() => setShowAvatarPicker(false)}>
          <div className="modal avatar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-icon">🎨</span>
              <h2>Choose Your Icon</h2>
              <p>Pick an emoji to represent you</p>
            </div>
            <div className="modal-body">
              <div className="avatar-grid">
                {AVATAR_OPTIONS.map((avatar, i) => (
                  <button
                    key={i}
                    onClick={() => selectAvatar(avatar)}
                    className={`avatar-option ${userProfile.avatar === avatar ? 'active' : ''}`}
                  >
                    {avatar}
                  </button>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowAvatarPicker(false)} className="btn-secondary">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal - View Other User's Calendar */}
      {showInviteModal && inviteUser && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal invite-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="invite-modal-header">
              <span className="invite-modal-avatar">{inviteUser.avatar || ''}</span>
              <div className="invite-modal-info">
                <h3>Invite {inviteUser.name}</h3>
                <p>Times shown in YOUR timezone ({getTimezoneAbbr(userProfile.timezone)})</p>
              </div>
            </div>

            <div className="invite-calendar-nav">
              <button 
                className="cal-nav-btn" 
                onClick={() => setInviteWeekStart(new Date(inviteWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000))}
              >
                ←
              </button>
              <h4>
                {inviteWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - 
                {new Date(inviteWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </h4>
              <button 
                className="cal-nav-btn" 
                onClick={() => setInviteWeekStart(new Date(inviteWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000))}
              >
                →
              </button>
            </div>

            <div className="invite-weekly-calendar">
              {getInviteWeekDays().map((day, i) => {
                const dateKey = day.toISOString().split('T')[0];
                const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));
                
                // Convert their availability to MY timezone
                const theirTz = inviteUser.timezone || 'America/New_York';
                const myTz = userProfile.timezone || 'America/New_York';
                const convertedSlots = [];
                
                // Go through all their available dates and hours
                if (inviteUser.availability) {
                  for (const [theirDate, theirHours] of Object.entries(inviteUser.availability)) {
                    for (const theirHour of theirHours) {
                      // Convert from their timezone to my timezone
                      const converted = convertTimezone(theirDate, theirHour, theirTz, myTz);
                      if (converted.date === dateKey) {
                        convertedSlots.push({
                          myHour: converted.hour,
                          theirDate: theirDate,
                          theirHour: theirHour
                        });
                      }
                    }
                  }
                }
                
                // Sort by hour
                convertedSlots.sort((a, b) => a.myHour - b.myHour);
                
                return (
                  <div key={i} className={`invite-day ${isPast ? 'past' : ''}`}>
                    <span className="invite-day-name">{day.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                    <span className="invite-day-date">{day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    <div className="invite-day-slots">
                      {!isPast && convertedSlots.length > 0 ? (
                        convertedSlots.map((slot, idx) => (
                          <button
                            key={idx}
                            className={`invite-slot-btn ${selectedInviteSlot?.myDate === dateKey && selectedInviteSlot?.myHour === slot.myHour ? 'selected' : ''}`}
                            onClick={() => setSelectedInviteSlot({ 
                              myDate: dateKey, 
                              myHour: slot.myHour,
                              theirDate: slot.theirDate,
                              theirHour: slot.theirHour
                            })}
                          >
                            {formatHour(slot.myHour)}
                          </button>
                        ))
                      ) : !isPast ? (
                        <span className="invite-no-slots">No slots</span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedInviteSlot && (
              <div className="invite-selected-slot">
                <p><Calendar size={14} /> <strong>{new Date(selectedInviteSlot.myDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</strong> at <strong>{formatHour(selectedInviteSlot.myHour)}</strong> (your time)</p>
              </div>
            )}

            <div className="invite-modal-actions">
              <button onClick={() => setShowInviteModal(false)} className="btn-secondary">Cancel</button>
              <button 
                onClick={sendInvite} 
                className="btn-primary"
                disabled={!selectedInviteSlot}
              >
                📨 Send Invite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Roleplay Now Modal */}
      {showRoleplayNowModal && pendingRoleplayRequest && (
        <div className="modal-overlay" onClick={declineRoleplayNow}>
          <div className="modal roleplay-modal" onClick={(e) => e.stopPropagation()}>
            <div className="roleplay-modal-header">
              <span className="roleplay-pulse"><Zap size={20} /></span>
              <h2>Roleplay Now!</h2>
            </div>
            <div className="roleplay-modal-body">
              <div className="roleplay-requester">
                <span className="requester-avatar">{pendingRoleplayRequest.senderAvatar}</span>
                <span className="requester-name">{pendingRoleplayRequest.senderName}</span>
              </div>
              <p>wants to start a roleplay session right now!</p>
              <div className="roleplay-timer">
                <span><Clock size={14} /> Request expires soon</span>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={declineRoleplayNow} className="btn-secondary">Not Now</button>
              <button onClick={() => acceptRoleplayNow(pendingRoleplayRequest)} className="btn-primary">
                <Zap size={18} /> Join Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Availability Modal */}
      {showResetModal && (
        <div className="modal-overlay" onClick={closeResetModal}>
          <div className="modal reset-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-icon-wrapper warning">
                {resetConfirmStep === 1 ? <AlertCircle size={32} /> : <Trash2 size={32} />}
              </span>
              <h2>{resetConfirmStep === 1 ? 'Reset All Availability?' : 'Are you absolutely sure?'}</h2>
              <p>
                {resetConfirmStep === 1 
                  ? 'This will remove all your available time slots from the calendar.'
                  : 'This action cannot be undone. All your availability will be permanently deleted.'}
              </p>
            </div>
            <div className="reset-info">
              <p>You currently have <strong>{Object.values(userProfile.availability || {}).flat().length}</strong> time slots across <strong>{Object.keys(userProfile.availability || {}).length}</strong> days.</p>
            </div>
            <div className="modal-actions">
              <button onClick={closeResetModal} className="btn-secondary">Cancel</button>
              <button onClick={resetAllAvailability} className="btn-danger">
                <Trash2 size={16} /> {resetConfirmStep === 1 ? 'Yes, Reset All' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Community Modal - For Students */}
      {showLeaveCommunityModal && (
        <div className="modal-overlay" onClick={() => { setShowLeaveCommunityModal(false); setLeaveConfirmStep(1); }}>
          <div className="modal danger-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-icon-wrapper danger">
                {leaveConfirmStep === 1 ? <LogOut size={32} /> : <AlertCircle size={32} />}
              </span>
              <h2>{leaveConfirmStep === 1 ? `Leave ${userProfile.communityName}?` : 'Are you absolutely sure?'}</h2>
              <p>
                {leaveConfirmStep === 1 
                  ? 'You will lose access to this classroom and all your scheduled sessions.'
                  : 'This action cannot be undone. You will need a new classroom code to rejoin.'}
              </p>
            </div>
            <div className="danger-info">
              <ul>
                <li>Your availability will be cleared</li>
                <li>Your scheduled sessions will be cancelled</li>
                <li>You will need a new code to rejoin</li>
              </ul>
            </div>
            <div className="modal-actions">
              <button onClick={() => { setShowLeaveCommunityModal(false); setLeaveConfirmStep(1); }} className="btn-secondary">Cancel</button>
              {leaveConfirmStep === 1 ? (
                <button onClick={() => setLeaveConfirmStep(2)} className="btn-danger">
                  Yes, I want to leave
                </button>
              ) : (
                <button onClick={leaveCommunity} className="btn-danger">
                  <LogOut size={16} /> Confirm Leave
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Community Modal - For Admins (3 step confirmation) */}
      {showDeleteCommunityModal && (
        <div className="modal-overlay" onClick={() => { setShowDeleteCommunityModal(false); setDeleteConfirmStep(1); setDeleteConfirmText(''); }}>
          <div className="modal danger-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-icon-wrapper danger">
                <Trash2 size={32} />
              </span>
              <h2>
                {deleteConfirmStep === 1 && `Delete ${communityInfo?.name}?`}
                {deleteConfirmStep === 2 && 'This will affect all members'}
                {deleteConfirmStep === 3 && 'Final Confirmation'}
              </h2>
              <p>
                {deleteConfirmStep === 1 && 'This will permanently delete your entire community and remove all members.'}
                {deleteConfirmStep === 2 && `${communityMembers.length} member(s) will lose access to this classroom immediately.`}
                {deleteConfirmStep === 3 && 'Type the community name to confirm deletion.'}
              </p>
            </div>
            
            {deleteConfirmStep === 3 && (
              <div className="delete-confirm-input">
                <label>Type <strong>{communityInfo?.name}</strong> to confirm:</label>
                <input 
                  type="text" 
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={communityInfo?.name}
                />
              </div>
            )}
            
            <div className="danger-info">
              {deleteConfirmStep < 3 && (
                <ul>
                  <li>All member accounts will be disconnected</li>
                  <li>All scheduled sessions will be cancelled</li>
                  <li>All data will be permanently deleted</li>
                  <li>This action CANNOT be undone</li>
                </ul>
              )}
            </div>
            
            <div className="modal-actions">
              <button onClick={() => { setShowDeleteCommunityModal(false); setDeleteConfirmStep(1); setDeleteConfirmText(''); }} className="btn-secondary">Cancel</button>
              {deleteConfirmStep === 1 && (
                <button onClick={() => setDeleteConfirmStep(2)} className="btn-danger">
                  I understand, continue
                </button>
              )}
              {deleteConfirmStep === 2 && (
                <button onClick={() => setDeleteConfirmStep(3)} className="btn-danger">
                  Yes, delete everything
                </button>
              )}
              {deleteConfirmStep === 3 && (
                <button 
                  onClick={deleteCommunity} 
                  className="btn-danger"
                  disabled={deleteConfirmText !== communityInfo?.name}
                >
                  <Trash2 size={16} /> Permanently Delete Community
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Post-Call Feedback Modal - Simplified to 2 steps */}
      {showFeedbackModal && feedbackBooking && (
        <div className="modal-overlay">
          <div className="modal feedback-modal" onClick={(e) => e.stopPropagation()}>
            {feedbackStep === 1 ? (
              <>
                <div className="feedback-header">
                  <span className="feedback-icon"><CheckCircle size={32} /></span>
                  <h2>Session Complete!</h2>
                  <p>How did your roleplay with <strong>{
                    feedbackBooking.participantNames?.[feedbackBooking.participants.find(p => p !== userProfile.id)] || 
                    users.find(u => u.id === feedbackBooking.participants.find(p => p !== userProfile.id))?.name || 
                    'your partner'
                  }</strong> go?</p>
                </div>
                <div className="feedback-ratings">
                  <button 
                    className="rating-btn okay" 
                    onClick={() => submitFeedbackRating('okay')}
                    title="It was okay"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="8" y1="15" x2="16" y2="15"/>
                      <line x1="9" y1="9" x2="9.01" y2="9"/>
                      <line x1="15" y1="9" x2="15.01" y2="9"/>
                    </svg>
                    <span>Okay</span>
                  </button>
                  <button 
                    className="rating-btn great" 
                    onClick={() => submitFeedbackRating('great')}
                    title="Great session!"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                      <line x1="9" y1="9" x2="9.01" y2="9"/>
                      <line x1="15" y1="9" x2="15.01" y2="9"/>
                    </svg>
                    <span>Great!</span>
                  </button>
                </div>
                <button onClick={skipFeedback} className="skip-btn">Skip</button>
              </>
            ) : (
              <>
                <div className="feedback-header">
                  <span className="feedback-icon"><FileText size={32} /></span>
                  <h2>Quick Reflection</h2>
                  <div className="feedback-selected-rating">
                    {feedbackRating === 'great' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                        <line x1="9" y1="9" x2="9.01" y2="9"/>
                        <line x1="15" y1="9" x2="15.01" y2="9"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="8" y1="15" x2="16" y2="15"/>
                        <line x1="9" y1="9" x2="9.01" y2="9"/>
                        <line x1="15" y1="9" x2="15.01" y2="9"/>
                      </svg>
                    )}
                  </div>
                </div>
                
                {/* Combined Reflection + Question in one view */}
                <div className="feedback-combined">
                  <div className="feedback-section">
                    <label>Your notes (private)</label>
                    <textarea
                      value={feedbackReflection}
                      onChange={(e) => setFeedbackReflection(e.target.value)}
                      placeholder="What went well? What could be improved?"
                      rows={3}
                      disabled={isGeneratingTips}
                    />
                  </div>
                  
                  <div className="feedback-section">
                    <label>Question for instructor (anonymous, optional)</label>
                    <textarea
                      value={feedbackAdminQuestion}
                      onChange={(e) => setFeedbackAdminQuestion(e.target.value)}
                      placeholder="Any questions about techniques, scenarios, etc?"
                      rows={2}
                    />
                    <span className="anonymous-badge">🔒 Anonymous</span>
                  </div>
                </div>
                
                {isGeneratingTips ? (
                  <div className="generating-tips">
                    <div className="spinner"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  <div className="modal-actions">
                    <button onClick={skipFeedback} className="btn-secondary">
                      Skip
                    </button>
                    <button onClick={submitCombinedFeedback} className="btn-primary">
                      {feedbackReflection.trim() ? 'Save Reflection' : 'Done'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Weekly AI Coach Modal */}
      {showWeeklyCoachModal && weeklyCoachInsight && (
        <div className="modal-overlay" onClick={closeWeeklyCoachModal}>
          <div className="modal weekly-coach-modal" onClick={(e) => e.stopPropagation()}>
            <div className="weekly-coach-header">
              <span className="coach-icon">🤖</span>
              <h2>Your Weekly Coach</h2>
              <p>Based on this week's practice sessions</p>
            </div>
            <div className="weekly-coach-content">
              {weeklyCoachInsight.split('\n').filter(line => line.trim()).map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
            <div className="weekly-coach-footer">
              <p className="coach-note">🗓️ Weekly insights are generated every Friday based on your reflections</p>
              <button onClick={closeWeeklyCoachModal} className="btn-primary">
                Got it! →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reflections Book Modal */}
      {showReflectionsBook && (
        <div className="modal-overlay" onClick={() => setShowReflectionsBook(false)}>
          <div className="modal reflections-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-icon">📖</span>
              <h2>My Reflections Book</h2>
              <p>Your personal roleplay journal</p>
            </div>
            <div className="modal-body reflections-list">
              {reflections.length === 0 ? (
                <div className="empty-reflections">
                  <FileText size={16} />
                  <p>No reflections yet</p>
                  <p className="empty-hint">Complete a roleplay session to add your first reflection!</p>
                </div>
              ) : (
                reflections.map(ref => (
                  <div key={ref.id} className="reflection-item">
                    <div className="reflection-header">
                      <span className="reflection-avatar">{ref.partnerAvatar || '👤'}</span>
                      <div className="reflection-meta">
                        <span className="reflection-partner">{ref.partnerName}</span>
                        <span className="reflection-date">
                          {ref.createdAt?.toDate?.()?.toLocaleDateString('en-US', { 
                            month: 'short', day: 'numeric', year: 'numeric' 
                          }) || 'Recently'}
                        </span>
                      </div>
                      <span className="reflection-rating">
                        {ref.rating === 'great' || ref.rating === 'happy' ? <ThumbsUp size={18} className="rating-great" /> : <Meh size={18} className="rating-okay" />}
                      </span>
                    </div>
                    <p className="reflection-text">{ref.reflection}</p>
                  </div>
                ))
              )}
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowReflectionsBook(false)} className="btn-primary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Event Popup Modal */}
      {showEventPopup && selectedEvent && (
        <div className="modal-overlay" onClick={() => setShowEventPopup(false)}>
          <div className="modal event-popup" onClick={(e) => e.stopPropagation()}>
            <div className="event-popup-header">
              <div className="event-popup-actions">
                <button className="event-action-btn" onClick={() => goToBookingChat(selectedEvent)} title="Message">✉️</button>
                <button className="event-action-btn" title="More">⋮</button>
                <button className="event-action-btn" onClick={() => setShowEventPopup(false)} title="Close">✕</button>
              </div>
            </div>
            
            <div className="event-popup-content">
              <div className="event-popup-title-row">
                <span className="event-dot" style={{ backgroundColor: '#f472b6' }}></span>
                <h2>Roleplay Session</h2>
              </div>
              
              <div className="event-popup-datetime">
                {(() => {
                  const dateObj = new Date(selectedEvent.dateTime || selectedEvent.date);
                  const hour = parseInt(selectedEvent.dateTime?.split('T')[1]?.split(':')[0] || 0);
                  return (
                    <>
                      {dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                      {' • '}
                      {formatHour(hour)} - {formatHour(hour + 1)}
                    </>
                  );
                })()}
              </div>
              
              <div className="event-popup-details">
                <div className="event-detail-row">
                  <span className="event-detail-icon"><User size={16} /></span>
                  <div className="event-detail-content">
                    <span className="event-detail-label">With</span>
                    <span className="event-detail-value">
                      {(() => {
                        const otherUserId = selectedEvent.participants?.find(p => p !== userProfile.id);
                        const otherUser = selectedEvent.participantDetails?.[otherUserId] || users.find(u => u.id === otherUserId);
                        return `${otherUser?.avatar || ''} ${otherUser?.name || 'Partner'}`;
                      })()}
                    </span>
                  </div>
                </div>
                
                {selectedEvent.meetingLink && (
                  <>
                    <div className="event-detail-row">
                      <span className="event-detail-icon"><Video size={16} /></span>
                      <div className="event-detail-content">
                        <a href={selectedEvent.meetingLink} target="_blank" rel="noopener noreferrer" className="event-zoom-link">
                          {selectedEvent.meetingLink.substring(0, 40)}...
                        </a>
                      </div>
                    </div>
                    
                    <div className="event-detail-row">
                      <span className="event-detail-icon"><FileText size={16} /></span>
                      <div className="event-detail-content">
                        <span className="event-detail-value">
                          Meeting ID: {selectedEvent.meetingLink.match(/\/j\/(\d+)/)?.[1] || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                
                <div className="event-detail-row">
                  <span className="event-detail-icon"><Calendar size={16} /></span>
                  <div className="event-detail-content">
                    <span className="event-detail-label">MeetSync - Roleplay</span>
                  </div>
                </div>
              </div>
              
              <div className="event-popup-tags">
                <span className="event-tag setting">Setting</span>
                <span className="event-tag closing">Closing</span>
              </div>
              
              <div className="event-popup-footer">
                {selectedEvent.meetingLink && (
                  <a href={selectedEvent.meetingLink} target="_blank" rel="noopener noreferrer" className="btn-primary event-join-btn">
                    🎥 Join Zoom Call
                  </a>
                )}
                <button onClick={() => goToBookingChat(selectedEvent)} className="btn-secondary">
                  💬 Open Chat
                </button>
                {canCancelBooking(selectedEvent) && (
                  <button 
                    onClick={() => {
                      setShowEventPopup(false);
                      openCancelModal(selectedEvent);
                    }} 
                    className="btn-danger-outline"
                    title="Cancel this session"
                  >
                    <X size={16} /> Cancel Session
                  </button>
                )}
              </div>
              
              {/* Show cancellation status if cancelled */}
              {selectedEvent.status === 'cancelled' && (
                <div className="event-cancelled-notice">
                  <AlertCircle size={16} />
                  <span>This session was cancelled</span>
                  {selectedEvent.cancellationReason && (
                    <p className="cancel-reason">Reason: {selectedEvent.cancellationReason}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Booking Modal */}
      {showCancelModal && cancellingBooking && (
        <div className="modal-overlay" onClick={() => !isCancelling && setShowCancelModal(false)}>
          <div className="modal cancel-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="modal-close-btn" 
              onClick={() => !isCancelling && setShowCancelModal(false)}
              disabled={isCancelling}
            >
              <X size={20} />
            </button>
            
            <div className="modal-header">
              <span className="modal-icon-wrapper danger">
                <AlertCircle size={32} />
              </span>
              <h2>Cancel Session?</h2>
              <p>Are you sure you want to cancel this roleplay session?</p>
            </div>
            
            <div className="cancel-booking-details">
              <div className="cancel-partner">
                <span className="cancel-avatar">
                  {cancellingBooking.participantDetails?.[
                    cancellingBooking.participants?.find(p => p !== userProfile.id)
                  ]?.avatar || '👤'}
                </span>
                <div className="cancel-info">
                  <span className="cancel-name">
                    {cancellingBooking.participantDetails?.[
                      cancellingBooking.participants?.find(p => p !== userProfile.id)
                    ]?.name || 'Partner'}
                  </span>
                  <span className="cancel-time">
                    {(() => {
                      const myDetails = cancellingBooking.participantDetails?.[userProfile.id];
                      if (myDetails?.localDate && myDetails?.localHour) {
                        return `${new Date(myDetails.localDate).toLocaleDateString('en-US', { 
                          weekday: 'short', month: 'short', day: 'numeric' 
                        })} at ${formatHour(myDetails.localHour)}`;
                      }
                      return cancellingBooking.dateTime || 'Scheduled';
                    })()}
                  </span>
                </div>
              </div>
              
              <div className="cancel-notice">
                {(() => {
                  const myDetails = cancellingBooking.participantDetails?.[userProfile.id];
                  let meetingTime;
                  if (myDetails?.localDate && myDetails?.localHour) {
                    meetingTime = new Date(`${myDetails.localDate}T${myDetails.localHour}`);
                  } else {
                    meetingTime = new Date(cancellingBooking.dateTime);
                  }
                  const hoursUntil = (meetingTime - new Date()) / (1000 * 60 * 60);
                  
                  if (hoursUntil >= 1) {
                    return (
                      <p className="notice-info">
                        <CheckCircle size={16} />
                        Time slot will be restored to both participants
                      </p>
                    );
                  } else {
                    return (
                      <p className="notice-warning">
                        <AlertCircle size={16} />
                        Less than 1 hour notice - time slot cannot be restored
                      </p>
                    );
                  }
                })()}
              </div>
            </div>
            
            <div className="form-group">
              <label>Reason for cancellation (optional)</label>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="Let your partner know why you're cancelling..."
                rows={2}
                disabled={isCancelling}
              />
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => setShowCancelModal(false)} 
                className="btn-secondary"
                disabled={isCancelling}
              >
                Keep Session
              </button>
              <button 
                onClick={handleCancelBooking} 
                className="btn-danger"
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <><Loader size={16} className="spin" /> Cancelling...</>
                ) : (
                  <>Cancel Session</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Broadcast Notification Modal */}
      {showBroadcastModal && (
        <div className="modal-overlay" onClick={() => !isSendingBroadcast && setShowBroadcastModal(false)}>
          <div className="modal broadcast-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="modal-close-btn" 
              onClick={() => !isSendingBroadcast && setShowBroadcastModal(false)}
              disabled={isSendingBroadcast}
            >
              <X size={20} />
            </button>
            
            <div className="modal-header">
              <span className="modal-icon-wrapper broadcast">
                <Bell size={32} />
              </span>
              <h2>Send Notification</h2>
              <p>This will be sent to all {users.filter(u => !u.isAdmin && u.communityId === userProfile?.communityId).length} students in your classroom</p>
            </div>
            
            <div className="form-group">
              <label>Message</label>
              <textarea
                value={broadcastMessage}
                onChange={e => setBroadcastMessage(e.target.value)}
                placeholder="Write your announcement here..."
                rows={4}
                disabled={isSendingBroadcast}
                maxLength={500}
              />
              <span className="char-count">{broadcastMessage.length}/500</span>
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => setShowBroadcastModal(false)} 
                className="btn-secondary"
                disabled={isSendingBroadcast}
              >
                Cancel
              </button>
              <button 
                onClick={sendBroadcastNotification} 
                className="btn-primary"
                disabled={isSendingBroadcast || !broadcastMessage.trim()}
              >
                {isSendingBroadcast ? (
                  <><Loader size={16} className="spin" /> Sending...</>
                ) : (
                  <>Send to All Students</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Profile Modal (Admin View) */}
      {showStudentProfileModal && selectedStudent && (
        <div className="modal-overlay" onClick={() => setShowStudentProfileModal(false)}>
          <div className="modal student-profile-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setShowStudentProfileModal(false)}>
              <X size={20} />
            </button>
            
            <div className="student-profile-header">
              <div className="student-avatar-large">{selectedStudent.avatar || '👤'}</div>
              <div className="student-info">
                <h2>{selectedStudent.name}</h2>
                <p>{selectedStudent.email}</p>
                <div className="student-meta">
                  <span><Calendar size={14} /> Joined {
                    selectedStudent.createdAt?.toDate 
                      ? selectedStudent.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) 
                      : selectedStudent.createdAt 
                        ? new Date(selectedStudent.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                        : 'Recently'
                  }</span>
                  <span><MapPin size={14} /> {selectedStudent.timezone || 'No timezone set'}</span>
                </div>
              </div>
            </div>
            
            <div className="student-stats-row">
              <div className="student-stat">
                <span className="stat-number">{getMemberStats(selectedStudent)?.totalSessions || 0}</span>
                <span className="stat-label">Total Sessions</span>
              </div>
              <div className="student-stat">
                <span className="stat-number">{Object.values(selectedStudent.availability || {}).flat().length}</span>
                <span className="stat-label">Available Slots</span>
              </div>
              <div className="student-stat">
                <span className="stat-number">
                  {bookings.filter(b => 
                    b.participants?.includes(selectedStudent.id) && 
                    b.status === 'scheduled'
                  ).length}
                </span>
                <span className="stat-label">Upcoming</span>
              </div>
            </div>
            
            <div className="activity-graph-section">
              <h3><BarChart3 size={18} /> Practice Activity (Last 30 Days)</h3>
              <div className="activity-graph">
                {(getStudentActivityData(selectedStudent.id) || []).map((day, idx) => (
                  <div key={idx} className="graph-bar-container" title={`${day.shortDate}: ${day.calls} call${day.calls !== 1 ? 's' : ''}`}>
                    <div 
                      className="graph-bar"
                      style={{ 
                        height: `${Math.max(4, (day.calls || 0) * 25)}px`,
                        backgroundColor: day.calls > 0 ? '#6366f1' : '#e5e7eb'
                      }}
                    ></div>
                    {idx % 5 === 0 && <span className="graph-label">{day.shortDate}</span>}
                  </div>
                ))}
              </div>
              <div className="graph-legend">
                <span>Each bar = 1 day</span>
                <span>Height = # of completed calls</span>
              </div>
            </div>
            
            {selectedStudent.bio && (
              <div className="student-bio-section">
                <h4>About</h4>
                <p>{selectedStudent.bio}</p>
              </div>
            )}
            
            <div className="modal-actions">
              <button onClick={() => { startChatWithMember(selectedStudent); setShowStudentProfileModal(false); }} className="btn-secondary">
                <MessageCircle size={16} /> Message
              </button>
              <button onClick={() => setShowStudentProfileModal(false)} className="btn-primary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className={`toast-notification toast-${toastType}`}>
          <span className="toast-icon">
            {toastType === 'dnd-on' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
            {toastType === 'dnd-off' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            )}
            {toastType === 'success' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            )}
            {toastType === 'default' && '✓'}
          </span>
          <span className="toast-message">{toastMessage}</span>
        </div>
      )}

      {/* Meeting Reminder Popup */}
      {showMeetingReminder && upcomingMeeting && (
        <div className="meeting-reminder-overlay">
          <div className="meeting-reminder-popup">
            <div className="reminder-icon"><Clock size={48} /></div>
            <h3>Meeting Starting Soon!</h3>
            <p className="reminder-text">
              Your meeting with <strong>{upcomingMeeting.otherName}</strong> starts in{' '}
              <span className="reminder-time">{upcomingMeeting.minutesUntil} minutes</span>
            </p>
            {upcomingMeeting.meetingLink ? (
              <a 
                href={upcomingMeeting.meetingLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-join-now"
                onClick={() => setShowMeetingReminder(false)}
              >
                <Video size={18} /> Join Zoom Now
              </a>
            ) : (
              <p className="reminder-no-link">Zoom link will be available when meeting starts</p>
            )}
            <button 
              className="reminder-dismiss"
              onClick={() => setShowMeetingReminder(false)}
            >
              Remind me later
            </button>
          </div>
        </div>
      )}

      {/* Flash-Prep Waiting Room Overlay */}
      {showPrepOverlay && prepPartner && (
        <div className="prep-overlay-pro">
          <div className="prep-bg-effects">
            <div className="prep-gradient-orb orb-1"></div>
            <div className="prep-gradient-orb orb-2"></div>
            <div className="prep-grid-lines"></div>
          </div>
          
          <div className="prep-content-pro">
            <div className="prep-status-bar">
              <div className="status-dot pulse"></div>
              <span>Establishing secure connection...</span>
            </div>
            
            <div className="prep-main">
              <div className="prep-avatar-ring">
                <svg className="avatar-ring-svg" viewBox="0 0 120 120">
                  <circle className="ring-track" cx="60" cy="60" r="54" fill="none" strokeWidth="4"/>
                  <circle className="ring-progress" cx="60" cy="60" r="54" fill="none" strokeWidth="4"/>
                </svg>
                <div className="prep-avatar-inner">
                  <span className="avatar-emoji">{prepPartner.avatar || '👤'}</span>
                </div>
              </div>
              
              <h1 className="prep-partner-name">{prepPartner.name}</h1>
              <span className="prep-partner-label">Your Practice Partner</span>
              
              <div className="prep-countdown-pro">
                {prepCountdown > 0 ? (
                  <>
                    <span className="countdown-label">Launching in</span>
                    <div className="countdown-display">
                      <span className="countdown-num">{prepCountdown}</span>
                    </div>
                  </>
                ) : (
                  <div className="countdown-launch">
                    <Zap size={32} />
                    <span>LAUNCHING</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="prep-footer-tips">
              <div className="tip-card">
                <Mic size={18} />
                <span>Speak with confidence</span>
              </div>
              <div className="tip-card">
                <HelpCircle size={18} />
                <span>Ask open questions</span>
              </div>
              <div className="tip-card">
                <Target size={18} />
                <span>Find 3 pain points</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Overlay - Anti-Double-Click Guard */}
      {showBookingOverlay && bookingOverlayData && (
        <div className="booking-overlay">
          <div className="booking-overlay-card">
            <div className="booking-sync-animation">
              <div className="sync-ring ring-1"></div>
              <div className="sync-ring ring-2"></div>
              <div className="sync-ring ring-3"></div>
              <div className="sync-icon">
                <Loader size={32} className="spin" />
              </div>
            </div>
            
            <h2>Securing Your Session</h2>
            <p className="booking-sync-text">Generating your secure Zoom bridge...</p>
            
            <div className="booking-partner-preview">
              <div className="booking-avatar">{bookingOverlayData.partnerAvatar || ''}</div>
              <div className="booking-partner-details">
                <span className="booking-partner-name">{bookingOverlayData.partnerName}</span>
                <span className="booking-partner-time">
                  <Calendar size={14} /> {new Date(bookingOverlayData.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  <Clock size={14} /> {formatHour(bookingOverlayData.hour)}
                </span>
              </div>
            </div>
            
            <div className="mission-brief">
              <div className="mission-brief-header">
                <Target size={16} />
                <span>TODAY'S MISSION BRIEF</span>
              </div>
              <div className="mission-brief-content">
                <p className="mission-focus">Focus: Handling Price Objections</p>
                <ul className="mission-objectives">
                  <li><Check size={12} /> Acknowledge the concern</li>
                  <li><Check size={12} /> Reframe value over cost</li>
                  <li><Check size={12} /> Ask discovery questions</li>
                </ul>
              </div>
            </div>
            
            <div className="booking-progress-dots">
              <span className="dot active"></span>
              <span className="dot active"></span>
              <span className="dot"></span>
            </div>
          </div>
        </div>
      )}

      {/* Bug Report / Feedback Modal */}
      {showBugReport && (
        <div className="modal-overlay" onClick={() => setShowBugReport(false)}>
          <div className="modal bug-report-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowBugReport(false)}>×</button>
            <div className="modal-header">
              <div className="modal-icon"><AlertCircle size={28} /></div>
              <h2>Send Feedback</h2>
              <p>Help us improve MeetSync</p>
            </div>
            
            {success ? (
              <div className="bug-report-success">
                <CheckCircle size={48} />
                <h3>Thank you!</h3>
                <p>Your feedback helps us make MeetSync better.</p>
                <button className="btn-primary" onClick={() => { setShowBugReport(false); setSuccess(''); }}>
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleBugReportSubmit}>
                <div className="form-group">
                  <label>Type</label>
                  <div className="feedback-type-buttons">
                    <button 
                      type="button"
                      className={`feedback-type-btn ${bugReportForm.type === 'bug' ? 'active' : ''}`}
                      onClick={() => setBugReportForm({...bugReportForm, type: 'bug'})}
                    >
                      <AlertCircle size={18} /> Bug
                    </button>
                    <button 
                      type="button"
                      className={`feedback-type-btn ${bugReportForm.type === 'feature' ? 'active' : ''}`}
                      onClick={() => setBugReportForm({...bugReportForm, type: 'feature'})}
                    >
                      <Sparkles size={18} /> Feature Request
                    </button>
                    <button 
                      type="button"
                      className={`feedback-type-btn ${bugReportForm.type === 'other' ? 'active' : ''}`}
                      onClick={() => setBugReportForm({...bugReportForm, type: 'other'})}
                    >
                      <MessageSquare size={18} /> Other
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    value={bugReportForm.description}
                    onChange={e => setBugReportForm({...bugReportForm, description: e.target.value})}
                    placeholder={bugReportForm.type === 'bug' 
                      ? "What happened? What did you expect to happen?" 
                      : bugReportForm.type === 'feature'
                      ? "What feature would help you? How would you use it?"
                      : "Tell us what's on your mind..."}
                    rows={4}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Email (optional)</label>
                  <input
                    type="email"
                    value={bugReportForm.email}
                    onChange={e => setBugReportForm({...bugReportForm, email: e.target.value})}
                    placeholder={userProfile?.email || "your@email.com"}
                  />
                  <span className="form-hint">We'll only contact you if we need more info</span>
                </div>
                
                <button type="submit" className="btn-primary" disabled={bugReportSubmitting}>
                  {bugReportSubmitting ? <><Loader size={16} className="spin" /> Sending...</> : 'Submit Feedback'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Onboarding Tutorial Overlay */}
      {showOnboarding && (
        <div className="onboarding-overlay">
          <div className="onboarding-modal">
            <button className="onboarding-skip" onClick={() => handleOnboardingAction('skip')}>
              Skip Tutorial
            </button>
            
            <div className="onboarding-progress">
              <div className={`progress-dot ${onboardingStep >= 1 ? 'active' : ''}`} />
              <div className={`progress-dot ${onboardingStep >= 2 ? 'active' : ''}`} />
              <div className={`progress-dot ${onboardingStep >= 3 ? 'active' : ''}`} />
            </div>
            
            {onboardingStep === 1 && (
              <div className="onboarding-step">
                <div className="onboarding-icon">
                  <Calendar size={64} />
                </div>
                <h2>Welcome to MeetSync! 👋</h2>
                <h3>Step 1: Set Your Availability</h3>
                <p>First, tell us when you're free to practice. Click on any day in your calendar and select the hours you're available.</p>
                <div className="onboarding-visual">
                  <div className="mini-calendar">
                    <div className="mini-day available">Mon</div>
                    <div className="mini-day available">Tue</div>
                    <div className="mini-day">Wed</div>
                    <div className="mini-day available">Thu</div>
                    <div className="mini-day">Fri</div>
                  </div>
                </div>
              </div>
            )}
            
            {onboardingStep === 2 && (
              <div className="onboarding-step">
                <div className="onboarding-icon">
                  <Users size={64} />
                </div>
                <h2>Get Matched 🤝</h2>
                <h3>Step 2: Find a Practice Partner</h3>
                <p>Click "Find Match" and we'll automatically pair you with a teammate who's available at the same time. A Zoom link is generated instantly!</p>
                <div className="onboarding-visual">
                  <div className="match-preview">
                    <div className="match-user">You</div>
                    <div className="match-arrow">⚡</div>
                    <div className="match-user">Partner</div>
                  </div>
                </div>
              </div>
            )}
            
            {onboardingStep === 3 && (
              <div className="onboarding-step">
                <div className="onboarding-icon">
                  <Target size={64} />
                </div>
                <h2>Practice & Improve 🚀</h2>
                <h3>Step 3: Jump Into Your Session</h3>
                <p>Join the Zoom call, practice your skills, and get better with every session. After each call, reflect on what went well!</p>
                <div className="onboarding-visual">
                  <div className="practice-preview">
                    <div className="practice-step"><Check size={16} /> Practice roleplay</div>
                    <div className="practice-step"><Check size={16} /> Give feedback</div>
                    <div className="practice-step"><Check size={16} /> Track progress</div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="onboarding-actions">
              {onboardingStep < 3 ? (
                <button className="btn-primary btn-large" onClick={() => handleOnboardingAction('next')}>
                  Next <ArrowRight size={18} />
                </button>
              ) : (
                <button className="btn-primary btn-large" onClick={() => handleOnboardingAction('calendar')}>
                  <Calendar size={18} /> Set My Availability
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrap App with ErrorBoundary
function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
